import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoLiveCelebration from '../components/get-live/GoLiveCelebration';
import Button from '../components/ui/button';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';
import LeadTransfer from '../components/LeadTransfer';
import WarmupRampBanner from '../components/WarmupRampBanner';
import SendingPaceBanner from '../components/SendingPaceBanner';
import TcpaHoursBanner from '../components/TcpaHoursBanner';
import LaunchConsole from './campaign-console/LaunchConsole';
import TestConversationModal from './campaign-console/TestConversationModal';
import TestGateExplainerModal from './campaign-console/TestGateExplainerModal';
import AdminActivationWarningModal from './campaign-console/AdminActivationWarningModal';
import ArchiveCampaignModal from './campaign-console/ArchiveCampaignModal';
import CampaignDncScrubModal from './campaign-console/CampaignDncScrubModal';
import WebChatExplainerModal from './campaign-console/WebChatExplainerModal';
import { deriveCampaignSetup } from './campaign-console/consoleData';
import {
  Plus,
  Search,
  Filter,
  Archive,
  ArchiveRestore,
  Zap,
  ZapOff,
  X,
  Megaphone,
  CheckCircle,
  Clock,
  TrendingUp,
  BarChart2,
  AlertCircle,
  MessageSquare,
  AlertTriangle,
  RefreshCcw,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
  Settings,
  User,
  Target,
  RotateCcw,
  FileText,
  Trash2,
  ArrowRight,
  Lock,
  Unlock,
  Sparkles,
  Flag,
  Save,
  Copy,
  Check,
  Pencil,
  Globe,
  Phone,
  Facebook
} from 'lucide-react';
import { PERMISSIONS } from '../lib/permissions';
import {
  toneOptions,
  initialPersonaOptions,
  engagementPersonaOptions,
  followupPersonaOptions,
  strategyTemplates
} from '../components/settings/aiconfigurationhub/constants';
import {
  buildInstructionBundle,
  buildInitialInstruction,
  buildFollowupInstruction
} from '../lib/instructionBuilder';

// API Base URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Cache for API responses
const cache = new Map();
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

// CSRF token cache
let csrfToken = null;
let csrfTokenExpiry = null;

/**
 * Fetch CSRF token from the server
 * Caches the token until it expires
 */
const getCsrfToken = async (forceRefresh = false) => {
  // Return cached token if still valid
  if (!forceRefresh && csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
    return csrfToken;
  }

  if (forceRefresh) {
    csrfToken = null;
    csrfTokenExpiry = null;
  }

  try {
    const authToken = localStorage.getItem('auth_token');
    // CSRF endpoint is at /api/csrf-token from server root
    const csrfUrl = API_BASE.includes('/api')
      ? `${API_BASE}/csrf-token`
      : `${API_BASE}/api/csrf-token`;

    const response = await fetch(csrfUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }

    const data = await response.json();
    csrfToken = data.csrfToken;
    // Set expiry to 5 minutes before actual expiry for safety
    csrfTokenExpiry = Date.now() + ((data.expiresIn - 300) * 1000);

    return csrfToken;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
};

/**
 * Fetch wrapper that auto-retries once with a fresh CSRF token on 403 failures.
 */
const fetchWithCsrf = async (url, options = {}) => {
  const authToken = localStorage.getItem('auth_token');
  const method = (options.method || 'GET').toUpperCase();

  const buildHeaders = async (forceRefresh = false) => {
    const headers = { ...options.headers };
    if (authToken && !headers.Authorization) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      headers['X-CSRF-Token'] = await getCsrfToken(forceRefresh);
    }
    return headers;
  };

  const response = await fetch(url, { ...options, headers: await buildHeaders(false) });

  if (response.status === 403) {
    let body;
    try { body = await response.clone().json(); } catch (_) {}
    if (body?.error === 'Invalid CSRF token' || body?.error === 'CSRF token missing') {
      console.warn('CSRF token rejected — refreshing and retrying...');
      return fetch(url, { ...options, headers: await buildHeaders(true) });
    }
  }

  return response;
};

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Drop every campaign-list entry (keyed campaigns-<tenant>-<filter>-<showArchived>) so
// the next fetchCampaigns() goes to the server. Without this, a mutation followed by
// fetchCampaigns() gets the pre-mutation list back for up to CACHE_DURATION.
const clearCampaignListCache = () => {
  for (const key of Array.from(cache.keys())) {
    if (String(key).startsWith('campaigns-')) cache.delete(key);
  }
};

// Hot-lead email check used by validateAiToggle on EVERY channel (2026-09-10). Returns
// the blocking error, or null when the address is set. One definition, called from two
// places: the web/voice branch (which used to return valid before ever reaching it) and
// the SMS branch (unchanged position, so SMS still reports its errors in the same order).
//
// Why it is a hard block rather than a fallback: `_shared/notificationRecipient.ts`
// puts the campaign address in the To and only ADDS the tenant owner to the cc. With
// this null the To collapses to the tenant address, so the campaign's own assignee is
// never addressed. That is the same harm fixed in detect-dropped-handoffs on
// 2026-08-20, reached through a different door. Measured 2026-08-28: 14 of 32
// campaigns had this null, two of them live and sending (Exodus "First to Market" and
// "Auction Date"). The ai-toggle route (campaigns.js) enforces the same rule.
const hotLeadEmailBlock = (campaign) => {
  const alertEmail = (campaign.notification_email || '').trim();
  if (alertEmail && alertEmail.includes('@')) return null;
  return {
    valid: false,
    error: {
      type: 'notification_email',
      title: 'Hot Lead Email Not Set',
      message: 'Add the email address that should receive hot leads from this campaign. Without it, alerts fall back to the account owner and whoever runs this campaign is never told.',
      action: 'Add Hot Lead Email',
      actionUrl: null
    }
  };
};

// Optimized Knowledge Assets Dropdown with better positioning
const KnowledgeAssetsDropdown = ({
  campaign,
  knowledgeAssets,
  selectedAssets,
  onUpdate
}) => {
  const masterAssets = knowledgeAssets.filter(a => a.is_master);
  const campaignAssets = knowledgeAssets.filter(a => !a.is_master);

  const getIcon = (asset) => {
    if (asset.source_type === 'pdf') return '📄';
    if (asset.source_type === 'master_kb') return '📚';
    if (asset.source_type === 'website') return '🌐';
    return '📄';
  };

  const getLabel = (asset) => {
    if (asset.source_type === 'website') return asset.website_url || 'Untitled Website';
    return asset.title || asset.file_name || 'Untitled Document';
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {masterAssets.length > 0 && (
        <div className="px-3 py-2 bg-orange-50 border-b border-orange-100">
          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-1.5">Always Active</p>
          {masterAssets.map((asset) => (
            <div key={asset.id} className="flex items-center py-1 text-sm text-gray-500">
              <span className="mr-2 flex-shrink-0">{getIcon(asset)}</span>
              <span className="truncate flex-1" title={getLabel(asset)}>{getLabel(asset)}</span>
              <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">Master</span>
            </div>
          ))}
        </div>
      )}
      <div className="px-3 py-2 max-h-36 overflow-y-auto">
        {campaignAssets.length === 0 ? (
          <div className="text-sm text-gray-500 py-1">No campaign-specific assets</div>
        ) : (
          campaignAssets.map((asset) => {
            const isSelected = selectedAssets?.includes(asset.id);
            return (
              <label key={asset.id} className="flex items-center py-1.5 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onUpdate(asset.id, e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                />
                <span className="text-sm flex items-center flex-1 min-w-0">
                  <span className="mr-2 flex-shrink-0">{getIcon(asset)}</span>
                  <span className="truncate" title={getLabel(asset)}>{getLabel(asset)}</span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

// Multi-select for the additional outbound phones in a campaign's round-robin
// pool. Primary phone (`campaign.phone_number_id`) is excluded from the
// options here — it's managed by the existing single-phone dropdown.
// Renders nothing if the tenant has fewer than 2 phones (no round-robin
// possible) so the legacy single-phone UX stays identical for those tenants.
const AdditionalPhonesSection = ({ campaign, phoneNumbers, onUpdate }) => {
  const primaryId = campaign.phone_number_id ? Number(campaign.phone_number_id) : null;
  const additionalIds = (campaign.additional_phone_ids || []).map(Number);
  const eligible = phoneNumbers.filter(p => Number(p.id) !== primaryId);

  if (phoneNumbers.length < 2) return null;
  if (!primaryId) return null;

  const toggle = (phoneId, checked) => {
    const id = Number(phoneId);
    const next = checked
      ? [...new Set([...additionalIds, id])]
      : additionalIds.filter(x => x !== id);
    onUpdate(campaign.id, next);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Additional phones (round-robin)
      </label>
      <div className="border border-surfox-border rounded-lg px-3 py-2 max-h-32 overflow-y-auto bg-white">
        {eligible.length === 0 ? (
          <div className="text-sm text-gray-500">No other phones available for this tenant.</div>
        ) : (
          eligible.map(phone => {
            const isSelected = additionalIds.includes(Number(phone.id));
            return (
              <label key={phone.id} className="flex items-center py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => toggle(phone.id, e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm">{phone.phone_number}</span>
              </label>
            );
          })
        )}
      </div>
      {additionalIds.length > 0 && (
        <p className="text-xs text-blue-700">
          Round-robin enabled across {additionalIds.length + 1} numbers. New leads are distributed by lowest current load; existing leads stay on their assigned number.
        </p>
      )}
    </div>
  );
};

// Optimized Campaign Progress with better performance
const CampaignProgress = React.memo(({ campaignId, onClick }) => {
  const [progress, setProgress] = useState({ 
    processed: 0, 
    total: 0,
    sent: 0,        
    delivered: 0,
    failed: 0,
    retries: 0,
    queued: 0,
    is_processing: false,
    processing_message: '',
    last_milestone: null
  });
  const [isLoading, setIsLoading] = useState(true);
  
  const fetchProgress = useCallback(async () => {
    const cacheKey = `progress-${campaignId}`;
    const cached = getCachedData(cacheKey);
    if (cached && !progress.is_processing) {
      setProgress(cached);
      setIsLoading(false);
      return;
    }

    try {
      const { data: leads, error: leadsError } = await supabase
        .from('leads')
        .select('id, ai_sent')
        .eq('campaign_id', campaignId);
      
      if (leadsError) throw leadsError;
      
      const leadIds = leads?.map(l => l.id) || [];
      
      if (leadIds.length > 0) {
        const { data: messageStats, error: statsError } = await supabase
          .rpc('get_campaign_message_stats', { campaign_ids: [campaignId] });

        if (!statsError && messageStats && messageStats.length > 0) {
          const stats = messageStats[0];
          const total = leads.length;
          const processed = leads.filter(l => l.ai_sent).length;
          
          const sent = Number(stats.sent_count) || 0;
          const delivered = Number(stats.delivered_count) || 0;
          const failed = Number(stats.failed_count) || 0;
          const retries = Number(stats.retry_count) || 0;
          const queued = Number(stats.queued_count) || 0;
          
          const { data: recentNotifications } = await supabase
            .from('notifications')
            .select('title, created_at')
            .eq('campaign_id', campaignId)
            .in('type', ['campaign_progress', 'campaign_complete'])
            .order('created_at', { ascending: false })
            .limit(1);
          
          const lastMilestone = recentNotifications?.[0];
          const isRecentlyProcessing = lastMilestone && 
            (new Date() - new Date(lastMilestone.created_at)) < 300000;
          
          const progressData = { 
            processed, 
            total,
            sent,
            delivered,
            failed,
            retries,
            queued,
            is_processing: processed < total && isRecentlyProcessing,
            processing_message: isRecentlyProcessing ? 'AI actively processing...' : '',
            last_milestone: lastMilestone?.title || null
          };
          
          setProgress(progressData);
          setCachedData(cacheKey, progressData);
        }
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId, progress.is_processing]);
  
  useEffect(() => {
    fetchProgress();
    
    const interval = setInterval(fetchProgress, progress.is_processing ? 10000 : 30000);
    return () => clearInterval(interval);
  }, [fetchProgress]);
  
  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
          <div className="h-1.5 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }
  
  if (progress.total === 0) {
    return (
      <div className="space-y-2">
        <span className="text-sm text-gray-500">No leads</span>
      </div>
    );
  }
  
  const percentage = Math.round((progress.processed / progress.total) * 100);

  return (
    <div
      className={`space-y-3 ${onClick ? 'cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded-lg transition-colors' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="space-y-1">
        <div className="flex items-center text-sm">
          <span className="font-medium">{progress.processed.toLocaleString()}</span>
          <span className="text-gray-500 mx-1">/</span>
          <span className="text-gray-500">{progress.total.toLocaleString()}</span>
          <span className="text-gray-500 ml-2">({percentage}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${
              percentage >= 80 ? 'bg-green-500' :
              percentage >= 50 ? 'bg-blue-500' :
              percentage >= 20 ? 'bg-amber-500' : 'bg-orange-400'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      
      {progress.is_processing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
          <div className="flex items-center space-x-2">
            <div className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            <span className="text-xs font-medium text-blue-800">
              {progress.processing_message}
            </span>
          </div>
          {progress.last_milestone && (
            <div className="text-xs text-blue-600 mt-1">
              Latest: {progress.last_milestone}
            </div>
          )}
        </div>
      )}
      
      {progress.processed > 0 && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
            <span className="text-gray-600 truncate">Msgs delivered:</span>
            <span className="font-medium text-green-600">{progress.delivered.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
            <span className="text-gray-600 truncate">Failed:</span>
            <span className="font-medium text-red-600">{progress.failed.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center gap-1">
            <RefreshCcw className="w-3 h-3 text-blue-500 flex-shrink-0" />
            <span className="text-gray-600 truncate">Retries:</span>
            <span className="font-medium text-blue-600">{progress.retries.toLocaleString()}</span>
          </div>
          
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-yellow-500 flex-shrink-0" />
            <span className="text-gray-600 truncate">Queued:</span>
            <span className="font-medium text-yellow-600">{progress.queued.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
});

// Optimized Mobile Campaign Card with better touch targets
const CampaignCard = React.memo(({
  campaign,
  showTenantColumn,
  tenantNames,
  salesTeamMembers,
  phoneNumbers,
  knowledgeAssets,
  selectedKnowledgeAssets,
  knowledgeDropdownOpen,
  setKnowledgeDropdownOpen,
  showArchived,
  tenantIndustries,
  getDynamicColumnHeader,
  getDynamicDropdownOptions,
  getStatusBadge,
  updateCampaignAssignment,
  updateCampaignPhoneNumber,
  updateCampaignAdditionalPhones,
  updateCampaignTimezone,
  updateCampaignDynamicField,
  updateCampaignKnowledgeLinks,
  updateHotLeadDistribution,
  updateCampaignAIRepresentativeName,
  setSelectedKnowledgeAssets,
  toggleAiOn,
  archiveCampaign,
  unarchiveCampaign,
  setProgressModalCampaign,
  setShowProgressModal,
  customTalkTracks = [],
}) => {
  const [expanded, setExpanded] = useState(false);

  // ← Add these two lines right here, after useState
  const tenantIndustry = tenantIndustries[campaign.tenant_id] || '';
  const showDynamicColumn = ['Staffing', 'Real Estate', 'Home Services', 'Financial Services', 'Auto Sales', 'Mortgage Lending'].includes(tenantIndustry);

  // 🟢 2026-09-17 (Tom's call): a number already imported into Retell for
  // voice belongs to whichever campaign is using it for that — it must not
  // be offered as an option for a DIFFERENT campaign, especially a bulk one,
  // where a shared number was found live to leave a voice-imported number
  // reachable from a bulk campaign's own lead base. Exempts this campaign's
  // ENTIRE pool (primary + additional_phone_ids), not just the primary —
  // narrower than that would break a legitimate multi-number SMS rotation on
  // a single voice-eligible campaign the moment those numbers auto-import
  // (assignment auto-imports for ANY non-bulk campaign, whether or not the
  // tenant ever intends to use voice on it), hiding them from their OWN
  // campaign's pool picker too.
  const selectablePhoneNumbers = useMemo(() => {
    const ownPool = new Set(
      [campaign.phone_number_id, ...(campaign.additional_phone_ids || [])]
        .filter(id => id != null)
        .map(Number)
    );
    return phoneNumbers.filter(p => !p.retell_imported_at || ownPool.has(Number(p.id)));
  }, [phoneNumbers, campaign.phone_number_id, campaign.additional_phone_ids]);

  const handleToggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight">{campaign.name}</h3>
          {campaign.description && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{campaign.description}</p>
          )}
        </div>
        <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
          {getStatusBadge(campaign)}
          <button
            onClick={handleToggleExpanded}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={expanded ? "Collapse details" : "Expand details"}
          >
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-surfox-bg border border-surfox-line rounded-xl p-3">
        <CampaignProgress
          campaignId={campaign.id}
          onClick={() => {
            setProgressModalCampaign(campaign);
            setShowProgressModal(true);
          }}
        />
      </div>

      {/* AI Toggle */}
      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
        <span className="text-sm font-medium text-gray-900">AI Processing</span>
        <button
          onClick={() => toggleAiOn(campaign.id, campaign.ai_on)}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            campaign.ai_on 
              ? 'bg-blue-600' 
              : 'bg-gray-300'
          }`}
          title={campaign.ai_on ? 'AI Enabled - Click to disable' : 'AI Disabled - Click to enable'}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
              campaign.ai_on ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="space-y-4 pt-2 border-t border-surfox-line">
          {/* Assignment */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Assigned To</label>
            <select
              value={campaign.assigned_to_sales_team_id || ''}
              onChange={(e) => updateCampaignAssignment(campaign.id, e.target.value || null)}
              className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
            >
              <option value="">Unassigned</option>
              {salesTeamMembers.map(member => (
                <option key={member.sales_team_id} value={member.sales_team_id}>
                  {member.full_name || member.email}
                </option>
              ))}
            </select>
          </div>
          {/* AI Representative Name */}
<div className="space-y-2">
  <label className="block text-sm font-medium text-gray-700 mb-2">
    AI Representative
  </label>
  <input
    type="text"
    value={campaign.ai_representative_name || ''}
    onChange={(e) => updateCampaignAIRepresentativeName(campaign.id, e.target.value)}
    placeholder="e.g., Jack, Sarah, Mike"
    className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm hover:border-gray-400 transition-colors"
  />
</div>


          {/* Phone Number */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
            <select
              value={campaign.phone_number_id || ''}
              onChange={(e) => updateCampaignPhoneNumber(campaign.id, e.target.value || null)}
              className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
            >
              <option value="">No phone assigned</option>
              {selectablePhoneNumbers.map(phone => (
                <option key={phone.id} value={phone.id}>
                  {phone.phone_number}
                </option>
              ))}
            </select>
          </div>

          <AdditionalPhonesSection
            campaign={campaign}
            phoneNumbers={selectablePhoneNumbers}
            onUpdate={updateCampaignAdditionalPhones}
          />

          {/* Campaign Timezone */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Campaign Timezone</label>
            <select
              value={campaign.timezone || ''}
              onChange={(e) => updateCampaignTimezone(campaign.id, e.target.value)}
              className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
            >
              <option value="">Select timezone</option>
              <option value="America/New_York">Eastern (EST/EDT)</option>
              <option value="America/Chicago">Central (CST/CDT)</option>
              <option value="America/Denver">Mountain (MST/MDT)</option>
              <option value="America/Los_Angeles">Pacific (PST/PDT)</option>
            </select>
          </div>

          {/* Dynamic Field */}
          {['Staffing', 'Real Estate', 'Home Services', 'Financial Services', 'Auto Sales', 'Mortgage Lending'].includes(tenantIndustries[campaign.tenant_id]) && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">{getDynamicColumnHeader(tenantIndustries[campaign.tenant_id])}</label>
              <DynamicFieldComponent
                campaign={campaign}
                tenantIndustry={tenantIndustry}
                getDynamicDropdownOptions={getDynamicDropdownOptions}
                updateCampaignDynamicField={updateCampaignDynamicField}
                customTalkTracks={customTalkTracks}
              />
            </div>
          )}

          {/* Knowledge Assets */}
<div className="space-y-2">
  <label className="block text-sm font-medium text-gray-700 mb-2">Knowledge Assets</label>
<KnowledgeAssetsDropdown
    campaign={campaign}
    knowledgeAssets={knowledgeAssets.filter(ka => ka.tenant_id === campaign.tenant_id)}
    selectedAssets={selectedKnowledgeAssets[campaign.id]}
    onUpdate={async (assetId, checked) => {
      const currentSelected = selectedKnowledgeAssets[campaign.id] || [];
      let newSelected;

      if (checked) {
        newSelected = [...currentSelected, assetId];
      } else {
        newSelected = currentSelected.filter(id => id !== assetId);
      }

      setSelectedKnowledgeAssets({
        ...selectedKnowledgeAssets,
        [campaign.id]: newSelected
      });

      const masterIds = knowledgeAssets
        .filter(ka => ka.tenant_id === campaign.tenant_id && ka.is_master)
        .map(ka => ka.id);

      await updateCampaignKnowledgeLinks(campaign.id, newSelected, masterIds);
    }}
  />
</div>
                {/* Hot Lead Assignment */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Hot Lead Assignment</label>
                  <div className="space-y-3">
                    <select
                      value={campaign.hot_lead_distribution_mode || ''}
                      onChange={(e) => updateHotLeadDistribution(campaign.id, 'mode', e.target.value || null)}
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
                    >
                      <option value="">No hot lead assignment</option>
                      <option value="single">Assign to specific person</option>
                      <option value="round_robin">Round robin team</option>
                    </select>
                    
                    {campaign.hot_lead_distribution_mode === 'single' && (
                      <select
                        value={campaign.hot_lead_single_assignee_id || ''}
                        onChange={(e) => updateHotLeadDistribution(campaign.id, 'single_assignee', e.target.value || null)}
                        className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
                      >
                        <option value="">Select assignee...</option>
                        {salesTeamMembers.map(member => (
                          <option key={member.sales_team_id} value={member.sales_team_id}>
                            {member.full_name || member.email}
                          </option>
                        ))}
                      </select>
                    )}
                    
                    {campaign.hot_lead_distribution_mode === 'round_robin' && (
                      <div className="border border-surfox-border rounded-lg px-3 py-2 max-h-32 overflow-y-auto bg-white">
                        {salesTeamMembers.length === 0 ? (
                          <div className="text-sm text-gray-500">No team members available</div>
                        ) : (
                          salesTeamMembers.map(member => {
                            const isSelected = campaign.hot_lead_round_robin_team?.includes(member.sales_team_id);
                            return (
                              <label key={member.sales_team_id} className="flex items-center py-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => updateHotLeadDistribution(campaign.id, 'round_robin_toggle', {
                                    memberId: member.sales_team_id,
                                    checked: e.target.checked
                                  })}
                                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm truncate">{member.full_name || member.email}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>

          {/* Actions */}
          <div className="flex justify-end pt-2">
            {showArchived ? (
              <Button 
                variant="outline"
                className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-300"
                onClick={() => unarchiveCampaign(campaign.id)}
              >
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Restore
              </Button>
            ) : (
              <Button 
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                onClick={() => archiveCampaign(campaign.id)}
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
            )}
          </div>

        </div>
      )}
    </div>
  );
});

// Specialty selector with support for tenant-scoped LLM-generated specialties.
// Industry-agnostic: queries generated_specialty_contexts by (tenant_id,
// industry, talk_track_type) and lets the user add a new specialty which is
// generated via the generate-specialty-context edge function. The generated
// paragraph itself (role_context) is invisible to the user — it is injected
// into instructionBuilder at prompt-assembly time.
const ADD_NEW_SPECIALTY_VALUE = '__add_new_specialty__';

const SpecialtySelector = React.memo(({
  campaign,
  tenantIndustry,
  childOptions,
  updateCampaignDynamicField,
  disableCustom = false
}) => {
  const [generated, setGenerated] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newSpecialtyText, setNewSpecialtyText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const tenantId = campaign.tenant_id;
  const talkTrackType = campaign.talk_track_type;

  useEffect(() => {
    if (!saveSuccess) return;
    const t = setTimeout(() => setSaveSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [saveSuccess]);

  useEffect(() => {
    if (!tenantId || !tenantIndustry || !talkTrackType) {
      setGenerated([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('generated_specialty_contexts')
        .select('id, specialty_name')
        .eq('tenant_id', tenantId)
        .eq('industry', tenantIndustry)
        .eq('talk_track_type', talkTrackType)
        .order('generated_at', { ascending: false });
      if (cancelled) return;
      if (!error && data) setGenerated(data);
    })();
    return () => { cancelled = true; };
  }, [tenantId, tenantIndustry, talkTrackType]);

  const handleSelectChange = (e) => {
    const value = e.target.value;
    if (value === ADD_NEW_SPECIALTY_VALUE) {
      setIsAddingNew(true);
      setNewSpecialtyText('');
      setSaveError(null);
      return;
    }
    updateCampaignDynamicField(campaign.id, 'child', value || null, tenantIndustry);
  };

  const handleSave = async () => {
    const normalized = newSpecialtyText.trim().toLowerCase();
    if (!normalized) {
      setSaveError('Enter a specialty name');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const { data, error } = await supabase.functions.invoke('generate-specialty-context', {
        body: {
          tenant_id: tenantId,
          industry: tenantIndustry,
          talk_track_type: talkTrackType,
          specialty_name: newSpecialtyText,
        },
      });
      if (error) throw error;
      if (!data?.row) throw new Error('Generation returned no row');

      const { data: refreshed } = await supabase
        .from('generated_specialty_contexts')
        .select('id, specialty_name')
        .eq('tenant_id', tenantId)
        .eq('industry', tenantIndustry)
        .eq('talk_track_type', talkTrackType)
        .order('generated_at', { ascending: false });
      if (refreshed) setGenerated(refreshed);

      await updateCampaignDynamicField(campaign.id, 'child', data.row.specialty_name, tenantIndustry);

      // Rebuild the cached instruction bundles so reply-handling paths (which
      // read ai_instruction_engagement / ai_instruction_followup_*) pick up
      // the new role_context. Matches the existing pattern used after a custom
      // talk track is assigned in updateCampaignDynamicField.
      let rebuildFailed = false;
      try {
        const rebuildRes = await fetchWithCsrf(`${API_BASE}/campaigns/${campaign.id}/apply-talk-track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!rebuildRes.ok) {
          const errText = await rebuildRes.text();
          console.warn('Instruction rebuild responded non-OK:', rebuildRes.status, errText);
          rebuildFailed = true;
        }
      } catch (rebuildErr) {
        console.warn('Instruction rebuild failed after specialty generation:', rebuildErr);
        rebuildFailed = true;
      }

      if (rebuildFailed) {
        // Specialty is saved and selected; only the cached bundles are stale.
        // Surface a non-fatal warning and keep the input open so it stays visible.
        setSaveError('Specialty saved, but cached prompts did not refresh. Re-save the campaign to refresh them.');
        return;
      }

      setIsAddingNew(false);
      setNewSpecialtyText('');
      setSaveSuccess(`"${data.row.specialty_name}" saved and prompts refreshed.`);
    } catch (err) {
      console.error('Failed to generate specialty context:', err);
      setSaveError(err?.message || 'Failed to generate. Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setNewSpecialtyText('');
    setSaveError(null);
  };

  const builtInSet = new Set(childOptions.map(o => o.value));
  const customOnly = generated.filter(g => !builtInSet.has(g.specialty_name));

  if (isAddingNew) {
    return (
      <div className="space-y-2">
        <input
          type="text"
          value={newSpecialtyText}
          onChange={(e) => setNewSpecialtyText(e.target.value)}
          placeholder="Enter specialty name"
          disabled={isSaving}
          className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !newSpecialtyText.trim()}
            className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Generating…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
        {isSaving && (
          <p className="text-xs text-surfox-faint">This may take a few seconds.</p>
        )}
        {saveError && (
          <p className="text-sm text-red-600">{saveError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {saveSuccess && (
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          {saveSuccess}
        </p>
      )}
    <select
      value={campaign.talk_track_specialty || ''}
      onChange={handleSelectChange}
      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
    >
      <option value="">Select specialty...</option>
      {childOptions.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
      {!disableCustom && customOnly.length > 0 && (
        <optgroup label="── Custom Specialties ──">
          {customOnly.map(g => (
            <option key={g.id} value={g.specialty_name}>
              {g.specialty_name}
            </option>
          ))}
        </optgroup>
      )}
      {!disableCustom && (
        <option value={ADD_NEW_SPECIALTY_VALUE}>+ Add new specialty</option>
      )}
    </select>
    </div>
  );
});

// Event Follow-up specialty options — fixed set, industry-agnostic, mirrors the
// three event_attendance_status variants the instructionBuilder branches read.
const EVENT_FOLLOWUP_CHILD_OPTIONS = [
  { value: 'attended', label: 'Attended' },
  { value: 'no_show', label: 'No Show' },
  { value: 'replay_watched', label: 'Replay Watched' },
];

// Optimized Dynamic Field Component
// Optimized Dynamic Field Component
const DynamicFieldComponent = React.memo(({
  campaign,
  tenantIndustry,
  getDynamicDropdownOptions,
  updateCampaignDynamicField,
  customTalkTracks = []
}) => {
  const options = getDynamicDropdownOptions(tenantIndustry);
  const parentValue = campaign.custom_talk_track_id
    ? `custom__${campaign.custom_talk_track_id}`
    : (campaign.talk_track_type || '');
  // Event Follow-up uses a fixed, industry-agnostic specialty set rather than
  // the per-industry childOptions. We also disable "+ Add new specialty" for
  // this track since the three values map 1:1 to instructionBuilder branches.
  const isEventFollowup = campaign.talk_track_type === 'event_followup';
  const effectiveChildOptions = isEventFollowup
    ? EVENT_FOLLOWUP_CHILD_OPTIONS
    : options.childOptions;

  return (
    <div className="space-y-3">
      <select
        value={parentValue}
        onChange={(e) => {
          updateCampaignDynamicField(campaign.id, 'parent', e.target.value || null, tenantIndustry);
        }}
        className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
      >
        <option value="">Select approach...</option>
        {options.parentOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        {customTalkTracks.length > 0 && (
          <optgroup label="── Custom Talk Tracks ──">
            {customTalkTracks.map(t => (
              <option key={`custom__${t.id}`} value={`custom__${t.id}`}>
                {t.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {/* Specialty selector renders for any industry once a standard parent is chosen.
          Built-in childOptions are populated for Staffing; other industries start empty
          and rely on tenant-created specialties via "+ Add new specialty".
          Event Follow-up overrides childOptions to its fixed three-value set and
          suppresses the custom-specialty path. */}
      {/* Specialty selection is retired for industry verticals (the rich intel now
          lives in src/lib/specialtyTemplates.js). The selector remains ONLY for
          event_followup, whose attended / no_show / replay_watched values are an
          industry-agnostic per-campaign variant, not an industry specialty. */}
      {isEventFollowup && !campaign.custom_talk_track_id && (
        <SpecialtySelector
          campaign={campaign}
          tenantIndustry={tenantIndustry}
          childOptions={effectiveChildOptions}
          updateCampaignDynamicField={updateCampaignDynamicField}
          disableCustom={isEventFollowup}
        />
      )}
    </div>
  );
});

// Optimized Statistics Card Component
const StatCard = React.memo(({ icon: Icon, title, value, subtitle, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    orange: "bg-orange-50 text-orange-600",
    purple: "bg-purple-50 text-purple-600"
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow duration-200 flex items-center">
      <div className={`w-10 h-10 ${colorClasses[color]} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="ml-4 min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
        {subtitle && (
          <p className="text-xs text-surfox-faint mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
});

// Hot Lead Assignment Component
const HotLeadAssignment = React.memo(({ assignment }) => {
  if (assignment.mode === 'single') {
    return (
      <div className="flex items-center gap-1.5 text-sm">
        <User className="w-4 h-4 text-surfox-primary flex-shrink-0" />
        <span className="font-semibold text-orange-700 truncate max-w-[200px]" title={assignment.assignee}>
          {assignment.assignee}
        </span>
      </div>
    );
  } else if (assignment.mode === 'round_robin') {
    return (
      <div className="flex items-center gap-1.5 text-sm">
        <RotateCcw className="w-4 h-4 text-surfox-primary flex-shrink-0" />
        <span className="font-semibold text-orange-700">
          {assignment.assignee}
        </span>
      </div>
    );
  } else {
    return (
      <span className="text-gray-400 text-sm italic">Not configured</span>
    );
  }
});

// Customer-friendly error component
const ErrorMessage = React.memo(({ error, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <AlertTriangle className="w-6 h-6 text-red-600" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      Unable to load campaigns
    </h3>
    <p className="text-gray-600 mb-4">
      We're having trouble connecting to your campaign data. Please try refreshing.
    </p>
    {error && (
      <p className="text-xs text-red-700 font-mono bg-red-100 border border-red-200 rounded-lg px-3 py-2 mb-4 max-w-xl mx-auto break-words">
        {String(error)}
      </p>
    )}
    {onRetry && (
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700">
        <RefreshCcw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    )}
  </div>
));

// Loading skeleton component
const CampaignSkeleton = React.memo(() => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6 animate-pulse">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className="h-5 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </div>
      <div className="h-6 bg-gray-200 rounded w-16"></div>
    </div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
      <div className="h-2 bg-gray-200 rounded"></div>
      <div className="h-8 bg-gray-200 rounded"></div>
    </div>
  </div>
));

// Recent Messages Tab Component
const RecentMessagesTab = ({ campaign }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('today');
  const [expandedMessage, setExpandedMessage] = useState(null);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        // Calculate time range
        const now = new Date();
        let startDate = new Date();

        if (timeFilter === 'hour') {
          startDate.setHours(now.getHours() - 1);
        } else if (timeFilter === 'today') {
          startDate.setHours(0, 0, 0, 0);
        } else if (timeFilter === '7days') {
          startDate.setDate(now.getDate() - 7);
        }

        // Query messages joined with leads
        let query = supabase
          .from('messages')
          .select(`
            id,
            message_id,
            message_body,
            direction,
            status,
            delivery_status,
            delivery_error_code,
            delivery_error_message,
            timestamp,
            sent_at,
            delivery_updated_at,
            phone,
            lead_id,
            leads!inner(id, name, first_name, last_name, campaign_id)
          `)
          .eq('leads.campaign_id', campaign.id)
          .gte('timestamp', startDate.toISOString())
          .order('timestamp', { ascending: false })
          .limit(50);

        // Apply status filter
        if (statusFilter !== 'all') {
          if (statusFilter === 'delivered') {
            query = query.eq('delivery_status', 'delivered');
          } else if (statusFilter === 'failed') {
            query = query.eq('delivery_status', 'failed');
          } else if (statusFilter === 'pending') {
            query = query.in('delivery_status', ['queued', 'sent', 'sending']);
          }
        }

        const { data, error } = await query;

        if (error) throw error;
        setMessages(data || []);
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [campaign.id, statusFilter, timeFilter]);

  const getStatusIcon = (deliveryStatus) => {
    if (deliveryStatus === 'delivered') {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    } else if (deliveryStatus === 'failed' || deliveryStatus === 'undelivered') {
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    } else {
      return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (deliveryStatus) => {
    if (deliveryStatus === 'delivered') return 'text-green-600';
    if (deliveryStatus === 'failed' || deliveryStatus === 'undelivered') return 'text-red-600';
    return 'text-yellow-600';
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-4 h-20" />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No messages found</p>
        <p className="text-xs text-gray-400 mt-1">
          {statusFilter !== 'all' ? 'Try adjusting your filters' : 'Messages will appear here once campaign starts'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex-1">
          <label className="text-[12px] font-semibold text-surfox-muted mb-1 block">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-surfox-border rounded-lg text-sm focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary"
          >
            <option value="all">All Messages</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="text-[12px] font-semibold text-surfox-muted mb-1 block">Time Range</label>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-surfox-border rounded-lg text-sm focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary"
          >
            <option value="hour">Last Hour</option>
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
          </select>
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-2">
        {messages.map((msg) => {
          const isExpanded = expandedMessage === msg.id;
          const leadName = msg.leads?.name || `${msg.leads?.first_name || ''} ${msg.leads?.last_name || ''}`.trim() || 'Unknown Lead';

          return (
            <div
              key={msg.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
            >
              {/* Message Row */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setExpandedMessage(isExpanded ? null : msg.id)}
              >
                <div className="flex items-start gap-3">
                  {/* Status Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getStatusIcon(msg.delivery_status)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{leadName}</p>
                        <p className="text-xs text-surfox-faint">{msg.phone}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs font-medium ${getStatusColor(msg.delivery_status)}`}>
                          {msg.delivery_status || 'pending'}
                        </span>
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {/* Message Preview */}
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {msg.message_body?.substring(0, 100)}
                      {msg.message_body?.length > 100 ? '...' : ''}
                    </p>

                    {/* Timestamp */}
                    <p className="text-xs text-gray-400">
                      {new Date(msg.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
                  {/* Full Message */}
                  <div>
                    <p className="text-[12px] font-semibold text-surfox-muted mb-1">Full Message</p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{msg.message_body}</p>
                  </div>

                  {/* Twilio Details */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div>
                      <p className="text-[12px] font-semibold text-surfox-muted mb-1">Twilio SID</p>
                      <p className="text-xs text-gray-900 font-mono bg-white px-2 py-1 rounded border border-gray-200">
                        {msg.message_id || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[12px] font-semibold text-surfox-muted mb-1">Direction</p>
                      <p className="text-xs text-gray-900">
                        {msg.direction === 'outbound' ? '↗ Outbound' : '↙ Inbound'}
                      </p>
                    </div>
                  </div>

                  {/* Error Details (if failed) */}
                  {msg.delivery_status === 'failed' && (msg.delivery_error_code || msg.delivery_error_message) && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-red-900 mb-1">Error Details</p>
                      {msg.delivery_error_code && (
                        <p className="text-xs text-red-700">
                          <span className="font-medium">Code:</span> {msg.delivery_error_code}
                        </p>
                      )}
                      {msg.delivery_error_message && (
                        <p className="text-xs text-red-700 mt-1">
                          <span className="font-medium">Message:</span> {msg.delivery_error_message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 text-xs">
                    {msg.sent_at && (
                      <div>
                        <span className="text-gray-600">Sent:</span>
                        <span className="ml-1 text-gray-900">{new Date(msg.sent_at).toLocaleTimeString()}</span>
                      </div>
                    )}
                    {msg.delivery_updated_at && (
                      <div>
                        <span className="text-gray-600">Updated:</span>
                        <span className="ml-1 text-gray-900">{new Date(msg.delivery_updated_at).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Results Count */}
      <div className="text-center py-3 text-sm text-gray-500">
        Showing {messages.length} message{messages.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

// Campaign Progress Modal Content
// A channel-switch PATCH (web / voice / messenger) the server refused: carry its plain-English reason (channelFlipGuard)
// to the catch block so the person sees WHY the campaign did not switch instead of the button silently snapping back.
async function channelFlipError(res, fallback) {
  const body = await res.json().catch(() => ({}));
  return Object.assign(new Error(fallback), { userMessage: body?.message || body?.error || null });
}

const CampaignProgressModal = ({ campaign, isWebChannel, isVoiceChannel, isMessengerChannel = false }) => {
  const [configHealth, setConfigHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const checkConfigHealth = async () => {
      setLoading(true);
      const issues = [];

      // Check 0: AI Strategy (talk track) — shells from the add-lead flow have none.
      // Web/voice campaigns derive strategy from channel config, so they're exempt.
      const hasTalkTrack = !!(campaign.custom_talk_track_id || campaign.talk_track_type);
      if (!hasTalkTrack && !isWebChannel && !isVoiceChannel && !isMessengerChannel) {
        issues.push({
          type: 'error',
          title: 'No AI Strategy Yet',
          message: 'Set up this campaign with Surf before it can go live',
          action: 'Set Up with Surf',
          actionEvent: 'surf:open'
        });
      }

      // Check 1: AI Instructions
      if (!campaign.ai_instruction_initial || campaign.ai_instruction_initial.trim() === '') {
        issues.push({
          type: 'error',
          title: 'AI Instructions Missing',
          message: 'Configure AI instructions to enable processing',
          actionUrl: `/settings?tab=ai-studio&campaign=${campaign.id}`
        });
      }

      // Check 2: Phone Number (skip for web channel — chat widget doesn't use a phone number)
      if (!campaign.phone_number_id && !isWebChannel && !isVoiceChannel && !isMessengerChannel) {
        issues.push({
          type: 'error',
          title: 'Phone Number Not Assigned',
          message: 'Assign a phone number to send messages',
          actionUrl: '/settings?tab=phone-numbers'
        });
      }

      // Check 3: Business Hours
      const { data: settingsData } = await supabase
        .from('platform_settings')
        .select('key, value')
        .eq('tenant_id', campaign.tenant_id)
        .in('key', ['officeOpenHour', 'officeCloseHour', 'officeDays']);

      const settings = {};
      if (settingsData) {
        settingsData.forEach(s => {
          settings[s.key] = s.value;
        });
      }

      if (!settings.officeOpenHour || !settings.officeCloseHour || !settings.officeDays) {
        issues.push({
          type: 'warning',
          title: 'Business Hours Not Configured',
          message: 'Messages will not respect business hours',
          actionUrl: '/settings?tab=company-profile'
        });
      }

      // Check 4: Lead Count
      const { count: leadCount } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('campaign_id', campaign.id);

      // Fetch live stats
      const { data: leads } = await supabase
        .from('leads')
        .select('id, ai_sent')
        .eq('campaign_id', campaign.id);

      let messageStats = {};
      if (leads && leads.length > 0) {
        const { data: stats } = await supabase
          .rpc('get_campaign_message_stats', { campaign_ids: [campaign.id] });

        if (stats && stats.length > 0) {
          messageStats = stats[0];
        }
      }

      setConfigHealth({
        issues,
        hasErrors: issues.some(i => i.type === 'error'),
        leadCount: leadCount || 0,
        settings
      });

      setStats({
        total: leads?.length || 0,
        processed: leads?.filter(l => l.ai_sent).length || 0,
        sent: Number(messageStats.sent_count) || 0,
        delivered: Number(messageStats.delivered_count) || 0,
        failed: Number(messageStats.failed_count) || 0,
        retries: Number(messageStats.retry_count) || 0,
        queued: Number(messageStats.queued_count) || 0
      });

      setLoading(false);
    };

    checkConfigHealth();
  }, [campaign]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  const percentage = stats.total > 0 ? Math.round((stats.processed / stats.total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
              activeTab === 'overview'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
            {activeTab === 'overview' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
              activeTab === 'messages'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Recent Messages
            {activeTab === 'messages' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Config Health Warning */}
      {configHealth.hasErrors && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900 mb-2">
                Configuration Issues Detected
              </h3>
              <p className="text-sm text-red-700 mb-3">
                This campaign cannot process leads until these issues are resolved:
              </p>
              <div className="space-y-2">
                {configHealth.issues.filter(i => i.type === 'error').map((issue, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-red-900">{issue.title}</p>
                        <p className="text-sm text-red-700">{issue.message}</p>
                      </div>
                      <button
                        onClick={() => {
                          if (issue.actionEvent) window.dispatchEvent(new CustomEvent(issue.actionEvent));
                          else window.location.href = issue.actionUrl;
                        }}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex-shrink-0 ml-3"
                      >
                        {issue.action || 'Fix Now'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warnings (non-blocking) */}
      {configHealth.issues.filter(i => i.type === 'warning').length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-yellow-900 mb-2">Warnings</h4>
              <div className="space-y-2">
                {configHealth.issues.filter(i => i.type === 'warning').map((issue, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-yellow-900">{issue.title}</p>
                      <p className="text-xs text-yellow-700">{issue.message}</p>
                    </div>
                    <button
                      onClick={() => {
                        if (issue.actionEvent) window.dispatchEvent(new CustomEvent(issue.actionEvent));
                        else window.location.href = issue.actionUrl;
                      }}
                      className="px-3 py-1.5 bg-yellow-600 text-white text-xs font-medium rounded-lg hover:bg-yellow-700 transition-colors flex-shrink-0 ml-3"
                    >
                      {issue.action || 'Configure'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Config Health - All Good */}
      {!configHealth.hasErrors && configHealth.issues.filter(i => i.type === 'warning').length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h4 className="font-medium text-green-900">Configuration Healthy</h4>
              <p className="text-sm text-green-700">All systems ready for AI processing</p>
            </div>
          </div>
        </div>
      )}

      {/* Live Stats Overview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Campaign Progress</h3>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-gray-700">
              {stats.processed.toLocaleString()} / {stats.total.toLocaleString()} leads processed
            </span>
            <span className="font-bold text-gray-900">{percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600 uppercase">Queued</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{stats.queued.toLocaleString()}</p>
            <p className="text-xs text-blue-600 mt-1">Waiting to send</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-600 uppercase">Delivered</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{stats.delivered.toLocaleString()}</p>
            <p className="text-xs text-green-600 mt-1">Successfully sent</p>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-xs font-medium text-red-600 uppercase">Failed</span>
            </div>
            <p className="text-2xl font-bold text-red-900">{stats.failed.toLocaleString()}</p>
            <p className="text-xs text-red-600 mt-1">Delivery failed</p>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <RefreshCcw className="w-4 h-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-600 uppercase">Retries</span>
            </div>
            <p className="text-2xl font-bold text-orange-900">{stats.retries.toLocaleString()}</p>
            <p className="text-xs text-orange-600 mt-1">Retry attempts</p>
          </div>
        </div>
      </div>

      {/* Campaign Configuration Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Configuration Summary</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-sm text-gray-600">AI Instructions</span>
            <span className="text-sm font-medium">
              {campaign.ai_instruction_initial ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Configured
                </span>
              ) : (
                <span className="text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Missing
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-sm text-gray-600">Phone Number</span>
            <span className="text-sm font-medium">
              {campaign.phone_number_id ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Assigned
                </span>
              ) : (
                <span className="text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Not Assigned
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-sm text-gray-600">Business Hours</span>
            <span className="text-sm font-medium">
              {configHealth.settings.officeOpenHour && configHealth.settings.officeCloseHour ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {configHealth.settings.officeDays} | {configHealth.settings.officeOpenHour}:00 - {configHealth.settings.officeCloseHour}:00
                </span>
              ) : (
                <span className="text-yellow-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Not Configured
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">Total Leads</span>
            <span className="text-sm font-bold text-gray-900">{configHealth.leadCount.toLocaleString()}</span>
          </div>
        </div>
      </div>
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <RecentMessagesTab campaign={campaign} />
      )}
    </div>
  );
};

// ============================================
// CAMPAIGN DETAIL PANEL — Tabbed config UI
// ============================================
const CampaignDetailPanel = React.memo(({
  campaign,
  widgetCampaignId,
  voiceCampaignId,
  messengerCampaignId,
  isGlobalAdmin,
  salesTeamsByTenant,
  webChatCompanyName,
  setWebChatCompanyName,
  saveWebChatCompanyName,
  setWebChatCampaign,
  webChatHolderName,   // name of the campaign currently holding the widget slot, or null
  setVoiceCampaign,
  setMessengerCampaign,
  phoneNumbers,
  knowledgeAssets,
  selectedKnowledgeAssets,
  setSelectedKnowledgeAssets,
  updateCampaignKnowledgeLinks,
  updateCampaignAssignment,
  updateCampaignPhoneNumber,
  updateCampaignAdditionalPhones,
  autoImportCampaignNumberSilently,
  updateCampaignTimezone,
  updateCampaignForwardNumber,
  updateCampaignNotificationEmail,
  updateCampaignNotificationCcEmail,
  updateCampaignVisitorSupportEmail,
  updateCampaignAIRepresentativeName,
  updateCampaignDynamicField,
  updateHotLeadDistribution,
  updateCampaignWarmTransferMode,
  importCampaignNumberToRetell,
  tenantIndustries,
  getDynamicColumnHeader,
  getDynamicDropdownOptions,
  customTalkTracks,
  showArchived,
  archiveCampaign,
  unarchiveCampaign,
  canEditInstructions,
  canRebuildBundle,
  onConfigSaved,
  initialTab = 'setup',
  singleSection = false,   // opened from the Launch Console for ONE section — hide the tab bar
}) => {
  // Review accounts and prospect demos read as fully set up (see deriveCampaignSetup in consoleData.js); display only.
  const { user: panelAuthUser } = useAuth();
  const isReviewerAccount = panelAuthUser?.is_reviewer === true;
  const isWeb = widgetCampaignId === campaign.id;
  const isVoice = voiceCampaignId === campaign.id;
  // Same brain as web (generate-web-chat-reply, channel:'messenger'); everywhere isWeb
  // exempts a field below, Messenger needs the same exemption.
  const isMessenger = messengerCampaignId === campaign.id;
  // Same idiom as isWeb/isVoice above: computed once, gates config sections inline below.
  // Voice re-engagement isn't itself a channel (a campaign can be SMS or the Voice channel and
  // still be eligible) — it's Phase 3's voice-eligibility flag, reused exactly as web chat reuses
  // isWeb, per Tom's explicit instruction not to invent a second conditional-config mechanism.
  const isVoiceEligible = campaign.allows_bulk_upload === false;
  // 🟢 2026-09-17 — same guard as CampaignCard's own selectablePhoneNumbers
  // (this panel has its own separate number picker, not shared state with the
  // card view). Exempts this campaign's ENTIRE pool, not just the primary —
  // see CampaignCard's own comment for why a narrower exemption breaks a
  // legitimate multi-number SMS rotation the moment those numbers auto-import.
  const selectablePhoneNumbers = useMemo(() => {
    const ownPool = new Set(
      [campaign.phone_number_id, ...(campaign.additional_phone_ids || [])]
        .filter(id => id != null)
        .map(Number)
    );
    return phoneNumbers.filter(p => !p.retell_imported_at || ownPool.has(Number(p.id)));
  }, [phoneNumbers, campaign.phone_number_id, campaign.additional_phone_ids]);
  // Confirmation before handing the website-widget slot to this campaign. The
  // segmented control looks like a view toggle; the switch is anything but.
  const [showWebChatExplainer, setShowWebChatExplainer] = useState(false);
  const tenantIndustry = tenantIndustries[campaign.tenant_id] || '';
  // global_admin always sees the dropdown so the internal SurFox AI talk track
  // is reachable even for tenants whose industry isn't one of the standard six.
  const hasDynamicField = isGlobalAdmin || ['Staffing', 'Real Estate', 'Home Services', 'Financial Services', 'Auto Sales', 'Mortgage Lending'].includes(tenantIndustry);
  const hasMultipleTeammates = (salesTeamsByTenant[campaign.tenant_id] || []).length > 1;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [isTrialing, setIsTrialing] = useState(false);

  // Fetch subscription status per campaign's tenant (not logged-in user's tenant)
  useEffect(() => {
    if (!campaign.tenant_id) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('tenants')
          .select('subscription_status')
          .eq('id', campaign.tenant_id)
          .single();
        setIsTrialing(data?.subscription_status === 'trialing');
      } catch (err) {
        console.warn('Could not fetch subscription status:', err.message);
      }
    })();
  }, [campaign.tenant_id]);
  const [config, setConfig] = useState(null);
  const [savedConfig, setSavedConfig] = useState(null);  // tracks what's actually in DB
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Voice AI v1 architecture revision (2026-09-13): campaign-number Retell-import
  // state + action, replacing the old Voice Re-engagement card. See the
  // "Enable Voice Calling" control inside the Follow-ups tab below.
  const [importingVoiceNumber, setImportingVoiceNumber] = useState(false);
  // 🟢 2026-09-17 (Tom's call): a Follow-up step's Channel dropdown must not
  // OFFER Voice at all until this campaign's number is actually imported —
  // send-followups/index.ts's resolveFollowupChannel already silently falls
  // back to SMS when it isn't, but showing a "choice" that quietly does
  // nothing is confusing. Fetched independently of the campaign list's own
  // query (that query only carries phone_numbers.id/phone_number) so this
  // stays a self-contained check.
  const [voiceNumberImported, setVoiceNumberImported] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isVoiceEligible || !campaign.phone_number_id) { if (!cancelled) setVoiceNumberImported(false); return; }
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('retell_imported_at')
        .eq('id', campaign.phone_number_id)
        .maybeSingle();
      if (!cancelled) setVoiceNumberImported(!error && !!data?.retell_imported_at);
    })();
    return () => { cancelled = true; };
  }, [campaign.phone_number_id, isVoiceEligible]);

  // Load config from DB when panel mounts
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('campaigns')
          .select(`
            initial_tone, initial_persona, engagement_tone, engagement_persona,
            path1_followup_delay, path2_ai_followup_enabled,
            path1_followup_channel,
            followup_delay_1, followup_delay_2, followup_delay_3,
            followup_channel_1, followup_channel_2, followup_channel_3,
            followup_1_tone, followup_1_persona,
            followup_2_tone, followup_2_persona,
            followup_3_tone, followup_3_persona,
            require_qualification_before_transfer, qualification_questions,
            goal, booking_url, booking_prompt,
            event_name, event_date, event_type,
            event_subject_matter, event_key_takeaways, event_replay_url
          `)
          .eq('id', campaign.id)
          .single();
        if (!isMounted) return;
        if (error) throw error;
        const loaded = {
          initialTone: data.initial_tone || '',
          initialPersona: data.initial_persona || '',
          engagementTone: data.engagement_tone || '',
          engagementPersona: data.engagement_persona || '',
          goal: data.goal || 'qualify_handoff',
          bookingUrl: data.booking_url || '',
          bookingPrompt: data.booking_prompt || '',
          eventName: data.event_name || '',
          eventDate: data.event_date || '',
          eventType: data.event_type || '',
          eventSubjectMatter: data.event_subject_matter || '',
          eventKeyTakeaways: data.event_key_takeaways || '',
          eventReplayUrl: data.event_replay_url || '',
          requireQualification: data.require_qualification_before_transfer || false,
          qualificationQuestions: data.qualification_questions || [],
          path1FollowupDelay: data.path1_followup_delay || 3,
          path1Enabled: data.path1_followup_delay !== 0,
          path1Channel: data.path1_followup_channel || 'sms',
          path2AiEnabled: data.path2_ai_followup_enabled !== false,
          path2SequenceEnabled: !(data.followup_delay_1 === 0 && data.followup_delay_2 === 0 && data.followup_delay_3 === 0),
          followups: [
            { id: 1, day: data.followup_delay_1 || 3, tone: data.followup_1_tone || '', persona: data.followup_1_persona || '', channel: data.followup_channel_1 || 'sms' },
            { id: 2, day: data.followup_delay_2 || 7, tone: data.followup_2_tone || '', persona: data.followup_2_persona || '', channel: data.followup_channel_2 || 'sms' },
            { id: 3, day: data.followup_delay_3 || 14, tone: data.followup_3_tone || '', persona: data.followup_3_persona || '', channel: data.followup_channel_3 || 'sms' },
          ],
        };
        setConfig(loaded);
        setSavedConfig(loaded);  // snapshot of DB state at load time
        setHasChanges(false);
      } catch (err) {
        console.error('Failed to load campaign config:', err);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [campaign.id]);

  // Tab completion status — computed from savedConfig (actual DB state), not in-memory config
  // Web chat REQUIRES a talk track (2026-09-10): generate-web-chat-reply builds its whole
  // brain from it (strategy_block + pillar_data + objectives_block), in every industry.
  // Voice stays exempt. Same rule as the readiness ring (consoleData.js) and the page and
  // server Go-live checks. Behavior and Qualification are green for web because web never
  // reads those fields and those tabs show a single line for web.
  const hasTalkTrack = !!(campaign.talk_track_type || campaign.custom_talk_track_id);
  const talkTrackGreen = isWeb ? hasTalkTrack : (isVoice || !hasDynamicField || hasTalkTrack);
  const tabStatus = useMemo(() => isReviewerAccount
    ? { setup: true, behavior: true, followups: true, knowledge: true, qualification: true }
    : ({
    setup: ((isWeb || isVoice) ? true : !!(campaign.ai_representative_name?.trim()))
           && (isWeb || isVoice || !!campaign.phone_number_id)
           && (isWeb || isVoice || !!campaign.timezone)
           && talkTrackGreen,
    behavior: isWeb ? true
              : !!(savedConfig?.initialTone) && !!(savedConfig?.initialPersona)
                && !!(savedConfig?.engagementTone) && !!(savedConfig?.engagementPersona),
    followups: isTrialing ? true : savedConfig !== null,
    knowledge: !!(knowledgeAssets?.some(a => a.tenant_id === campaign.tenant_id && a.is_master))
               || !!(selectedKnowledgeAssets?.[campaign.id]?.length),
    qualification: isWeb ? true
                   : !savedConfig || !savedConfig.requireQualification
                     || !!(savedConfig.qualificationQuestions?.some(q => q?.trim())),
  }), [isReviewerAccount, campaign, savedConfig, isWeb, isVoice, webChatCompanyName, talkTrackGreen, knowledgeAssets, selectedKnowledgeAssets, isTrialing]);

  const changeConfig = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }, []);

  const changeFollowup = useCallback((idx, field, value) => {
    setConfig(prev => {
      const updated = prev.followups.map((f, i) => {
        if (i === idx) return { ...f, [field]: value };
        // When follow-up 1 tone/persona changes, default later empty ones to match
        if (idx === 0 && i > 0 && (field === 'tone' || field === 'persona') && !f[field]) {
          return { ...f, [field]: value };
        }
        return f;
      });
      return { ...prev, followups: updated };
    });
    setHasChanges(true);
  }, []);

  const saveConfig = useCallback(async () => {
    if (!canRebuildBundle || !config) return;
    setSaving(true);
    setLocalError('');
    try {
      const campaignMetadata = {
        campaign_id: campaign.id,
        campaign_name: campaign.name,
        talk_track_type: campaign.talk_track_type,
        talk_track_specialty: campaign.talk_track_specialty,
      };
      const platformSettings = { ai_representative_name: { value: campaign.ai_representative_name || '' } };

      let tenantBehaviors = {};
      const { data: behaviorSettings } = await supabase
        .from('tenant_behavior_settings')
        .select('push_back_on_not_interested, challenge_budget_objections, gate_information')
        .eq('tenant_id', campaign.tenant_id)
        .single();
      if (behaviorSettings) {
        tenantBehaviors = {
          push_back_on_not_interested: behaviorSettings.push_back_on_not_interested,
          challenge_budget_objections: behaviorSettings.challenge_budget_objections,
          gate_information: behaviorSettings.gate_information,
        };
      }

      const { data: industryRow } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('tenant_id', campaign.tenant_id)
        .eq('key', 'industry')
        .single();
      const companyIndustry = industryRow?.value || '';

      const initialBundle = buildInitialInstruction({
        tone: config.initialTone,
        persona: config.initialPersona,
        industry: companyIndustry,
        campaignMetadata,
        platformSettings,
        tenantBehaviors,
        requireQualification: config.requireQualification,
        qualificationQuestions: config.qualificationQuestions,
        stableOnly: true,
      });
      const engagementBundle = buildInstructionBundle({
        tone: config.engagementTone,
        persona: config.engagementPersona,
        industry: companyIndustry,
        campaignMetadata,
        platformSettings,
        tenantBehaviors,
        requireQualification: config.requireQualification,
        qualificationQuestions: config.qualificationQuestions,
        stableOnly: true,
      });
      const followupBundles = config.followups.map((f, i) =>
        buildFollowupInstruction({
          tone: f.tone || config.engagementTone,
          persona: f.persona,
          industry: companyIndustry,
          campaignMetadata,
          followupStage: i + 1,
          platformSettings,
          tenantBehaviors,
          requireQualification: config.requireQualification,
          qualificationQuestions: config.qualificationQuestions,
          stableOnly: true,
        })
      );

      // Trialing tenants: force all follow-ups disabled to conserve message allotment
      const followupsDisabled = isTrialing;

      const updates = {
        ai_instruction_initial: initialBundle,
        ai_instruction_engagement: engagementBundle,
        ai_instruction_followup_1: followupBundles[0],
        ai_instruction_followup_2: followupBundles[1],
        ai_instruction_followup_3: followupBundles[2],
        path1_followup_delay: followupsDisabled ? 0 : (config.path1Enabled ? (config.path1FollowupDelay || 3) : 0),
        path1_followup_channel: config.path1Channel || 'sms',
        path2_ai_followup_enabled: followupsDisabled ? false : config.path2AiEnabled,
        initial_tone: config.initialTone,
        initial_persona: config.initialPersona,
        engagement_tone: config.engagementTone,
        engagement_persona: config.engagementPersona,
        goal: config.goal,
        booking_url: config.bookingUrl || null,
        booking_prompt: config.bookingPrompt || null,
        event_name: config.eventName || null,
        event_date: config.eventDate || null,
        event_type: config.eventType || null,
        event_subject_matter: config.eventSubjectMatter || null,
        event_key_takeaways: config.eventKeyTakeaways || null,
        event_replay_url: config.eventReplayUrl || null,
        require_qualification_before_transfer: config.requireQualification,
        qualification_questions: config.qualificationQuestions,
        followup_delay_1: followupsDisabled ? 0 : (config.path2SequenceEnabled ? (config.followups[0]?.day || 3) : 0),
        followup_delay_2: followupsDisabled ? 0 : (config.path2SequenceEnabled ? (config.followups[1]?.day || 7) : 0),
        followup_delay_3: followupsDisabled ? 0 : (config.path2SequenceEnabled ? (config.followups[2]?.day || 14) : 0),
        followup_1_tone: config.followups[0]?.tone || null,
        followup_1_persona: config.followups[0]?.persona || null,
        followup_2_tone: config.followups[1]?.tone || null,
        followup_2_persona: config.followups[1]?.persona || null,
        followup_3_tone: config.followups[2]?.tone || null,
        followup_3_persona: config.followups[2]?.persona || null,
        followup_channel_1: config.followups[0]?.channel || 'sms',
        followup_channel_2: config.followups[1]?.channel || 'sms',
        followup_channel_3: config.followups[2]?.channel || 'sms',
      };

      const { error } = await supabase.from('campaigns').update(updates).eq('id', campaign.id);
      if (error) throw error;

      // 🟢 2026-09-17 (Tom's call): auto-import used to fire on plain number
      // ASSIGNMENT, which meant a tenant building a multi-number SMS rotation
      // with zero voice intent still got every number imported into Retell
      // ($2/month each, real cost). Moved here — only fires once a tenant has
      // actually chosen Voice for a Follow-up step, matching what this was
      // supposed to do. Fire-and-forget, same as its other call sites; safe
      // to call even when nothing changed (already-imported numbers skip).
      const anyFollowupIsVoice =
        updates.path1_followup_channel === 'voice' ||
        updates.followup_channel_1 === 'voice' ||
        updates.followup_channel_2 === 'voice' ||
        updates.followup_channel_3 === 'voice';
      if (anyFollowupIsVoice) {
        autoImportCampaignNumberSilently?.(campaign.id);
      }

      setSavedConfig({ ...config });  // snapshot: what's now in DB
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      onConfigSaved(campaign.id, {
        initial_tone: config.initialTone,
        initial_persona: config.initialPersona,
        engagement_tone: config.engagementTone,
        engagement_persona: config.engagementPersona,
        ai_instruction_initial: initialBundle,
      });
    } catch (err) {
      setLocalError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [config, campaign, canRebuildBundle, onConfigSaved, isTrialing]);

  // Web chat has no Follow-ups tab: it never follows up (it answers visitors who are on
  // the site). Its Behavior tab is one line plus the web-only Goal card, and its
  // Qualification tab is one line, so the only thing it can save is the Goal.
  const TABS = [
    { id: 'setup', label: 'Setup' },
    { id: 'behavior', label: 'Behavior' },
    { id: 'followups', label: 'Follow-ups' },
    { id: 'knowledge', label: 'Knowledge' },
    { id: 'qualification', label: 'Qualification' },
  ].filter(tab => !(isWeb && tab.id === 'followups'));

  const needsSaveButton = isWeb
    ? activeTab === 'behavior'
    : ['behavior', 'followups', 'qualification'].includes(activeTab);

  // Console-style card: white surface, soft surfox border, on the modal's gray bg.
  const cardCls = "bg-white border border-surfox-border rounded-2xl shadow-[0_1px_2px_rgba(18,30,50,.05)] p-5";
  const cardLabel = "font-mono text-[11px] font-bold text-surfox-faint uppercase tracking-wider";

  return (
    <div className="space-y-6">
      {/* Tab Header — hidden when the panel is focused on a single section (opened from the Launch Console) */}
      {!singleSection && (
        <div className="flex bg-white border border-surfox-border rounded-2xl shadow-[0_1px_2px_rgba(18,30,50,.05)] overflow-x-auto">
          {TABS.map(tab => {
            const green = tabStatus[tab.id];
            const isActive = activeTab === tab.id;
            const isLocked = isTrialing && tab.id === 'followups';
            return (
              <button
                key={tab.id}
                onClick={() => !isLocked && setActiveTab(tab.id)}
                disabled={isLocked}
                className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-all duration-200 ${
                  isLocked
                    ? 'border-transparent text-gray-400 font-medium cursor-not-allowed'
                    : isActive
                      ? 'border-surfox-primary text-surfox-primary font-semibold bg-white'
                      : 'border-transparent text-gray-600 font-medium hover:text-gray-900 hover:bg-white/70'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 transition-colors duration-300 ${green ? 'bg-green-500' : 'bg-red-400'}`} />
                {tab.label}
                {isLocked && <>
                  <Lock className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-[10px] text-gray-400 italic">Paid plans</span>
                </>}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content — each section is its own console card on the modal background */}
      <div className="space-y-4">

          {/* ── SETUP TAB ── */}
          {activeTab === 'setup' && (
            <div className="space-y-4">
              {/* Channel & Identity card */}
              <div className={cardCls}>
                <div className={cardLabel + " mb-4"}>Channel &amp; Identity</div>
                {/* Channel segmented control */}
                {showWebChatExplainer && (
                  <WebChatExplainerModal
                    campaign={campaign}
                    currentHolderName={webChatHolderName}
                    onCancel={() => setShowWebChatExplainer(false)}
                    onConfirm={() => {
                      setShowWebChatExplainer(false);
                      // Explained here, so the parent skips its own window.confirm.
                      setWebChatCampaign(campaign.id, { confirmed: true });
                    }}
                  />
                )}
                <div className="inline-flex p-1 bg-surfox-bg border border-surfox-border rounded-xl mb-5">
                  {[
                    { key: 'sms',   label: 'SMS',      Icon: MessageSquare, active: !isWeb && !isVoice && !isMessenger, disabled: false,   onClick: () => { if (isWeb) setWebChatCampaign(campaign.id); if (isVoice) setVoiceCampaign(campaign.id); if (isMessenger) setMessengerCampaign(campaign.id); } },
                    // Turning web chat ON explains itself first; turning it OFF (back to SMS) is safe and immediate.
                    { key: 'web',   label: 'Web Chat', Icon: Globe,         active: isWeb,              disabled: isVoice || isMessenger, onClick: () => isWeb ? setWebChatCampaign(campaign.id) : setShowWebChatExplainer(true) },
                    // Opened up to every tenant 2026-09-13 — Voice AI v1 is built and tested
                    // end to end; this was "internal-only" only because it wasn't finished yet.
                    // Still gated on Phase 3's allows_bulk_upload flag, same as the Voice
                    // Re-engagement/Warm Transfer cards below: a bulk-upload campaign (built for
                    // CSV/aged/reactivation lists) becoming the tenant's designated voice channel
                    // is exactly the contradiction Phase 3 exists to prevent (retell-llm-stream.js
                    // already defends against it deep in call-answering, but nothing should let
                    // this UI create the contradiction in the first place). Still rendered when
                    // this campaign is ALREADY the voice campaign even if bulk-upload-flagged
                    // (should not happen going forward — see the DB trigger this ships with —
                    // but if it ever does, the owner still needs a way to switch it back to SMS).
                    ...((isVoiceEligible || isVoice)
                      ? [{ key: 'voice', label: 'Voice', Icon: Phone, active: isVoice, disabled: isWeb || isMessenger, onClick: () => setVoiceCampaign(campaign.id) }]
                      : []),
                    // Messenger: same simple toggle as Voice (no explainer modal — mirrors the
                    // simpler of the two existing patterns, not Web Chat's confirm dialog).
                    // Disabled alongside Web/Voice: one campaign is one channel.
                    { key: 'messenger', label: 'Messenger', Icon: Facebook, active: isMessenger, disabled: isWeb || isVoice, onClick: () => setMessengerCampaign(campaign.id) },
                  ].map(seg => (
                    <button
                      key={seg.key}
                      onClick={seg.onClick}
                      disabled={seg.disabled}
                      className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-1.5 rounded-lg transition-all ${
                        seg.active
                          ? 'bg-white text-surfox-primary shadow-[0_1px_2px_rgba(18,30,50,.08)]'
                          : 'text-surfox-muted hover:text-surfox-ink'
                      } ${seg.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      <seg.Icon size={14} /> {seg.label}
                    </button>
                  ))}
                </div>

              {/* ── Identity & Connection ── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* AI Name / Company Name */}
                {isWeb ? (
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">Company Name</label>
                    <input
                      type="text"
                      value={webChatCompanyName}
                      onChange={(e) => setWebChatCompanyName(e.target.value)}
                      onBlur={(e) => saveWebChatCompanyName(e.target.value)}
                      placeholder="e.g., SurFox AI"
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                    />
                  </div>
                ) : isVoice ? (
                  <div className="space-y-1 sm:col-span-2">
                    <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-800 leading-relaxed">
                      <strong>Voice campaign.</strong> Inbound calls for this campaign are answered by
                      the AI agent configured in <em>Settings → Voice Channel</em>. The phone number,
                      voice, greeting, and after-hours behavior are all managed there — not per-campaign.
                      This campaign provides goal, tone, persona, knowledge, and qualification context
                      to the voice agent at call time.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">
                      AI Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={campaign.ai_representative_name || ''}
                      onChange={(e) => updateCampaignAIRepresentativeName(campaign.id, e.target.value)}
                      placeholder="e.g., Alex, Sarah, Mike"
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                    />
                  </div>
                )}

                {/* Phone Number — hidden for voice (number comes from Voice Channel settings)
                    and for web chat (it texts nobody; 2026-09-10 it used to show greyed out). */}
                {!isVoice && !isWeb && (
                  <div className="space-y-1">
                    <label className={`block text-xs font-medium ${isWeb ? 'text-gray-400' : 'text-gray-600'}`}>
                      Phone Number {!isWeb && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      value={campaign.phone_number_id || ''}
                      onChange={(e) => updateCampaignPhoneNumber(campaign.id, e.target.value || null)}
                      disabled={isWeb}
                      className={`w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary shadow-sm ${isWeb ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'}`}
                    >
                      <option value="">{isWeb ? 'Not required for web' : 'No phone assigned'}</option>
                      {!isWeb && selectablePhoneNumbers.map(phone => (
                        <option key={phone.id} value={phone.id}>{phone.phone_number}</option>
                      ))}
                    </select>
                  </div>
                )}

                {!isVoice && !isWeb && (
                  <AdditionalPhonesSection
                    campaign={campaign}
                    phoneNumbers={selectablePhoneNumbers}
                    onUpdate={updateCampaignAdditionalPhones}
                  />
                )}

                {/* Timezone — SMS/voice only. It sets the texting window; web chat answers
                    whenever a visitor is on the site, and the ring never required it for web. */}
                {!isWeb && (
                <div className="space-y-1">
                  <label className="block text-[12px] font-semibold text-surfox-muted">
                    Timezone <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={campaign.timezone || ''}
                    onChange={(e) => updateCampaignTimezone(campaign.id, e.target.value)}
                    className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                  >
                    <option value="">Select timezone</option>
                    <option value="America/New_York">Eastern (EST/EDT)</option>
                    <option value="America/Chicago">Central (CST/CDT)</option>
                    <option value="America/Denver">Mountain (MST/MDT)</option>
                    <option value="America/Los_Angeles">Pacific (PST/PDT)</option>
                  </select>
                </div>
                )}

                {/* Talk Track (industry-specific) */}
                {hasDynamicField && (
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">
                      {getDynamicColumnHeader(tenantIndustry)}
                    </label>
                    <DynamicFieldComponent
                      campaign={campaign}
                      tenantIndustry={tenantIndustry}
                      getDynamicDropdownOptions={getDynamicDropdownOptions}
                      updateCampaignDynamicField={updateCampaignDynamicField}
                      customTalkTracks={customTalkTracks}
                    />
                  </div>
                )}

                {/* Web chat needs a talk track to go live (2026-09-10), in every industry,
                    and the picker above only shows for some industries. Say so here and
                    point at Surf, which is what builds talk tracks. */}
                {isWeb && !hasTalkTrack && (
                  <div className="space-y-1 sm:col-span-2">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed">
                      Web chat answers from this campaign's talk track, and it does not have one yet.{' '}
                      <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('surf:open'))} className="font-semibold underline">
                        Build it with Surf
                      </button>
                    </div>
                  </div>
                )}

                {/* Assignment — hidden for solo tenants (no invited teammates) */}
                {hasMultipleTeammates && (
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">Assignment</label>
                    <select
                      value={campaign.assigned_to_sales_team_id || ''}
                      onChange={(e) => updateCampaignAssignment(campaign.id, e.target.value || null)}
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                    >
                      <option value="">Unassigned</option>
                      {(salesTeamsByTenant[campaign.tenant_id] || []).map(member => (
                        <option key={member.sales_team_id} value={member.sales_team_id}>
                          {member.full_name || member.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              </div>
              {/* end Channel & Identity card */}

              {/* Event Details — only when this campaign is an Event Follow-up.
                  Lives in the Setup tab next to the talk track picker because
                  that's where talk_track_type is selected. Gated on `config`
                  so the inputs don't render undefined values while the panel
                  is still loading its state. */}
              {/* Not for web chat: event details feed the SMS event follow-up copy only. */}
              {campaign.talk_track_type === 'event_followup' && config && !isWeb && (
                <div className={cardCls}>
                  <div className="flex items-center gap-2 mb-4">
                    <Megaphone className="w-4 h-4 text-surfox-primary" />
                    <h5 className="font-mono text-[11px] font-bold text-surfox-faint uppercase tracking-wider">Event Details</h5>
                  </div>
                  <div className="space-y-3 max-w-2xl">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[12px] font-semibold text-surfox-muted mb-1">
                          Event Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={config.eventName}
                          onChange={(e) => changeConfig('eventName', e.target.value)}
                          disabled={!canEditInstructions}
                          placeholder="e.g. The 2026 SDR Productivity Webinar"
                          className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Event Date</label>
                        <input
                          type="date"
                          value={config.eventDate}
                          onChange={(e) => changeConfig('eventDate', e.target.value)}
                          disabled={!canEditInstructions}
                          className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Event Type</label>
                      <select
                        value={config.eventType}
                        onChange={(e) => changeConfig('eventType', e.target.value)}
                        disabled={!canEditInstructions}
                        className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                      >
                        <option value="">Select event type...</option>
                        <option value="webinar">Webinar</option>
                        <option value="trade_show">Trade Show</option>
                        <option value="conference">Conference</option>
                        <option value="live_demo">Live Demo</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-surfox-muted mb-1">
                        Subject Matter <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={config.eventSubjectMatter}
                        onChange={(e) => changeConfig('eventSubjectMatter', e.target.value)}
                        disabled={!canEditInstructions}
                        rows={3}
                        placeholder="Describe what your event covered. The more detail the better."
                        className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-surfox-muted mb-1">
                        Key Takeaways or Offers Made <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        value={config.eventKeyTakeaways}
                        onChange={(e) => changeConfig('eventKeyTakeaways', e.target.value)}
                        disabled={!canEditInstructions}
                        rows={3}
                        placeholder="What offers were made? What were the top 3 things covered?"
                        className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-surfox-muted mb-1">
                        Replay Link <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={config.eventReplayUrl}
                        onChange={(e) => changeConfig('eventReplayUrl', e.target.value)}
                        disabled={!canEditInstructions}
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Notifications & Routing ── */}
              <div className={cardCls}>
                <h5 className="font-mono text-[11px] font-bold text-surfox-faint uppercase tracking-wider mb-4">Notifications & Routing</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Forward Calls To — was mislabeled "(SMS only)"; it never was. This is a
                      real, live number this campaign forwards a human to: for SMS, it's read
                      by src/api_routes/inbound.js's plain Twilio <Dial> call-forwarding (a
                      caller who dials THIS campaign's SMS-sending number gets forwarded here,
                      no AI involved). Voice AI v1 Phase 9 reuses the SAME column as the Retell
                      live-transfer destination (src/ws/retell-llm-stream.js reads
                      campaign.forward_call_number when warm_transfer_mode='live_transfer') —
                      same real-world meaning ("who picks up for this campaign"), no reason to
                      make a tenant enter the same number twice in two different fields. The two
                      mechanisms are otherwise unrelated: inbound.js matches by
                      campaign.phone_number_id (a plain Twilio number), which a voice campaign
                      never has, so there's no collision between them. */}
                  {!isWeb && (
                    <div className="space-y-1">
                      <label className="block text-[12px] font-semibold text-surfox-muted">Forward Calls To</label>
                      <input
                        type="tel"
                        defaultValue={campaign.forward_call_number || ''}
                        onBlur={(e) => updateCampaignForwardNumber(campaign.id, e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                      />
                      {isVoice && (
                        <p className="text-[11px] leading-relaxed text-surfox-faint">
                          {campaign.warm_transfer_mode === 'live_transfer'
                            ? 'Live-transfer destination for this campaign\'s Warm Transfer setting below.'
                            : 'Only used if Warm Transfer below is set to Live transfer.'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Hot Lead Notification Email */}
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">Hot Lead Email</label>
                    <input
                      type="email"
                      defaultValue={campaign.notification_email || ''}
                      onBlur={(e) => updateCampaignNotificationEmail(campaign.id, e.target.value)}
                      placeholder="e.g., sales@yourcompany.com"
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                    />
                  </div>

                  {/* Hot Lead CC Email */}
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">Hot Lead CC Email</label>
                    <input
                      type="email"
                      defaultValue={campaign.notification_cc_email || ''}
                      onBlur={(e) => updateCampaignNotificationCcEmail(campaign.id, e.target.value)}
                      placeholder="e.g., manager@yourcompany.com"
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                    />
                  </div>

                  {/* Visitor Support Email — web chat only.
                      The other two addresses in this card go INWARD (who on your team gets
                      told about a hot lead). This one goes OUTWARD: the AI reads it aloud to
                      a stranger on your website. The helper text has to say that, because
                      "Hot Lead Email" and "Support Email" sitting side by side is a coin
                      flip otherwise, and getting it wrong publishes an internal inbox. */}
                  {isWeb && (
                    <div className="space-y-1 sm:col-span-2">
                      <label className="block text-[12px] font-semibold text-surfox-muted">Visitor Support Email</label>
                      <input
                        type="email"
                        defaultValue={campaign.visitor_support_email || ''}
                        onBlur={(e) => updateCampaignVisitorSupportEmail(campaign.id, e.target.value)}
                        placeholder="e.g., support@yourcompany.com"
                        className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                      />
                      <p className="text-[11px] leading-relaxed text-surfox-faint">
                        What the AI gives a website visitor who asks for a contact address during a chat.
                        Leave it blank and it will not offer one. This is not where hot lead alerts go.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Voice Calling for this campaign's number (architecture revision, 2026-09-13,
                  strategic-planning/VOICE-AI-V1-HANDOFF.md) — REPLACES the old Voice
                  Re-engagement card. Voice is no longer a separate, independently-timed
                  feature with its own on/off switch and delay; it's a per-step CHANNEL CHOICE
                  on the Follow-up Sequence itself (see the Follow-ups tab below). All this
                  card does is the one-time, explicit step that MAKES voice possible for this
                  campaign at all: importing its own phone number into Retell. Real, ongoing
                  cost ($2/month per imported number, per Retell's published pricing, charged
                  whether or not it's ever called) — never triggered automatically.
                  🟢 2026-09-17: excluded on the tenant's OWN designated Voice campaign
                  (isVoice) — that campaign already gets its number imported as part of
                  Settings > Voice Channel provisioning, a different flow entirely,
                  and this card's "not yet enabled" copy/button was showing on it
                  regardless of that, confusingly implying voice wasn't set up when it
                  already was. This card is for an ordinary SMS campaign that wants a
                  Follow-up step to be able to use Voice, not for the dedicated line. */}
              {isVoiceEligible && !isVoice && (
                <div className={cardCls}>
                  <h5 className="font-mono text-[11px] font-bold text-surfox-faint uppercase tracking-wider mb-4">Voice Calling</h5>
                  {voiceNumberImported ? (
                    <p className="text-[12px] text-green-700 leading-relaxed">
                      ✅ Enabled — this campaign's number is imported into your voice AI agent.
                      Follow-up steps on the Follow-ups tab can be set to Voice.
                    </p>
                  ) : (
                    <>
                      <p className="text-[12px] text-surfox-muted leading-relaxed mb-4">
                        Before any Follow-up step on this campaign can be set to Voice (see the
                        Follow-ups tab), this campaign's own phone number has to be imported into
                        your voice AI agent once. After that, a lead who calls back the number
                        that texted them reaches your voice AI with full context — not a busy
                        signal or a generic voicemail.
                      </p>
                      <button
                        type="button"
                        disabled={importingVoiceNumber}
                        onClick={async () => {
                          setImportingVoiceNumber(true);
                          try {
                            await importCampaignNumberToRetell(campaign.id);
                            // Re-check rather than assume — a partial/failed import
                            // (surfaced via setError inside importCampaignNumberToRetell)
                            // must not flip this card to "enabled" when it isn't.
                            if (campaign.phone_number_id) {
                              const { data, error } = await supabase
                                .from('phone_numbers')
                                .select('retell_imported_at')
                                .eq('id', campaign.phone_number_id)
                                .maybeSingle();
                              setVoiceNumberImported(!error && !!data?.retell_imported_at);
                            }
                          } finally {
                            setImportingVoiceNumber(false);
                          }
                        }}
                        className="text-[12px] font-semibold px-3 py-2 rounded-lg border border-surfox-primary/40 text-surfox-primary hover:bg-surfox-primary/5 disabled:opacity-50"
                      >
                        {importingVoiceNumber ? 'Importing…' : 'Enable Voice Calling for this campaign\'s number'}
                      </button>
                      <p className="mt-2 text-[11px] leading-relaxed text-surfox-faint">
                        $2/month per imported number (Retell's own fee), for as long as it stays
                        imported — not a per-call cost. Safe to run more than once; already-imported
                        numbers are skipped.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* ── Warm Transfer (Voice AI v1 Phase 8/9, strategic-planning/VOICE-AI-V1-HANDOFF.md
                  §0.4) — same isVoiceEligible gate and card styling as Voice Re-engagement above
                  (a separate card, not folded into it: this is a different decision — HOW a
                  voice call that turns hot reaches a human, not whether/when to place a
                  re-engagement call). Both options are real and selectable as of Phase 9.
                  "Live transfer" copy is deliberately honest about what it actually is: a COLD
                  transfer (the AI hands the caller off and drops out — Retell's own docs;
                  see gotchas.md's Voice AI section for why a warm transfer with a private
                  whisper to the destination is not buildable against this account's agent
                  type). campaigns.warm_transfer_mode defaults to 'async_handoff' on every
                  campaign, so the fallback below is a display nicety for an unloaded/undefined
                  value, not a real ambiguity. */}
              {isVoiceEligible && (
                <div className={cardCls}>
                  <h5 className="font-mono text-[11px] font-bold text-surfox-faint uppercase tracking-wider mb-4">Warm Transfer</h5>
                  <p className="text-[12px] text-surfox-muted leading-relaxed mb-4">
                    When a voice call turns into a hot lead, this is how it reaches your team.
                  </p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => updateCampaignWarmTransferMode(campaign.id, 'async_handoff', campaign.forward_call_number)}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                        (campaign.warm_transfer_mode || 'async_handoff') === 'async_handoff'
                          ? 'border-surfox-primary bg-surfox-primary/5'
                          : 'border-surfox-border bg-white hover:border-surfox-primary/40'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${(campaign.warm_transfer_mode || 'async_handoff') === 'async_handoff' ? 'bg-surfox-primary' : 'bg-surfox-border'}`} />
                        <span className="text-[12px] font-semibold text-surfox-ink">Async handoff</span>
                        {(campaign.warm_transfer_mode || 'async_handoff') === 'async_handoff' && (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-surfox-primary bg-surfox-primary/10 rounded px-1.5 py-0.5">Selected</span>
                        )}
                      </div>
                      <p className="text-[11px] leading-relaxed text-surfox-faint mt-1">
                        The AI ends the call, marks the lead Hot, and notifies your team the same
                        way every other channel's hot lead does. A team member calls back using
                        the existing click-to-call dialer.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateCampaignWarmTransferMode(campaign.id, 'live_transfer', campaign.forward_call_number)}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                        campaign.warm_transfer_mode === 'live_transfer'
                          ? 'border-surfox-primary bg-surfox-primary/5'
                          : 'border-surfox-border bg-white hover:border-surfox-primary/40'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${campaign.warm_transfer_mode === 'live_transfer' ? 'bg-surfox-primary' : 'bg-surfox-border'}`} />
                        <span className="text-[12px] font-semibold text-surfox-ink">Live transfer</span>
                        {campaign.warm_transfer_mode === 'live_transfer' && (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-surfox-primary bg-surfox-primary/10 rounded px-1.5 py-0.5">Selected</span>
                        )}
                      </div>
                      <p className="text-[11px] leading-relaxed text-surfox-faint mt-1">
                        The AI can connect the caller live to "Forward Calls To" above, right on
                        the call. This is a direct hand-off, not a warm transfer with a private
                        briefing to whoever picks up — they hear the same thing the caller does.
                        If nobody answers, the call ends and your team is notified to call the
                        lead back, the same way async hand-off works.
                      </p>
                      {!campaign.forward_call_number && (
                        <p className="text-[11px] leading-relaxed text-amber-700 mt-1.5">
                          Set "Forward Calls To" above first — that's the number this transfers to.
                        </p>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Hot Lead Assignment — hidden for solo tenants (no invited teammates) ── */}
              {hasMultipleTeammates && (
              <div className={cardCls}>
                <h5 className="font-mono text-[11px] font-bold text-surfox-faint uppercase tracking-wider mb-4">Hot Lead Assignment</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-[12px] font-semibold text-surfox-muted">Distribution Mode</label>
                    <select
                      value={campaign.hot_lead_distribution_mode || ''}
                      onChange={(e) => updateHotLeadDistribution(campaign.id, 'mode', e.target.value || null)}
                      className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                    >
                      <option value="">No assignment</option>
                      <option value="single">Assign to specific person</option>
                      <option value="round_robin">Round robin team</option>
                    </select>
                  </div>
                  {campaign.hot_lead_distribution_mode === 'single' && (
                    <div className="space-y-1">
                      <label className="block text-[12px] font-semibold text-surfox-muted">Assignee</label>
                      <select
                        value={campaign.hot_lead_single_assignee_id || ''}
                        onChange={(e) => updateHotLeadDistribution(campaign.id, 'single_assignee', e.target.value || null)}
                        className="w-full text-sm border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white shadow-sm"
                      >
                        <option value="">Select assignee...</option>
                        {(salesTeamsByTenant[campaign.tenant_id] || []).map(member => (
                          <option key={member.sales_team_id} value={member.sales_team_id}>
                            {member.full_name || member.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {campaign.hot_lead_distribution_mode === 'round_robin' && (
                    <div className="space-y-1">
                      <label className="block text-[12px] font-semibold text-surfox-muted">Team Members</label>
                      <div className="border border-gray-200 rounded-lg px-3 py-2 max-h-32 overflow-y-auto bg-gray-50/50">
                        {(salesTeamsByTenant[campaign.tenant_id] || []).length === 0 ? (
                          <div className="text-sm text-gray-500">No team members available</div>
                        ) : (
                          (salesTeamsByTenant[campaign.tenant_id] || []).map(member => {
                            const isSelected = campaign.hot_lead_round_robin_team?.includes(member.sales_team_id);
                            return (
                              <label key={member.sales_team_id} className="flex items-center py-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => updateHotLeadDistribution(campaign.id, 'round_robin_toggle', {
                                    memberId: member.sales_team_id,
                                    checked: e.target.checked
                                  })}
                                  className="mr-3 h-4 w-4 text-surfox-primary focus:ring-2 focus:ring-surfox-primary/25 border-gray-300 rounded"
                                />
                                <span className="text-sm truncate">{member.full_name || member.email}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          )}

          {/* ── BEHAVIOR TAB ── */}
          {activeTab === 'behavior' && !config && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-surfox-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {activeTab === 'behavior' && config && (
            <div className={cardCls + " space-y-6"}>
              {/* Web chat never reads the tone/persona fields (generate-web-chat-reply's
                  campaign select has none of them), so they are replaced by one line. The
                  web-only Goal card below stays: web DOES read goal and booking_url. */}
              {isWeb ? (
                <p className="text-sm text-surfox-muted">Web chat uses this campaign's talk track for its voice and qualifying questions.</p>
              ) : (<>
              {/* Playbooks */}
              {strategyTemplates && strategyTemplates.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-surfox-primary" />
                    <h5 className="font-semibold text-surfox-ink text-sm">Playbooks</h5>
                    <span className="text-xs text-gray-400">Apply proven patterns</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {strategyTemplates.map((tpl, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (!canEditInstructions) return;
                          setConfig(prev => ({
                            ...prev,
                            initialTone: tpl.initialTone,
                            initialPersona: tpl.initialPersona,
                            engagementTone: tpl.engagementTone,
                            engagementPersona: tpl.engagementPersona,
                            followups: prev.followups.map((f, i) => ({
                              ...f,
                              tone: tpl.followups[i]?.tone || tpl.initialTone,
                              persona: tpl.followups[i]?.persona || followupPersonaOptions[i] || 'Gentle Reminder',
                            })),
                          }));
                          setHasChanges(true);
                        }}
                        disabled={!canEditInstructions}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-surfox-primarySoft text-gray-700 hover:text-surfox-primary text-sm rounded-lg transition-colors disabled:opacity-50"
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Initial Contact */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-surfox-primary" />
                  <h5 className="font-semibold text-surfox-ink text-sm">Initial Contact</h5>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Tone <span className="text-red-500">*</span></label>
                    <select
                      value={config.initialTone}
                      onChange={(e) => changeConfig('initialTone', e.target.value)}
                      disabled={!canEditInstructions}
                      className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                    >
                      <option value="">Select tone...</option>
                      {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Persona <span className="text-red-500">*</span></label>
                    <select
                      value={config.initialPersona}
                      onChange={(e) => changeConfig('initialPersona', e.target.value)}
                      disabled={!canEditInstructions}
                      className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                    >
                      <option value="">Select persona...</option>
                      {initialPersonaOptions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Engagement Phase */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-surfox-primary" />
                  <h5 className="font-semibold text-surfox-ink text-sm">Engagement Phase</h5>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Tone <span className="text-red-500">*</span></label>
                    <select
                      value={config.engagementTone}
                      onChange={(e) => changeConfig('engagementTone', e.target.value)}
                      disabled={!canEditInstructions}
                      className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                    >
                      <option value="">Select tone...</option>
                      {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Persona <span className="text-red-500">*</span></label>
                    <select
                      value={config.engagementPersona}
                      onChange={(e) => changeConfig('engagementPersona', e.target.value)}
                      disabled={!canEditInstructions}
                      className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                    >
                      <option value="">Select persona...</option>
                      {engagementPersonaOptions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              </>)}

              {/* Goal — web chat only (SMS ignores campaigns.goal) */}
              {isWeb && <div>
                <div className="flex items-center gap-2 mb-3">
                  <Flag className="w-4 h-4 text-surfox-primary" />
                  <h5 className="font-semibold text-surfox-ink text-sm">Goal</h5>
                </div>
                <div className="space-y-3 max-w-md">
                  <div>
                    <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Primary Goal</label>
                    <select
                      value={config.goal}
                      onChange={(e) => changeConfig('goal', e.target.value)}
                      disabled={!canEditInstructions}
                      className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                    >
                      <option value="qualify_handoff">Qualify and Hand Off</option>
                      <option value="book_demo">Book a Demo</option>
                      <option value="schedule_call">Schedule a Phone Call</option>
                    </select>
                  </div>
                  {(config.goal === 'book_demo' || config.goal === 'schedule_call') && (
                    <>
                      <div>
                        <label className="block text-[12px] font-semibold text-surfox-muted mb-1">
                          Booking URL <span className="text-gray-400">(Calendly, HubSpot, Cal.com, etc.)</span>
                        </label>
                        <input
                          type="text"
                          value={config.bookingUrl}
                          onChange={(e) => changeConfig('bookingUrl', e.target.value)}
                          disabled={!canEditInstructions}
                          placeholder="https://calendly.com/your-link"
                          className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Custom booking prompt <span className="text-gray-400">(optional)</span></label>
                        <input
                          type="text"
                          value={config.bookingPrompt}
                          onChange={(e) => changeConfig('bookingPrompt', e.target.value)}
                          disabled={!canEditInstructions}
                          placeholder="e.g. Would you be open to a quick 20-minute Zoom?"
                          className="w-full px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary disabled:bg-gray-50 text-sm"
                        />
                        <p className="text-xs text-gray-400 mt-1">If blank, AI uses its own judgment on when and how to push.</p>
                      </div>
                    </>
                  )}
                </div>
              </div>}
            </div>
          )}

          {/* ── FOLLOW-UPS TAB ── */}
          {activeTab === 'followups' && !config && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-surfox-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {/* Not for web chat, which never follows up (its tab is hidden too). */}
          {activeTab === 'followups' && config && !isWeb && (
            <div className={cardCls + " space-y-5"}>
              {/* Voice AI v1 architecture revision (2026-09-13): each step below now has its
                  own Channel choice (SMS or Voice), gated on isVoiceEligible the same way the
                  Voice Calling card on the Setup tab is. This REPLACES the old "these are two
                  independent timers" banner — there is only one ordered sequence now, so the
                  same-time-collision problem that banner was explaining is structurally gone,
                  not just explained. */}
              {isVoiceEligible && (
                // Made more pronounced 2026-09-13 (was a thin, near-white tinted box easy to
                // scroll past) — a solid-color badge + icon + slightly larger text so a
                // tenant actually notices this capability exists instead of only finding it
                // by already knowing to look for it.
                <div className="flex items-start gap-3 rounded-xl border-2 border-surfox-primary bg-surfox-primary/10 px-4 py-3 shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-surfox-primary flex items-center justify-center">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-[13px] leading-relaxed text-surfox-ink">
                    <span className="font-bold text-surfox-primary">Voice available:</span> any step below can be
                    set to Voice instead of SMS — same day, one attempt either way, never both.
                    Requires this campaign's number to be imported into Retell first (Setup tab →
                    Voice Calling). A step set to Voice that isn't ready yet falls back to SMS
                    automatically rather than skipping the lead.
                  </p>
                </div>
              )}

              {/* Path 1: No-Reply */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-surfox-primary" />
                    <h5 className="font-semibold text-surfox-ink text-sm">No-Reply Follow-up</h5>
                  </div>
                  <div
                    onClick={() => canEditInstructions && changeConfig('path1Enabled', !config.path1Enabled)}
                    className={`relative w-8 h-4 rounded-full transition-colors ${config.path1Enabled ? 'bg-surfox-primary' : 'bg-gray-300'} ${canEditInstructions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${config.path1Enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </div>
                <div className="bg-surfox-bg border border-surfox-line rounded-xl p-3">
                  <p className="text-xs text-surfox-faint mb-2">For leads who never respond (1 follow-up, then marked dead)</p>
                  {config.path1Enabled && (
                    // GRID, not flex+max-w — the flex version relied on max-w (a ceiling,
                    // not a size) plus a w-full child to imply column widths, which some
                    // container widths resolved by stacking the two fields into separate
                    // rows instead of side by side (found live 2026-09-13). Grid gives each
                    // column a real, deterministic width, same fix already used for the
                    // Follow-up 1/2/3 rows below.
                    <div className={isVoiceEligible && voiceNumberImported ? "grid grid-cols-2 gap-3 max-w-xs" : "max-w-[120px]"}>
                      <div>
                        <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Days before follow-up</label>
                        <input
                          type="number"
                          value={config.path1FollowupDelay || 3}
                          onChange={(e) => changeConfig('path1FollowupDelay', parseInt(e.target.value) || 3)}
                          disabled={!canEditInstructions}
                          min="1" max="30"
                          className="w-full px-2 py-1.5 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-100 text-sm"
                        />
                      </div>
                      {isVoiceEligible && voiceNumberImported && (
                        <div>
                          <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Channel</label>
                          <select
                            value={config.path1Channel || 'sms'}
                            onChange={(e) => changeConfig('path1Channel', e.target.value)}
                            disabled={!canEditInstructions}
                            className="w-full px-2 py-1.5 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-100 text-sm"
                          >
                            <option value="sms">SMS</option>
                            <option value="voice">Voice</option>
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Path 2: AI-Scheduled */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-surfox-primary" />
                    <h5 className="font-semibold text-surfox-ink text-sm">AI-Scheduled Follow-up</h5>
                  </div>
                  <div
                    onClick={() => canEditInstructions && changeConfig('path2AiEnabled', !config.path2AiEnabled)}
                    className={`relative w-8 h-4 rounded-full transition-colors ${config.path2AiEnabled ? 'bg-surfox-primary' : 'bg-gray-300'} ${canEditInstructions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${config.path2AiEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </div>
                <div className="bg-surfox-bg border border-surfox-line rounded-xl p-3">
                  <p className="text-xs text-surfox-faint">When a lead says "reach me next month", the AI honors that timing and follows up on the requested date.</p>
                </div>
              </div>

              {/* Follow-up Sequence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-surfox-primary" />
                    <h5 className="font-semibold text-surfox-ink text-sm">Follow-up Sequence</h5>
                  </div>
                  <div
                    onClick={() => canEditInstructions && changeConfig('path2SequenceEnabled', !config.path2SequenceEnabled)}
                    className={`relative w-8 h-4 rounded-full transition-colors ${config.path2SequenceEnabled ? 'bg-surfox-primary' : 'bg-gray-300'} ${canEditInstructions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${config.path2SequenceEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </div>
                <div className="bg-surfox-bg border border-surfox-line rounded-xl p-3 mb-3">
                  <p className="text-xs text-surfox-faint">For leads who engaged then went silent. Sends timed follow-ups before marking dead.</p>
                </div>
                <div className={`space-y-3 ${!config.path2SequenceEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                  {config.followups.map((followup, idx) => (
                    <div key={followup.id} className="bg-surfox-bg border border-surfox-line rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${idx === 0 ? 'bg-surfox-primary' : idx === 1 ? 'bg-surfox-cyan' : 'bg-surfox-amber'}`}>
                          {idx + 1}
                        </div>
                        <span className="text-sm font-medium text-gray-700">Follow-up {idx + 1}</span>
                      </div>
                      <div className={isVoiceEligible ? "grid grid-cols-4 gap-2" : "grid grid-cols-3 gap-2"}>
                        <div>
                          <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Days</label>
                          <input
                            type="number"
                            value={followup.day}
                            onChange={(e) => changeFollowup(idx, 'day', parseInt(e.target.value) || 1)}
                            disabled={!canEditInstructions}
                            min="1"
                            className="w-full px-2 py-1.5 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-100 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Tone</label>
                          <select
                            value={followup.tone}
                            onChange={(e) => changeFollowup(idx, 'tone', e.target.value)}
                            disabled={!canEditInstructions}
                            className="w-full px-2 py-1.5 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-100 text-sm"
                          >
                            <option value="">Same</option>
                            {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Persona</label>
                          <select
                            value={followup.persona}
                            onChange={(e) => changeFollowup(idx, 'persona', e.target.value)}
                            disabled={!canEditInstructions}
                            className="w-full px-2 py-1.5 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-100 text-sm"
                          >
                            <option value="">Select...</option>
                            {followupPersonaOptions.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        {isVoiceEligible && voiceNumberImported && (
                          <div>
                            <label className="block text-[12px] font-semibold text-surfox-muted mb-1">Channel</label>
                            <select
                              value={followup.channel || 'sms'}
                              onChange={(e) => changeFollowup(idx, 'channel', e.target.value)}
                              disabled={!canEditInstructions}
                              className="w-full px-2 py-1.5 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-100 text-sm"
                            >
                              <option value="sms">SMS</option>
                              <option value="voice">Voice</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── KNOWLEDGE TAB ── */}
          {activeTab === 'knowledge' && (
            <div className={cardCls}>
              <p className="text-sm text-surfox-muted mb-4">Assign knowledge documents to this campaign. The AI will reference them when answering lead questions.</p>
              <KnowledgeAssetsDropdown
                campaign={campaign}
                knowledgeAssets={knowledgeAssets.filter(ka => ka.tenant_id === campaign.tenant_id)}
                selectedAssets={selectedKnowledgeAssets[campaign.id]}
                onUpdate={async (assetId, checked) => {
                  const currentSelected = selectedKnowledgeAssets[campaign.id] || [];
                  const newSelected = checked
                    ? [...currentSelected, assetId]
                    : currentSelected.filter(id => id !== assetId);
                  setSelectedKnowledgeAssets({ ...selectedKnowledgeAssets, [campaign.id]: newSelected });
                  await updateCampaignKnowledgeLinks(campaign.id, newSelected);
                }}
              />
            </div>
          )}

          {/* ── QUALIFICATION TAB ── */}
          {activeTab === 'qualification' && !config && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-surfox-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {/* Web chat never reads require_qualification_before_transfer or
              qualification_questions; its qualifying questions come from the talk track. */}
          {activeTab === 'qualification' && config && isWeb && (
            <div className={cardCls}>
              <p className="text-sm text-surfox-muted">Web chat uses this campaign's talk track for its voice and qualifying questions.</p>
            </div>
          )}
          {activeTab === 'qualification' && config && !isWeb && (
            <div className={cardCls + " space-y-4"}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h5 className="font-semibold text-surfox-ink text-sm">Qualification Questions</h5>
                  <p className="text-xs text-surfox-faint mt-0.5">Questions the AI asks before handing off to your team.</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm text-gray-600">Require before transfer</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={config.requireQualification}
                      onChange={(e) => changeConfig('requireQualification', e.target.checked)}
                      disabled={!canEditInstructions}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-surfox-primary/25 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-surfox-primary"></div>
                  </div>
                </label>
              </div>

              {config.requireQualification ? (
                <div className="space-y-2">
                  {config.qualificationQuestions.map((q, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => {
                          const updated = [...config.qualificationQuestions];
                          updated[idx] = e.target.value;
                          changeConfig('qualificationQuestions', updated);
                        }}
                        disabled={!canEditInstructions}
                        placeholder={`Question ${idx + 1}...`}
                        className="flex-1 px-3 py-2 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 disabled:bg-gray-50 text-sm"
                      />
                      <button
                        onClick={() => changeConfig('qualificationQuestions', config.qualificationQuestions.filter((_, i) => i !== idx))}
                        disabled={!canEditInstructions}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => changeConfig('qualificationQuestions', [...config.qualificationQuestions, ''])}
                    disabled={!canEditInstructions}
                    className="flex items-center gap-2 text-sm font-semibold text-surfox-primary hover:brightness-110"
                  >
                    <Plus className="w-4 h-4" />
                    Add Question
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Enable "Require before transfer" to add qualification questions.</p>
                </div>
              )}
            </div>
          )}

          {/* Save & Rebuild button (Behavior / Follow-ups / Qualification) */}
          {needsSaveButton && config && (
            <div className={cardCls + " flex items-center justify-between gap-4 !py-4"}>
              <div className="text-sm">
                {localError && <span className="text-red-600">{localError}</span>}
                {saveSuccess && <span className="text-green-600">Saved successfully!</span>}
                {!localError && !saveSuccess && hasChanges && (
                  <span className="text-surfox-amber text-xs">Unsaved changes</span>
                )}
              </div>
              <button
                onClick={saveConfig}
                disabled={saving || !canRebuildBundle || !hasChanges}
                className="flex items-center gap-2 px-4 py-2 bg-surfox-primary text-white font-semibold rounded-lg hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save & Rebuild</span>
                  </>
                )}
              </button>
            </div>
          )}
      </div>

      {/* Archiving is fully reversible (Restore lives one click away), so this
          is styled neutral. Red read as "delete" and scared people off. */}
      {activeTab === 'setup' && (
        <div className="flex items-center justify-end gap-3">
          {showArchived ? (
            <>
              <span className="text-xs text-gray-500">
                Puts this campaign back in your active list.
              </span>
              <button
                className="flex items-center gap-1.5 text-xs text-gray-700 border border-surfox-border bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors"
                onClick={() => unarchiveCampaign(campaign.id)}
              >
                <ArchiveRestore className="w-3.5 h-3.5" />
                Restore campaign
              </button>
            </>
          ) : (
            <>
              <span className="text-xs text-gray-500">
                Hides this campaign from your active list. Nothing is deleted, and you can restore it anytime.
              </span>
              <button
                className="flex items-center gap-1.5 text-xs text-gray-700 border border-surfox-border bg-white hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors"
                onClick={() => archiveCampaign(campaign.id)}
              >
                <Archive className="w-3.5 h-3.5" />
                Archive campaign
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
});

// Main Campaign Management Component
export default function CampaignManagement() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Deep-link target from the Get-Live Companion (/campaign-management?campaign=<id>).
  const deepLinkCampaignId = useMemo(
    () => new URLSearchParams(location.search).get('campaign') || undefined,
    [location.search]
  );
  const canEditInstructions = hasPermission(PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS);
  const canRebuildBundle = hasPermission(PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE);
  const [campaigns, setCampaigns] = useState([]);
  const [tenants, setTenants] = useState([]);
  // Map of tenant_id → true when that tenant is at/over their SMS allotment.
  // Drives the "Paused (limit reached)" badge; proactive sending is frozen
  // server-side, so an active campaign for an over-limit tenant is really paused.
  const [smsOverByTenant, setSmsOverByTenant] = useState({});
  // Map of tenant_id → true when that tenant's website chat kill switch has fired
  // (tenants.web_chat_limit_reached_at set). Drives "Paused (chat limit reached)" on
  // the widget campaign specifically — the DB trigger already flipped its ai_on off.
  const [webChatOverByTenant, setWebChatOverByTenant] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [salesTeamsByTenant, setSalesTeamsByTenant] = useState({});
  const [activeTab, setActiveTab] = useState('campaigns');
  const [selectedCampaignForTemplates, setSelectedCampaignForTemplates] = useState('');
  const [campaignTemplates, setCampaignTemplates] = useState({});
  const [previewContent, setPreviewContent] = useState('');
  const [editingTemplates, setEditingTemplates] = useState({});
  const selectedLeadId = 1;
  const [showPreview, setShowPreview] = useState(false);

  const [savingTemplates, setSavingTemplates] = useState(false);
  const [campaignStats, setCampaignStats] = useState({
    totalLeads: 0,
    processedLeads: 0,
    remainingLeads: 0
  });
  // Actual outbound messages sent in the last hour (used for AI Speed card).
  // Measured from messages table, not estimated from BATCH_SIZE × cron frequency.
  const [messagesLastHour, setMessagesLastHour] = useState(null);
  const [tenantNames, setTenantNames] = useState({});
  const [tenantIndustries, setTenantIndustries] = useState({});
  // tenant_id -> true when officeOpenHour/CloseHour/officeDays are all set.
  // Folded into the launch-readiness ring (Decision B): a green Setup must mean
  // the campaign can legally text, which requires configured business hours.
  const [businessHoursByTenant, setBusinessHoursByTenant] = useState({});
  const [knowledgeAssets, setKnowledgeAssets] = useState([]);
  const [selectedKnowledgeAssets, setSelectedKnowledgeAssets] = useState({});
  const [knowledgeDropdownOpen, setKnowledgeDropdownOpen] = useState({});
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  // Launch Console: which campaign's full Settings panel is open in a modal, and on which tab.
  const [settingsCampaignId, setSettingsCampaignId] = useState(null);
  const [settingsTab, setSettingsTab] = useState('setup');
  const [recentlyUnlocked, setRecentlyUnlocked] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  // Inline description editing
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  const handleCopyId = useCallback(async (e, campaignId) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(campaignId);
      setCopiedId(campaignId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy campaign ID:', err);
    }
  }, []);

  // AI Toggle Validation State
  const [validationError, setValidationError] = useState(null);
  const [showBusinessHoursWarning, setShowBusinessHoursWarning] = useState(false);
  const [businessHoursInfo, setBusinessHoursInfo] = useState(null);
  // global_admin activation warning — replaces a native confirm(). Holds the toggle
  // args so Confirm can resume the exact call that was interrupted.
  const [adminActivationConfirm, setAdminActivationConfirm] = useState(null);
  // Archive / restore confirm: { mode: 'archive'|'restore', campaignId, name, isWebChat, busy, error }
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  // Go-live celebration — set to the campaign that just turned AI ON.
  const [celebrateCampaign, setCelebrateCampaign] = useState(null);

  // Progress Modal State
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalCampaign, setProgressModalCampaign] = useState(null);

  // AI toggle toast
  const [aiToast, setAiToast] = useState(null);


  const isGlobalAdmin = user?.role === 'global_admin';
  const showTenantColumn = user?.role === 'global_admin' || user?.role === 'partner_admin';
  const [isFetching, setIsFetching] = useState(false);
  const [customTalkTracks, setCustomTalkTracks] = useState([]);
  const [widgetCampaignId, setWidgetCampaignId] = useState(null);
  const [voiceCampaignId,  setVoiceCampaignId]  = useState(null);
  const [messengerCampaignId, setMessengerCampaignId] = useState(null);
  useEffect(() => {
    document.title = "Campaign Manager – SurFox";
  }, []);

  // Fetch real-time campaign throughput stats.
  // - processed/remaining: from message_queue (jobs the worker has completed vs pending)
  // - messagesLastHour: from messages table, actual outbound in the last 60 minutes
  // Refreshes every 60s so the cards reflect what's actually happening, not hardcoded estimates.
  useEffect(() => {
    if (!user?.tenant_id) return;

    const tenantScope = (isGlobalAdmin && tenantFilter === 'all')
      ? null
      : (isGlobalAdmin ? tenantFilter : user.tenant_id);

    const fetchStats = async () => {
      try {
        // Prospect demo: there is no send queue (messaging is off), so "Leads Analyzed" would read 0 beside a full dashboard.
        // Count the sample leads instead: total, and how many the AI has already texted.
        if (user?.is_prospect_demo === true || user?.is_reviewer === true) {
          const [{ count: total }, { count: texted }] = await Promise.all([
            supabase.from('leads').select('*', { count: 'exact', head: true }).eq('tenant_id', user.tenant_id),
            supabase.from('leads').select('*', { count: 'exact', head: true }).eq('tenant_id', user.tenant_id).eq('ai_sent', true),
          ]);
          setCampaignStats({ totalLeads: total || 0, processedLeads: texted || 0, remainingLeads: Math.max(0, (total || 0) - (texted || 0)) });
          return;
        }

        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

        let completedQ = supabase.from('message_queue').select('*', { count: 'exact', head: true }).eq('status', 'completed');
        let pendingQ = supabase.from('message_queue').select('*', { count: 'exact', head: true }).eq('status', 'pending');
        // Only count messages that ACTUALLY went out to Twilio (sent_at populated).
        // Excludes drafts deferred by business_hours_hold which sit in the table
        // with direction='outbound' but sent_at=null until their scheduled_response_time.
        let recentQ = supabase.from('messages').select('*', { count: 'exact', head: true }).eq('direction', 'outbound').not('sent_at', 'is', null).gte('sent_at', oneHourAgo);

        if (tenantScope) {
          completedQ = completedQ.eq('tenant_id', tenantScope);
          pendingQ = pendingQ.eq('tenant_id', tenantScope);
          recentQ = recentQ.eq('tenant_id', tenantScope);
        }

        const [completedRes, pendingRes, recentRes] = await Promise.all([completedQ, pendingQ, recentQ]);
        const completed = completedRes.count || 0;
        const pending = pendingRes.count || 0;
        const recent = recentRes.count || 0;

        setCampaignStats({
          totalLeads: completed + pending,
          processedLeads: completed,
          remainingLeads: pending,
        });
        setMessagesLastHour(recent);
      } catch (err) {
        console.error('Failed to fetch campaign throughput stats:', err);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [user?.tenant_id, isGlobalAdmin, tenantFilter]);

  const [webChatCompanyName, setWebChatCompanyName] = useState('');
  // The website install code, for the web campaign's "Website" card in the console.
  // Comes from the same GET /web-chat/widget call below; nothing new is fetched for it.
  const [webChatSnippet, setWebChatSnippet] = useState('');

  const fetchWidgetConfigRef = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/web-chat/widget`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setWidgetCampaignId(data.widget_campaign_id || null);
        setWebChatCompanyName(data.web_chat_company_name || '');
        setWebChatSnippet(data.embed_snippet || '');
      }
    } catch (err) {
      console.error('Failed to fetch widget config:', err);
    }
  }, []);

  // Fetch current web chat campaign assignment + company name
  useEffect(() => {
    fetchWidgetConfigRef();
  }, [fetchWidgetConfigRef]);

  // Fetch voice channel campaign assignment (mirror of fetchWidgetConfigRef).
  const fetchVoiceConfigRef = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/voice/number`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setVoiceCampaignId(data.voice_ai_campaign_id || null);
      }
    } catch (err) {
      console.error('Failed to fetch voice config:', err);
    }
  }, []);

  useEffect(() => {
    fetchVoiceConfigRef();
  }, [fetchVoiceConfigRef]);

  // Fetch Messenger channel campaign assignment (mirror of fetchVoiceConfigRef).
  const fetchMessengerConfigRef = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/messenger/page`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessengerCampaignId(data.campaign_id || null);
      }
    } catch (err) {
      console.error('Failed to fetch Messenger config:', err);
    }
  }, []);

  useEffect(() => {
    fetchMessengerConfigRef();
  }, [fetchMessengerConfigRef]);

  const saveWebChatCompanyName = useCallback(async (name) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_BASE}/web-chat/widget/name`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_name: name }),
      });
    } catch (err) {
      console.error('Failed to save web chat company name:', err);
    }
  }, []);

  // Check if all tabs are complete for a given campaign (controls Start AI lock)
  const isCampaignSetupComplete = useCallback((campaign) => {
    const isWeb = campaign.id === widgetCampaignId;
    const isVoice = campaign.id === voiceCampaignId;
    const isMessenger = campaign.id === messengerCampaignId;
    // Setup: AI name (web uses webChatCompanyName, SMS uses ai_representative_name) + phone (SMS only)
    // Voice campaigns get their identity from Voice Channel settings (tenant-level), not the campaign row.
    // Messenger has the same shape as web here (no phone, no tenant-level name field yet).
    const nameOk = (isWeb || isVoice || isMessenger) ? true : !!(campaign.ai_representative_name?.trim());
    if (!nameOk) return false;
    if (!isWeb && !isVoice && !isMessenger && !campaign.phone_number_id) return false;
    if (!isWeb && !isVoice && !isMessenger && !campaign.timezone) return false;
    // Behavior: all four tone/persona fields must be saved
    if (!campaign.initial_tone || !campaign.initial_persona) return false;
    if (!campaign.engagement_tone || !campaign.engagement_persona) return false;
    return true;
  }, [widgetCampaignId, voiceCampaignId, messengerCampaignId, webChatCompanyName]);

  // Called by CampaignDetailPanel after a successful Save & Rebuild
  const onConfigSaved = useCallback((campaignId, updatedFields) => {
    setCampaigns(prev => {
      const updated = prev.map(c => c.id === campaignId ? { ...c, ...updatedFields } : c);
      // Detect unlock transition: was incomplete before, complete after
      const oldCampaign = prev.find(c => c.id === campaignId);
      const newCampaign = updated.find(c => c.id === campaignId);
      if (oldCampaign && newCampaign) {
        const wasComplete = !!(oldCampaign.initial_tone && oldCampaign.initial_persona && oldCampaign.engagement_tone && oldCampaign.engagement_persona);
        const isNowComplete = !!(newCampaign.initial_tone && newCampaign.initial_persona && newCampaign.engagement_tone && newCampaign.engagement_persona);
        if (!wasComplete && isNowComplete) {
          setRecentlyUnlocked(prev => new Set([...prev, campaignId]));
          setTimeout(() => setRecentlyUnlocked(prev => { const s = new Set(prev); s.delete(campaignId); return s; }), 1500);
        }
      }
      return updated;
    });
  }, []);

  // Name of whichever campaign currently owns the website-widget slot. Feeds the
  // explainer modal so "you are about to move it off X" is concrete, not abstract.
  const webChatHolderName = useMemo(
    () => (widgetCampaignId ? (campaigns.find(c => c.id === widgetCampaignId)?.name || null) : null),
    [widgetCampaignId, campaigns]
  );

  const setWebChatCampaign = useCallback(async (campaignId, { confirmed = false } = {}) => {
    const isUnsetting = widgetCampaignId === campaignId;
    const newId = isUnsetting ? null : campaignId;

    // Web Chat is a single tenant-level slot. Whichever campaign LOSES it is paused by
    // the server (web-chat.js, Decision A 2026-09-10), so it cannot start texting as an
    // SMS campaign without passing the SMS go-live checks. Say so before it happens.
    // Moving the slot here from another campaign: skipped when WebChatExplainerModal
    // already covered it (confirmed: true).
    if (!confirmed && !isUnsetting && widgetCampaignId && widgetCampaignId !== campaignId) {
      const currentName = campaigns.find(c => c.id === widgetCampaignId)?.name || 'another campaign';
      const nextName = campaigns.find(c => c.id === campaignId)?.name || 'this campaign';
      const ok = window.confirm(
        `Web Chat is currently assigned to "${currentName}". ` +
        `Moving it to "${nextName}" points your live website widget at "${nextName}".\n\n` +
        `"${currentName}" will be paused. It will not text anyone until you turn it on again ` +
        `and it passes the SMS checks. Continue?`
      );
      if (!ok) return;
    }
    // Switching the web campaign back to SMS: the same pause, so the same warning. This
    // used to be immediate and silent, and left the campaign live as an SMS campaign.
    if (!confirmed && isUnsetting) {
      const name = campaigns.find(c => c.id === campaignId)?.name || 'this campaign';
      const ok = window.confirm(
        `Switch "${name}" back to SMS?\n\n` +
        `"${name}" will be paused, and the chat disappears from your website until another ` +
        `campaign is set to Web Chat and is live. ` +
        `It will not text anyone until you turn it on again and it passes the SMS checks.`
      );
      if (!ok) return;
    }

    setWidgetCampaignId(newId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/web-chat/widget/campaign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ campaign_id: newId }),
      });
      if (!res.ok) throw await channelFlipError(res, 'Failed to update web chat campaign');
      // Reflect the pause the server just made, so the console shows it Paused now
      // instead of Live until the next refetch.
      const body = await res.json().catch(() => ({}));
      const pausedId = body?.paused_campaign?.id;
      if (pausedId) {
        setCampaigns(prev => prev.map(c => (c.id === pausedId ? { ...c, ai_on: false } : c)));
        clearCampaignListCache();
      }
    } catch (err) {
      console.error('Web chat campaign update failed:', err);
      setWidgetCampaignId(widgetCampaignId); // revert
      if (err.userMessage) window.alert(err.userMessage);
    }
  }, [widgetCampaignId, campaigns]);

  // Toggle this campaign as the default for inbound voice calls.
  // Mirrors setWebChatCampaign — single tenant-level slot, PATCH the column.
  const setVoiceCampaign = useCallback(async (campaignId) => {
    const isUnsetting = voiceCampaignId === campaignId;
    const newId = isUnsetting ? null : campaignId;
    setVoiceCampaignId(newId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/voice/settings/campaign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ campaign_id: newId }),
      });
      if (!res.ok) throw await channelFlipError(res, 'Failed to update voice campaign');
      // The campaign that lost the voice slot is paused by the server (2026-10-18); show it Paused now.
      const body = await res.json().catch(() => ({}));
      const pausedId = body?.paused_campaign?.id;
      if (pausedId) {
        setCampaigns(prev => prev.map(c => (c.id === pausedId ? { ...c, ai_on: false } : c)));
        clearCampaignListCache();
      }
    } catch (err) {
      console.error('Voice campaign update failed:', err);
      setVoiceCampaignId(voiceCampaignId); // revert
      if (err.userMessage) window.alert(err.userMessage);
    }
  }, [voiceCampaignId]);

  // Toggle this campaign as the default for inbound Messenger conversations. Mirrors
  // setVoiceCampaign (single tenant-level slot, PATCH the column) — the server pauses
  // whichever campaign loses the slot (src/api_routes/messenger.js PATCH /campaign), same
  // reasoning as web chat's Decision A: an unbound-but-live campaign would otherwise pass
  // straight through to send-batch as an ordinary SMS campaign.
  const setMessengerCampaign = useCallback(async (campaignId) => {
    const isUnsetting = messengerCampaignId === campaignId;
    const newId = isUnsetting ? null : campaignId;
    setMessengerCampaignId(newId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/messenger/campaign`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ campaign_id: newId }),
      });
      if (!res.ok) throw await channelFlipError(res, 'Failed to update Messenger campaign');
      const body = await res.json().catch(() => ({}));
      const pausedId = body?.paused_campaign?.id;
      if (pausedId) {
        setCampaigns(prev => prev.map(c => (c.id === pausedId ? { ...c, ai_on: false } : c)));
        clearCampaignListCache();
      }
    } catch (err) {
      console.error('Messenger campaign update failed:', err);
      setMessengerCampaignId(messengerCampaignId); // revert
      if (err.userMessage) window.alert(err.userMessage);
    }
  }, [messengerCampaignId]);

  // Fetch custom talk tracks for this tenant
  useEffect(() => {
    if (!user?.tenant_id) return;
    supabase
      .from('custom_talk_tracks')
      .select('id, name, audience_type, campaign_goal')
      .eq('tenant_id', user.tenant_id)
      .order('name')
      .then(({ data }) => { if (data) setCustomTalkTracks(data); });
  }, [user?.tenant_id]);


  // Get dynamic column header
const getDynamicColumnHeader = useCallback((industry) => {
  switch (industry) {
    case 'Staffing':
    case 'Real Estate':
      return 'Talk Track';
    case 'Home Services':
    case 'Financial Services':
    case 'Mortgage Lending':
      return 'Service Type';
    case 'Auto Sales':
      return 'Vehicle Type';
    default:
      return null;
  }
}, []);

  // Get dynamic dropdown options
// Built-in parent/child options per industry. childOptions seeds the SpecialtySelector dropdown
// with hardcoded specialties (Staffing only); other industries start empty and rely on the
// LLM-generated specialties stored in generated_specialty_contexts.
const getDynamicDropdownOptions = useCallback((industry) => {
  const baseOptions = (() => {
    switch (industry) {
      case 'Staffing':
        return {
          parentOptions: [
            { value: 'recruiting_candidates', label: 'Recruiting Candidates (B2C)' },
            { value: 'recruiting_candidates_multi_role', label: 'Recruiting Candidates - Multi Role (KB-driven)' },
            { value: 'acquiring_clients', label: 'Acquiring Clients (B2B)' },
            { value: 'credentialing_services', label: 'Credentialing Services (B2B)' }
          ],
          childOptions: [
            { value: 'physician', label: 'Physician / Doctor' },
            { value: 'obgyn', label: 'OB/GYN' },
            { value: 'rn', label: 'Registered Nurse (RN)' },
            { value: 'aprn', label: 'APRN / Nurse Practitioner' },
            { value: 'lpn', label: 'LPN / LVN' },
            { value: 'cna', label: 'CNA' },
            { value: 'medical_assistant', label: 'Medical Assistant' },
            { value: 'therapist', label: 'Therapist (PT/OT/SLP)' },
            { value: 'rad_tech', label: 'Radiology Tech' },
            { value: 'lcsw', label: 'Clinical Social Worker (LCSW)' },
            { value: 'psych', label: 'Psych / Behavioral Health' },
            { value: 'healthcare_other', label: 'Other Healthcare Role' }
          ]
        };
      case 'Real Estate':
        return {
          parentOptions: [
            { value: 'seller_leads', label: 'Seller Leads' },
            { value: 'buyer_leads', label: 'Buyer Leads' },
            { value: 'traditional_sales', label: 'Traditional Sales' },
            { value: 'investment_wholesale', label: 'Investment/Wholesale Buying' },
            { value: 'expired_listings', label: 'Expired Listings' },
            { value: 'fsbo_leads', label: 'FSBO (For Sale By Owner)' },
            { value: 'agent_fixer_upper', label: 'Agent Fixer-Upper Outreach' }
          ],
          // Investor specialties have hardcoded prompts in instructionBuilder under
          // talk_track_type === 'investment_wholesale'. They show under all parents
          // (matching Staffing's pattern) but only carry tuned prompts under the
          // investor parent — other parents fall back to their generic prompt.
          childOptions: [
            { value: 'probate', label: 'Probate / Inherited' },
            { value: 'pre_foreclosure', label: 'Pre-Foreclosure' },
            { value: 'vacant_land', label: 'Vacant Land' },
            { value: 'absentee_owner', label: 'Absentee Owner' },
            { value: 'tired_landlord', label: 'Tired Landlord' },
            { value: 'tax_delinquent', label: 'Tax Delinquent' },
            { value: 'divorce', label: 'Divorce Sellers' },
            { value: 'vacant_house', label: 'Vacant / Distressed House' }
          ]
        };
      case 'Home Services':
        return {
          parentOptions: [
            { value: 'hvac', label: 'HVAC' },
            { value: 'roofing', label: 'Roofing' },
            { value: 'solar', label: 'Solar' },
            { value: 'lawn_care', label: 'Lawn Care' },
            { value: 'plumbing', label: 'Plumbing' },
            { value: 'other', label: 'Other' }
          ],
          childOptions: []
        };
      case 'Financial Services':
        return {
          parentOptions: [
            { value: 'mortgage_lending', label: 'Mortgage Lending' },
            { value: 'insurance', label: 'Insurance' },
            { value: 'credit_repair', label: 'Credit Repair' },
            { value: 'tax_relief', label: 'Tax Relief' }
          ],
          childOptions: []
        };
      case 'Auto Sales':
        return {
          parentOptions: [
            { value: 'new_cars', label: 'New Cars' },
            { value: 'used_cars', label: 'Used Cars' },
            { value: 'lease_offers', label: 'Lease Offers' },
            { value: 'trade_in_leads', label: 'Trade-In Leads' }
          ],
          childOptions: []
        };
      case 'Mortgage Lending':
        return {
          parentOptions: [
            { value: 'purchase', label: 'Purchase' },
            { value: 'refinance', label: 'Refinance' },
            { value: 'heloc', label: 'HELOC' },
            { value: 'pre_approval', label: 'Pre-Approval' }
          ],
          childOptions: []
        };
      default:
        return { parentOptions: [], childOptions: [] };
    }
  })();

  // Event Follow-up — industry-agnostic, available to every tenant.
  // Appended to every industry's parentOptions so any business running a
  // webinar / trade show / conference / live demo can run a post-event
  // follow-up campaign. Specialty options (attended / no_show / replay_watched)
  // are resolved in DynamicFieldComponent rather than per-industry childOptions.
  const withEventFollowup = {
    ...baseOptions,
    parentOptions: [
      ...baseOptions.parentOptions,
      { value: 'event_followup', label: 'Event Follow-up' },
    ],
  };

  // Internal: SurFox AI demo-lead qualification — global_admin only.
  // Appears in every industry's dropdown so global admins can attach this
  // track to ANY tenant (typically the SurFox internal tenant for inbound
  // voice demos handled by Tom).
  if (isGlobalAdmin) {
    return {
      ...withEventFollowup,
      parentOptions: [
        ...withEventFollowup.parentOptions,
        { value: 'surfox_ai_qualification', label: '⚡ SurFox AI — Qualify Demo Leads (Internal)' },
      ],
    };
  }
  return withEventFollowup;
}, [isGlobalAdmin]);

  // Get status badge
  const getStatusBadge = useCallback((campaign) => {
    const isActive = campaign.is_active;
    const isArchived = campaign.archived;
    const aiOn = campaign.ai_on;
    
    if (isArchived) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-surfox-border shadow-sm">
          <Archive className="w-3 h-3 flex-shrink-0" />
          Archived
        </span>
      );
    }

    // Web chat's own kill switch — checked before the SMS one so the widget campaign
    // reports its real cause rather than the generic SMS message. The DB trigger already
    // flipped ai_on off, so this is a hard stop, not "replies still flow" like SMS.
    if (campaign.id === widgetCampaignId && webChatOverByTenant[campaign.tenant_id]) {
      return (
        <span
          title="Monthly website chat limit reached. Chat is off until your usage resets next billing cycle, or immediately if you upgrade."
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300 shadow-sm"
        >
          <Clock className="w-3 h-3 flex-shrink-0" />
          Paused (chat limit reached)
        </span>
      );
    }

    // Tenant at/over SMS allotment → proactive sending is frozen server-side.
    // An "active" campaign is really paused until the cycle resets (replies still flow).
    if (isActive && smsOverByTenant[campaign.tenant_id]) {
      return (
        <span
          title="Monthly message limit reached. New outreach and follow-ups are paused until usage resets. Replies to existing leads continue."
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300 shadow-sm"
        >
          <Clock className="w-3 h-3 flex-shrink-0" />
          Paused (limit reached)
        </span>
      );
    }

    if (aiOn && isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300 shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
          <Zap className="w-3 h-3 flex-shrink-0" />
          AI Active
        </span>
      );
    } else if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300 shadow-sm">
          <CheckCircle className="w-3 h-3 flex-shrink-0" />
          Ready
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200 shadow-sm">
          <Clock className="w-3 h-3 flex-shrink-0" />
          Inactive
        </span>
      );
    }
  }, [smsOverByTenant, webChatOverByTenant, widgetCampaignId]);

  // Get hot lead assignment info
  const getHotLeadAssignment = useCallback((campaign) => {
  if (campaign.hot_lead_distribution_mode === 'single' && campaign.hot_lead_single_assignee_id) {
    const tenantMembers = salesTeamsByTenant[campaign.tenant_id] || [];
    const assignee = tenantMembers.find(m => m.sales_team_id === campaign.hot_lead_single_assignee_id);
      return {
        mode: 'single',
        assignee: assignee?.full_name || assignee?.email || 'Unknown'
      };
    } else if (campaign.hot_lead_distribution_mode === 'round_robin' && campaign.hot_lead_round_robin_team?.length > 0) {
      return {
        mode: 'round_robin',
        assignee: `${campaign.hot_lead_round_robin_team.length} team members`
      };
    }
    return { mode: null, assignee: null };
  }, [salesTeamsByTenant]);

  // Memoized filtered campaigns
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(campaign => {
      const matchesSearch =
        campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.tenants?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus = campaign.is_active === true;
      } else if (statusFilter === 'inactive') {
        matchesStatus = campaign.is_active === false;
      } else if (statusFilter === 'ai_active') {
        matchesStatus = campaign.ai_on === true;
      }

      return matchesSearch && matchesStatus;
    });
  }, [campaigns, searchTerm, statusFilter]);

  // ---- Launch Console adapters ---------------------------------------------
  // Tag each campaign with the channel the console renders (sms | chat | voice).
  // Web-chat and voice are identified by the tenant-level widget/voice campaign ids.
  const consoleCampaigns = useMemo(() => filteredCampaigns.map(c => ({
    ...c,
    channel: c.id === widgetCampaignId ? 'chat' : c.id === voiceCampaignId ? 'voice' : c.id === messengerCampaignId ? 'messenger' : 'sms',
  })), [filteredCampaigns, widgetCampaignId, voiceCampaignId, messengerCampaignId]);

  // Sandbox "Test conversation with SurFox" modal — holds the campaign being tested.
  const [testCampaign, setTestCampaign] = useState(null);
  // The is_demo rollout gate was dropped 2026-07-15 (here and in simulate-conversation).
  // The sandbox is no longer a preview: the pre-launch conversation gate REQUIRES every
  // tenant to test before launching, so hiding the button would strand them.
  const sandboxEnabled = true;

  // Pre-launch conversation gate status, per campaign. Cached so selecting between
  // campaigns doesn't re-hit the endpoint (whose first read also runs the smart-skip
  // model call). Invalidated whenever a campaign's gate progresses.
  const [gateStatuses, setGateStatuses] = useState({});
  const fetchGateStatus = useCallback(async (campaign) => {
    if (!campaign?.id) return null;
    if (gateStatuses[campaign.id]) return gateStatuses[campaign.id];
    try {
      const res = await fetchWithCsrf(`${API_BASE}/campaign-test-gate/${campaign.id}`);
      if (!res.ok) return null;
      const status = await res.json();
      setGateStatuses((prev) => ({ ...prev, [campaign.id]: status }));
      return status;
    } catch {
      return null;
    }
  }, [gateStatuses]);
  const invalidateGateStatus = useCallback((campaignId) => {
    setGateStatuses((prev) => {
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });
  }, []);

  // Explainer shown whenever activation is attempted before the gate clears. The
  // locked button stays locked; this is the reason it's locked, never a bypass.
  const [gateBlockedCampaign, setGateBlockedCampaign] = useState(null);
  const explainGate = useCallback(async (campaign) => {
    setGateBlockedCampaign(campaign);
    // Pull fresh status so the modal's progress reflects reality even if the
    // cached copy is stale (e.g. they banked a conversation in another tab).
    const status = await fetchGateStatus(campaign);
    if (status) setGateStatuses((prev) => ({ ...prev, [campaign.id]: status }));
  }, [fetchGateStatus]);

  // Retroactive campaign-level DNC scrub — holds the campaign whose scrub modal is open.
  const [scrubCampaign, setScrubCampaign] = useState(null);
  // DNC eligibility resolved server-side: platform master key (Internal Tools) +
  // this tenant's include_dnc + FTC SAN + ORG ID must all be present.
  const [dncEligible, setDncEligible] = useState(false);
  useEffect(() => {
    if (!user?.tenant_id) { setDncEligible(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithCsrf(`${API_BASE}/dnc/eligibility`, { method: 'GET' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setDncEligible(res.ok && !!data.eligible);
      } catch (e) {
        if (!cancelled) setDncEligible(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  // Load business-hours config for every tenant in the campaign list. Read-only;
  // feeds the launch-readiness ring (Decision B — green Setup ⇒ can legally text).
  useEffect(() => {
    const tenantIds = [...new Set(campaigns.map(c => c.tenant_id).filter(Boolean))];
    if (tenantIds.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('platform_settings')
          .select('tenant_id, key, value')
          .in('tenant_id', tenantIds)
          .in('key', ['officeOpenHour', 'officeCloseHour', 'officeDays']);
        if (error || cancelled) return;
        const byTenant = {};
        (data || []).forEach(({ tenant_id, key, value }) => {
          const v = (value && typeof value === 'object' && 'value' in value) ? value.value : value;
          (byTenant[tenant_id] ||= {})[key] = v;
        });
        const ready = {};
        tenantIds.forEach(id => {
          const s = byTenant[id] || {};
          ready[id] = !!(s.officeOpenHour && s.officeCloseHour && s.officeDays);
        });
        if (!cancelled) setBusinessHoursByTenant(ready);
      } catch (err) {
        console.warn('Business-hours fetch failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [campaigns]);

  // Per-step completion for the console's 5-step readiness model. The five steps
  // ARE the five Campaign Settings tabs, so this mirrors the panel's `tabStatus`
  // exactly (web/voice exemptions, talk-track gating, knowledge-asset check).
  // Per-step completion for the console's 5-step readiness model — delegates to
  // the shared deriveCampaignSetup (also used by the Get-Live Companion) so the
  // ring, the panel, and the companion all agree. Folds in the two checks the
  // AI-ON gate enforces (ai_instruction_initial + business hours) per Decision B.
  const consoleGetSetup = useCallback((c) => deriveCampaignSetup(c, {
    widgetCampaignId, voiceCampaignId, messengerCampaignId, isGlobalAdmin,
    industries: tenantIndustries, knowledgeAssets, selectedKnowledgeAssets,
    businessHoursByTenant, isReviewer: user?.is_reviewer === true,
  }), [widgetCampaignId, voiceCampaignId, messengerCampaignId, tenantIndustries, isGlobalAdmin, knowledgeAssets, selectedKnowledgeAssets, businessHoursByTenant, user?.is_reviewer]);

  const settingsCampaign = useMemo(
    () => campaigns.find(c => c.id === settingsCampaignId) || null,
    [campaigns, settingsCampaignId]
  );

  // Open the full settings panel on a specific tab. Used by each console step.
  const openCampaignSettings = useCallback((campaignId, tab = 'setup') => {
    setSettingsTab(tab);
    setSettingsCampaignId(campaignId);
  }, []);

  // Real "Audience & schedule" figures for the selected campaign in the console.
  // Lead count from `leads`, sending window + send rate from the tenant's
  // platform_settings (business hours + hourly throttle). ETA = remaining / rate.
  const fetchCampaignAudience = useCallback(async (campaign) => {
    if (!campaign?.id) return null;
    // Web chat campaign: none of the SMS figures below apply (it sends nothing, has no
    // uploaded list and no sending window), so they are not fetched. Instead three
    // counts for its Website card. Each is a head:true count that Postgres answers from
    // the partial index idx_leads_session_id (session_id IS NOT NULL), measured
    // 2026-09-10 with EXPLAIN ANALYZE at 0.4-5.5 ms over the 43 web leads on the
    // platform. "Chats started" is deliberately NOT here: it needs messages, which has no
    // tenant+channel index, and a browser tab must not add heavy queries to this DB.
    if (campaign.channel === 'chat') {
      try {
        const webLeads = () => supabase.from('leads').select('id', { count: 'exact', head: true })
          .eq('campaign_id', campaign.id).not('session_id', 'is', null);
        const [totalRes, visitorsRes, contactsRes, bookingsRes] = await Promise.all([
          supabase.from('leads').select('*', { count: 'exact', head: true }).eq('campaign_id', campaign.id),
          webLeads(),
          webLeads().or('email.not.is.null,phone.not.is.null'),
          webLeads().eq('booking_completed', true),
        ]);
        // A failed count reads as "not known" (shown as a dash), never as zero.
        const countOf = (r, label) => {
          if (r?.error) { console.warn(`Web chat ${label} count failed:`, r.error.message); return null; }
          return r?.count ?? null;
        };
        return {
          leads: countOf(totalRes, 'lead') ?? 0,
          web: {
            visitors: countOf(visitorsRes, 'visitor'),
            contacts: countOf(contactsRes, 'contact'),
            bookings: countOf(bookingsRes, 'booking'),
          },
        };
      } catch (err) {
        console.warn('Web chat audience fetch failed:', err?.message);
        return null;
      }
    }
    try {
      const [totalRes, processedRes, settingsRes, uploadsRes] = await Promise.all([
        supabase.from('leads').select('*', { count: 'exact', head: true }).eq('campaign_id', campaign.id),
        supabase.from('leads').select('*', { count: 'exact', head: true }).eq('campaign_id', campaign.id).eq('ai_sent', true),
        supabase.from('platform_settings').select('key, value').eq('tenant_id', campaign.tenant_id)
          .in('key', ['officeOpenHour', 'officeCloseHour']),
        // The customer's own uploaded list(s). Bulk import is the ONLY intake path
        // that keeps the original file (AddLead.jsx puts it in the `bulk-uploads`
        // bucket and records the URL on the job row); single-entry adds, Surf-built
        // campaigns, Zapier, FUB and web chat leave nothing to download. RLS on
        // bulk_upload_jobs already scopes to the caller's tenant.
        supabase.from('bulk_upload_jobs')
          .select('id, file_name, file_url, file_size, created_at, status, total_records, successful_records, duplicate_records, failed_records')
          .eq('campaign_id', campaign.id)
          .order('created_at', { ascending: false })
          .limit(20),
      ]);
      if (uploadsRes?.error) console.warn('Source list fetch failed:', uploadsRes.error.message);
      const sourceFiles = uploadsRes?.error ? [] : (uploadsRes?.data || []);
      const raw = (v) => (v && typeof v === 'object' && 'value' in v) ? v.value : v;
      const s = {};
      (settingsRes?.data || []).forEach(r => { s[r.key] = raw(r.value); });

      const fmtHour = (h) => {
        const n = Number(h);
        if (!Number.isFinite(n)) return null;
        const ampm = n >= 12 ? 'PM' : 'AM';
        const h12 = ((n + 11) % 12) + 1;
        return `${h12} ${ampm}`;
      };
      const openH = fmtHour(s.officeOpenHour), closeH = fmtHour(s.officeCloseHour);
      const sendingWindow = (openH && closeH) ? `${openH} – ${closeH}` : '—';

      // Send rate is governed by the process-message-queue worker, NOT a per-tenant
      // setting. (The old `ai_hourly_throttle_limit` is unused by any edge function.)
      // The worker claims one campaign + up to BATCH_SIZE=10 jobs per run, fires
      // every 5 min (*/5 cron), 10 parallel workers. Theoretical floor for one
      // campaign is 10 × 12 = ~120/hr, but in production a single backlogged
      // campaign runs higher (observed ~120-160/hr, ~150 typical): the per-campaign
      // lock releases on batch completion (seconds, not the full 5-min visibility
      // window), so it gets re-claimed faster than once per tick. Variable by
      // contention. This is an approximate capacity used for the ETA.
      // Source: supabase/functions/process-message-queue/index.ts (BATCH_SIZE)
      //         + 20260502_schedule_process_message_queue_worker*.sql (*/5 cron ×10).
      const SEND_RATE_PER_HOUR = 150; // observed prod typical; tune if the worker changes
      const hourly = SEND_RATE_PER_HOUR;
      const sendRate = `~${hourly} / hr`;

      const total = totalRes?.count || 0;
      const processed = processedRes?.count || 0;
      const remaining = Math.max(0, total - processed);
      let eta = '—';
      if (total === 0) eta = '—';
      else if (remaining === 0) eta = 'Complete';
      else if (Number.isFinite(Number(s.officeOpenHour)) && Number.isFinite(Number(s.officeCloseHour))) {
        const hrsPerDay = Math.max(1, Number(s.officeCloseHour) - Number(s.officeOpenHour));
        const perDay = hourly * hrsPerDay;
        const days = Math.ceil(remaining / perDay);
        eta = days <= 1 ? '~1 day' : `~${days} days`;
      }

      // Send progress — how many leads have actually been texted, and the
      // delivery breakdown. Same source the old CampaignProgress card used.
      let delivered = 0, failed = 0, retries = 0, queued = 0;
      if (total > 0) {
        const { data: messageStats, error: statsError } = await supabase
          .rpc('get_campaign_message_stats', { campaign_ids: [campaign.id] });
        if (!statsError && messageStats && messageStats.length > 0) {
          const stats = messageStats[0];
          delivered = Number(stats.delivered_count) || 0;
          failed = Number(stats.failed_count) || 0;
          retries = Number(stats.retry_count) || 0;
          queued = Number(stats.queued_count) || 0;
        }
      }

      return { leads: total, sendingWindow, sendRate, eta, processed, delivered, failed, retries, queued, sourceFiles };
    } catch (err) {
      console.warn('Audience fetch failed:', err?.message);
      return null;
    }
  }, []);

  const fetchTenants = useCallback(async () => {
  if (!showTenantColumn || !user?.tenant_id) return;
  
  try {
    let query = supabase
      .from('tenants')
      .select('id, name')
      .or(VISIBLE_TENANTS_OR) // hide inactive/suspended from global_admin dropdown
      .order('name');

    // Partner admin only sees their child tenants
    if (user?.role === 'partner_admin') {
      query = query.eq('parent_id', user.tenant_id);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    if (data) {
      setTenants(data);
      // Populate tenantNames lookup
      const names = {};
      data.forEach(t => { names[t.id] = t.name; });
      setTenantNames(names);
    }
  } catch (err) {
    console.warn('Could not fetch tenants:', err.message);
  }
}, [showTenantColumn, user?.role, user?.tenant_id]);

  // Data fetching functions with caching and error handling
  const fetchCampaigns = useCallback(async () => {
    if (isFetching || !user?.tenant_id) return;
    
    const cacheKey = `campaigns-${user.tenant_id}-${tenantFilter}-${showArchived}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setCampaigns(cached);
      setLoading(false);
      return;
    }
    
    try {
      setIsFetching(true);
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      let url = `${API_BASE}/campaigns`;
      const params = new URLSearchParams();
      
      if (isGlobalAdmin && tenantFilter !== 'all') {
        params.append('tenant_id', tenantFilter);
      }
      
      if (showArchived) {
        params.append('show_archived', 'true');
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch campaigns: ${response.status}`);
      }

      const data = await response.json();
      
      // Enhance campaigns with additional details
      const campaignIds = data.map(c => c.id);
      if (campaignIds.length > 0) {
        const { data: campaignDetails, error: detailError } = await supabase
          .from('campaigns')
          .select(`
            id,
            assigned_to_sales_team_id,
            phone_number_id,
            talk_track_type,
            talk_track_specialty,
            custom_talk_track_id,
            hot_lead_distribution_mode,
            hot_lead_single_assignee_id,
            hot_lead_round_robin_team,
            ai_representative_name,
            forward_call_number,
            notification_email,
            notification_cc_email,
            visitor_support_email,
            initial_tone,
            initial_persona,
            engagement_tone,
            engagement_persona,
            ai_instruction_initial,
            allows_bulk_upload,
            warm_transfer_mode,
            phone_numbers (
              id,
              phone_number
            )
          `)
          .in('id', campaignIds);
        
        if (!detailError && campaignDetails) {
          const { data: poolRows } = await supabase
            .from('campaign_phone_numbers')
            .select('campaign_id, phone_number_id')
            .in('campaign_id', campaignIds);

          const poolByCampaign = {};
          (poolRows || []).forEach(r => {
            (poolByCampaign[r.campaign_id] ||= []).push(r.phone_number_id);
          });

          const enhancedCampaigns = data.map(campaign => {
            const details = campaignDetails.find(d => d.id === campaign.id);
            const primaryId = details?.phone_number_id ?? null;
            const allIds = poolByCampaign[campaign.id] || [];
            const additional_phone_ids = allIds.filter(id => id !== primaryId);
            return {
              ...campaign,
              ...details,
              additional_phone_ids
            };
          });

          setCampaigns(enhancedCampaigns);
          setCachedData(cacheKey, enhancedCampaigns);
        } else {
          setCampaigns(data);
          setCachedData(cacheKey, data);
        }
      } else {
        setCampaigns(data);
        setCachedData(cacheKey, data);
      }
      setError('');
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(`Failed to load campaigns: ${err.message}`);
      setCampaigns([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [user?.tenant_id, isGlobalAdmin, tenantFilter, showArchived, isFetching]);

// Resolve which tenants (across the loaded campaigns) are at/over their SMS
// allotment, so the badge can show "Paused (limit reached)". Global admins read
// the all-tenants overview in one call; others read their own usage.
useEffect(() => {
  let cancelled = false;
  (async () => {
    try {
      const tenantIds = [...new Set(campaigns.map(c => c.tenant_id).filter(Boolean))];
      if (tenantIds.length === 0) { setSmsOverByTenant({}); setWebChatOverByTenant({}); return; }
      const map = {};
      const chatMap = {};
      if (isGlobalAdmin) {
        const res = await fetchWithCsrf(`${API_BASE}/billing/tenants-overview`);
        if (res.ok) {
          const { tenants: rows } = await res.json();
          (rows || []).forEach(t => {
            map[t.tenant_id] = t.total_limit != null && t.enforced_count >= t.total_limit;
            chatMap[t.tenant_id] = !!t.web_chat_limit_reached_at;
          });
        }
      } else {
        const res = await fetchWithCsrf(`${API_BASE}/billing/usage`);
        if (res.ok) {
          const u = await res.json();
          const total = u.sms_limit != null ? u.sms_limit + (u.topup_messages_current_cycle || 0) : null;
          if (user?.tenant_id) {
            map[user.tenant_id] = total != null && (u.sms_count || 0) >= total;
            chatMap[user.tenant_id] = !!u.web_chat_limit_reached_at;
          }
        }
      }
      if (!cancelled) { setSmsOverByTenant(map); setWebChatOverByTenant(chatMap); }
    } catch {
      /* non-fatal: badge just won't show paused state */
    }
  })();
  return () => { cancelled = true; };
}, [campaigns, isGlobalAdmin, user?.tenant_id]);

const updateCampaignAIRepresentativeName = useCallback(async (campaignId, name) => {
  // Optimistic update
  setCampaigns(prev => prev.map(c => 
    c.id === campaignId ? { ...c, ai_representative_name: name } : c
  ));

  try {
    const { error } = await supabase
      .from('campaigns')
      .update({ ai_representative_name: name || null })
      .eq('id', campaignId);

    if (error) throw error;
    setError('');
  } catch (err) {
    console.error('Error updating AI representative name:', err);
    setError('Failed to update AI representative name');
    fetchCampaigns(); // Revert on error
  }
}, [fetchCampaigns]);

const updateCampaignDescription = useCallback(async (campaignId, description) => {
  const trimmed = description.trim();
  // Optimistic update
  setCampaigns(prev => prev.map(c =>
    c.id === campaignId ? { ...c, description: trimmed || null } : c
  ));

  try {
    const { error } = await supabase
      .from('campaigns')
      .update({ description: trimmed || null })
      .eq('id', campaignId);

    if (error) throw error;
    setError('');
  } catch (err) {
    console.error('Error updating campaign description:', err);
    setError('Failed to update campaign description');
    fetchCampaigns(); // Revert on error
  }
}, [fetchCampaigns]);

  // Other data fetching functions with caching
  const fetchSalesTeamMembers = useCallback(async () => {
  if (!user?.tenant_id) return;
  
  const cacheKey = isGlobalAdmin ? 'salesTeams-all' : `salesTeams-${user.tenant_id}`;
  const cached = getCachedData(cacheKey);
  if (cached) {
    setSalesTeamsByTenant(cached);
    return;
  }
  
  try {
    let query = supabase
      .from('sales_team_view')
      .select('*')
      .eq('is_available', true)
      .order('full_name');
    
    if (!isGlobalAdmin) {
      query = query.eq('tenant_id', user.tenant_id);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    // Group by tenant_id
    const byTenant = {};
    data?.forEach(member => {
      if (!byTenant[member.tenant_id]) {
        byTenant[member.tenant_id] = [];
      }
      byTenant[member.tenant_id].push(member);
    });
    
    setSalesTeamsByTenant(byTenant);
    setCachedData(cacheKey, byTenant);
  } catch (err) {
    console.error('Error fetching sales team:', err);
    setSalesTeamsByTenant({});
  }
}, [user?.tenant_id, isGlobalAdmin]);

const fetchTenantIndustries = useCallback(async () => {
  if (!user?.tenant_id) return;
  
  const cacheKey = isGlobalAdmin ? 'tenantIndustries-all' : `tenantIndustries-${user.tenant_id}`;
  const cached = getCachedData(cacheKey);
  if (cached) {
    setTenantIndustries(cached);
    return;
  }
  
  try {
    let query = supabase
      .from('platform_settings')
      .select('tenant_id, value')
      .eq('key', 'industry');
    
    // Non-global admins only get their own
    if (!isGlobalAdmin) {
      query = query.eq('tenant_id', user.tenant_id);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    const industries = {};
    data?.forEach(row => {
      industries[row.tenant_id] = row.value;
    });
    setTenantIndustries(industries);
    setCachedData(cacheKey, industries);
  } catch (err) {
    console.error('Error fetching tenant industries:', err);
    setTenantIndustries({});
  }
}, [user?.tenant_id, isGlobalAdmin]);

  const fetchKnowledgeAssets = useCallback(async () => {
  if (!user?.tenant_id) return;
  
  const cacheKey = isGlobalAdmin ? 'knowledgeAssets-all' : `knowledgeAssets-${user.tenant_id}`;
  const cached = getCachedData(cacheKey);
  if (cached) {
    setKnowledgeAssets(cached);
    return;
  }
  
  try {
    let query = supabase
      .from('knowledge_base')
      .select('id, title, source_type, file_url, website_url, file_name, created_at, tenant_id, is_master')
      .order('created_at', { ascending: false });
    
    // Only filter by tenant for non-global admins
    if (!isGlobalAdmin) {
      query = query.eq('tenant_id', user.tenant_id);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    setKnowledgeAssets(data || []);
    setCachedData(cacheKey, data || []);
  } catch (err) {
    console.error('Error fetching knowledge assets:', err);
  }
}, [user?.tenant_id, isGlobalAdmin]);

  const fetchPhoneNumbers = useCallback(async () => {
  if (!user?.tenant_id) return;
  
  const cacheKey = `phoneNumbers-${user.tenant_id}`;
  const cached = getCachedData(cacheKey);
  if (cached) {
    setPhoneNumbers(cached);
    return;
  }
  
  try {
    const { data, error } = await supabase
      .from('phone_numbers')
      .select('id, phone_number, retell_imported_at')
      .eq('tenant_id', user.tenant_id)
      .eq('status', 'active')
      .order('phone_number');
    
    if (error) throw error;
    
    setPhoneNumbers(data || []);
    setCachedData(cacheKey, data || []);
  } catch (err) {
    console.error('Error fetching phone numbers:', err);
    setPhoneNumbers([]);
  }
}, [user?.tenant_id]);

  // Update functions with optimistic updates
  const updateCampaignAssignment = useCallback(async (campaignId, salesTeamId) => {
  // Optimistic update
  const campaign = campaigns.find(c => c.id === campaignId);
  const tenantMembers = salesTeamsByTenant[campaign?.tenant_id] || [];
  const salesPerson = tenantMembers.find(s => s.sales_team_id === salesTeamId);
    setCampaigns(prev => prev.map(c => {
      if (c.id === campaignId) {
        return {
          ...c,
          assigned_to_sales_team_id: salesTeamId,
          assigned_to_name: salesPerson?.full_name,
          assigned_to_email: salesPerson?.email
        };
      }
      return c;
    }));

    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ assigned_to_sales_team_id: salesTeamId })
        .eq('id', campaignId);

      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating campaign assignment:', err);
      setError('Failed to update campaign assignment');
      fetchCampaigns(); // Revert on error
    }
  }, [campaigns, salesTeamsByTenant, fetchCampaigns]);

  const updateCampaignForwardNumber = useCallback(async (campaignId, number) => {
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, forward_call_number: number } : c
    ));
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ forward_call_number: number || null })
        .eq('id', campaignId);
      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating forward call number:', err);
      setError('Failed to update forward call number');
      fetchCampaigns();
    }
  }, [fetchCampaigns]);

  const updateCampaignNotificationEmail = useCallback(async (campaignId, email) => {
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, notification_email: email } : c
    ));
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ notification_email: email || null })
        .eq('id', campaignId);
      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating campaign notification email:', err);
      setError('Failed to update notification email');
      fetchCampaigns();
    }
  }, [fetchCampaigns]);

  const updateCampaignNotificationCcEmail = useCallback(async (campaignId, email) => {
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, notification_cc_email: email } : c
    ));
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ notification_cc_email: email || null })
        .eq('id', campaignId);
      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating campaign notification CC email:', err);
      setError('Failed to update notification CC email');
      fetchCampaigns();
    }
  }, [fetchCampaigns]);

  // The address a WEBSITE VISITOR is given, NOT an internal alert recipient. Same save
  // path as the two above it (optimistic set, direct campaigns update, refetch on failure).
  const updateCampaignVisitorSupportEmail = useCallback(async (campaignId, email) => {
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, visitor_support_email: email } : c
    ));
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ visitor_support_email: email || null })
        .eq('id', campaignId);
      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating campaign visitor support email:', err);
      setError('Failed to update visitor support email');
      fetchCampaigns();
    }
  }, [fetchCampaigns]);

  // Voice AI v1 architecture revision (2026-09-13): replaces the retired
  // updateCampaignVoiceReengagement. This is the one explicit, tenant/Tom-triggered
  // action that makes voice actually possible for a campaign — importing its own
  // phone number(s) into Retell — hitting the new Express route rather than a direct
  // Supabase write, since it also drives real Twilio trunk + Retell REST calls
  // server-side. Same auth pattern VoiceChannelSection.jsx already established
  // (makeAuthenticatedRequest: Supabase session bearer token against the Express API).
  const importCampaignNumberToRetell = useCallback(async (campaignId) => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error(sessionError.message);
      if (!session?.access_token) throw new Error('No valid session. Please log in again.');

      const apiBase = process.env.REACT_APP_API_URL
        || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api.getsurfox.com/api');

      const response = await fetch(`${apiBase}/voice/campaign-number/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId }),
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error || `${response.status} ${response.statusText}`);
      }

      const results = body.results || {};
      const failed = Object.values(results).filter((r) => !r.ok);
      if (failed.length > 0) {
        setError(`Voice calling: ${Object.keys(results).length - failed.length}/${Object.keys(results).length} number(s) imported; ${failed.length} failed — ${failed.map((r) => r.error || r.skippedReason).join('; ')}`);
      } else {
        setError('');
      }
    } catch (err) {
      console.error('Error importing campaign number(s) to Retell:', err);
      setError(`Failed to enable voice calling: ${err.message}`);
    }
  }, []);

  // Voice AI v1 architecture revision, follow-up (2026-09-13): the correct
  // auto-import trigger is ASSIGNMENT, not purchase (see
  // _shared/retellCampaignNumbers.ts's header) — this hits the SAME route as
  // importCampaignNumberToRetell above, fired silently in the background right
  // after a campaign's phone-number assignment actually changes below. Never
  // surfaces setError: the route's own allows_bulk_upload + voice-provisioning
  // gates make this a safe no-op for the overwhelming majority of tenants/
  // campaigns, and surfacing a "voice calling" error after a plain phone-number
  // save would be confusing since the tenant did not ask about voice. Logs
  // loudly to the console on any real (non-expected) failure per this rollout's
  // no-silent-failure standard. importCampaignNumberToRetell (the explicit
  // "Enable Voice Calling" button) remains the user-facing fallback for a
  // campaign whose assignment happened before this existed.
  const autoImportCampaignNumberSilently = useCallback(async (campaignId) => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) return;

      const apiBase = process.env.REACT_APP_API_URL
        || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api.getsurfox.com/api');

      const response = await fetch(`${apiBase}/voice/campaign-number/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId }),
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        // 409 (tenant not voice-provisioned) and 400 CAMPAIGN_NOT_VOICE_ELIGIBLE
        // are the expected, quiet no-op for the overwhelming majority of saves —
        // only a genuine failure is worth a console entry.
        if (response.status !== 409 && body?.error !== 'CAMPAIGN_NOT_VOICE_ELIGIBLE') {
          console.error(`🔴 [auto-import] campaign-number/import failed for campaign ${campaignId}: ${body?.error || response.statusText}`);
        }
        return;
      }

      const results = body.results || {};
      const failed = Object.values(results).filter((r) => !r.ok);
      if (failed.length > 0) {
        console.error(`🔴 [auto-import] campaign-number/import partial failure for campaign ${campaignId}:`, results);
      }
    } catch (err) {
      console.error(`🔴 [auto-import] campaign-number/import threw for campaign ${campaignId}:`, err);
    }
  }, []);

  // Phase 8, Voice AI v1 (strategic-planning/VOICE-AI-V1-HANDOFF.md §0.4): same save
  // path as every other per-field campaign setting in this panel. 'async_handoff' is
  // the only value campaigns_warm_transfer_mode_check allows today — this still writes
  // through the real column (optimistic set, .update(), revert-on-error) rather than
  // being a decorative click handler, so Phase 9 only has to enable the disabled
  // "Live transfer" option in the JSX above, not rebuild this wiring.
  // Voice AI v1 Phase 9: 'live_transfer' is now a real, selectable mode — the DB CHECK
  // constraint was widened (20260912_voice_ai_phase9_live_transfer.sql) once
  // src/ws/retell-llm-stream.js had a real mechanism behind it. Requires
  // forward_call_number to already be set: the same column campaign.forward_call_number
  // (labeled "Forward Calls To" in Notifications & Routing above) is what
  // retell-llm-stream.js reads as the live-transfer destination, so selecting this mode
  // with no number set would silently offer the AI a tool with nothing to transfer to —
  // retell-llm-stream.js's own resolveTransferCapability() would just never enable the
  // tool in that case, but surfacing that as a confusing no-op here instead of a clear
  // upfront error would be worse.
  const updateCampaignWarmTransferMode = useCallback(async (campaignId, mode, forwardCallNumber) => {
    if (mode !== 'async_handoff' && mode !== 'live_transfer') {
      return; // the DB CHECK constraint only allows these two values today
    }
    if (mode === 'live_transfer' && !String(forwardCallNumber || '').trim()) {
      // No page-level setError() here — the Live Transfer card already shows this exact
      // warning inline (the amber "Set 'Forward Calls To' first" text right below the
      // option), which the tenant is already looking at when they click it. Firing the
      // shared error banner too just duplicates the message in a confusing place (it
      // renders behind the modal, not inside it).
      return;
    }

    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, warm_transfer_mode: mode } : c
    ));

    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ warm_transfer_mode: mode })
        .eq('id', campaignId);
      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating warm transfer mode:', err);
      setError(`Failed to update warm transfer mode: ${err.message}`);
      fetchCampaigns(); // Revert to real DB state on failure
    }
  }, [fetchCampaigns]);

  const updateCampaignTimezone = useCallback(async (campaignId, timezone) => {
    // Optimistic update
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, timezone } : c
    ));

    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ timezone })
        .eq('id', campaignId);

      if (error) throw error;
      setError('');
    } catch (err) {
      console.error('Error updating campaign timezone:', err);
      setError('Failed to update campaign timezone');
      fetchCampaigns(); // Revert on error
    }
  }, [supabase, fetchCampaigns]);

  const updateCampaignPhoneNumber = useCallback(async (campaignId, phoneNumberId) => {
    const phoneIdNum = phoneNumberId ? Number(phoneNumberId) : null;
    const phone = phoneNumbers.find(p => p.id === phoneIdNum);
    setCampaigns(prev => prev.map(c => {
      if (c.id === campaignId) {
        return {
          ...c,
          phone_number_id: phoneIdNum,
          phone_number: phone?.phone_number || null,
          additional_phone_ids: (c.additional_phone_ids || []).filter(id => id !== phoneIdNum)
        };
      }
      return c;
    }));

    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ phone_number_id: phoneIdNum })
        .eq('id', campaignId);
      if (error) throw error;

      // Mirror primary into the pool so the helper's fallback chain is consistent.
      if (phoneIdNum) {
        await supabase
          .from('campaign_phone_numbers')
          .upsert(
            { campaign_id: campaignId, phone_number_id: phoneIdNum },
            { onConflict: 'campaign_id,phone_number_id' }
          );
        // 🔴🟢 2026-09-17 (Tom's call): auto-import used to fire HERE, on plain
        // number assignment — moved to the Follow-ups "Save & Rebuild" handler,
        // firing only once a tenant has actually chosen Voice for a step. This
        // call site imported every number a tenant assigns to ANY non-bulk
        // campaign, whether or not they ever wanted voice on it (e.g. a pure
        // multi-number SMS rotation), at a real $2/month/number cost.
      }
      setError('');
    } catch (err) {
      console.error('Error updating campaign phone number:', err);
      setError('Failed to update campaign phone number');
      fetchCampaigns();
    }
  }, [phoneNumbers, fetchCampaigns, autoImportCampaignNumberSilently]);

  const updateCampaignAdditionalPhones = useCallback(async (campaignId, nextAdditionalIds) => {
    const desired = nextAdditionalIds.map(Number);
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId ? { ...c, additional_phone_ids: desired } : c
    ));

    try {
      const campaign = campaigns.find(c => c.id === campaignId);
      const primaryId = campaign?.phone_number_id ? Number(campaign.phone_number_id) : null;
      const previousAdditional = (campaign?.additional_phone_ids || []).map(Number);

      const toAdd = desired.filter(id => !previousAdditional.includes(id));
      const toRemove = previousAdditional.filter(id => !desired.includes(id));

      if (toRemove.length > 0) {
        await supabase
          .from('campaign_phone_numbers')
          .delete()
          .eq('campaign_id', campaignId)
          .in('phone_number_id', toRemove);
      }

      if (toAdd.length > 0) {
        await supabase
          .from('campaign_phone_numbers')
          .upsert(
            toAdd.map(phone_number_id => ({ campaign_id: campaignId, phone_number_id })),
            { onConflict: 'campaign_id,phone_number_id' }
          );
      }

      // Defensive: ensure primary stays in the pool (no-op if already there).
      if (primaryId) {
        await supabase
          .from('campaign_phone_numbers')
          .upsert(
            { campaign_id: campaignId, phone_number_id: primaryId },
            { onConflict: 'campaign_id,phone_number_id' }
          );
      }
      // 🔴🟢 2026-09-17 (Tom's call): same removal as updateCampaignPhoneNumber
      // above — auto-import now only fires from the Follow-ups save handler,
      // once a tenant has actually chosen Voice for a step, not on plain pool
      // assignment.
      setError('');
    } catch (err) {
      console.error('Error updating additional phone pool:', err);
      setError('Failed to update additional phones');
      fetchCampaigns();
    }
  }, [campaigns, fetchCampaigns, autoImportCampaignNumberSilently]);

const updateCampaignDynamicField = useCallback(async (campaignId, field, value) => {

  let updateData = {};

  // Custom talk track selected (value prefixed with 'custom__')
  if (value && typeof value === 'string' && value.startsWith('custom__')) {
    const customId = value.slice(8);
    updateData = { custom_talk_track_id: customId };
  } else if (field === 'parent') {
    updateData.talk_track_type = value;
    updateData.talk_track_specialty = null;
    updateData.custom_talk_track_id = null;
  } else if (field === 'child') {
    updateData.talk_track_specialty = value;
  } else {
    return;
  }

  // Optimistic update
  setCampaigns(prev => prev.map(c => 
    c.id === campaignId ? { ...c, ...updateData } : c
  ));

  try {
    const { data, error } = await supabase
      .from('campaigns')
      .update(updateData)
      .eq('id', campaignId)
      .select('id');

    if (error) {
      console.error('Supabase update error:', error);
      throw error;
    }
    if (!data || data.length === 0) {
      console.error('Update matched 0 rows — possible RLS block or missing campaign');
      throw new Error('Update matched 0 rows');
    }

    // If a custom talk track was assigned, auto-rebuild instructions for this campaign
    if (updateData.custom_talk_track_id) {
      try {
        const rebuildRes = await fetchWithCsrf(`${API_BASE}/campaigns/${campaignId}/apply-talk-track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!rebuildRes.ok) {
          const errText = await rebuildRes.text();
          console.warn('Instruction rebuild responded non-OK:', rebuildRes.status, errText);
        }
      } catch (rebuildErr) {
        console.warn('Instruction rebuild failed after talk track assignment:', rebuildErr);
        // Non-fatal — assignment saved, rebuild can be retried via global admin
      }
    }

    setError('');
  } catch (err) {
    console.error('Error updating campaign dynamic field:', err);
    setError('Failed to update campaign');
    fetchCampaigns(); // Revert on error
  }
}, [fetchCampaigns]);

  const updateCampaignKnowledgeLinks = useCallback(async (campaignId, selectedIds, masterIds = []) => {
    try {
      // Delete only non-master links — master links are managed by DB triggers
      let deleteQuery = supabase
        .from('campaign_knowledge_links')
        .delete()
        .eq('campaign_id', campaignId);

      if (masterIds.length > 0) {
        deleteQuery = deleteQuery.not('knowledge_id', 'in', `(${masterIds.join(',')})`);
      }

      const { error: deleteError } = await deleteQuery;
      if (deleteError) throw deleteError;

      if (selectedIds.length > 0) {
        const newLinks = selectedIds.map((knowledge_id) => ({
          campaign_id: campaignId,
          knowledge_id: knowledge_id
        }));

        const { error: insertError } = await supabase
          .from('campaign_knowledge_links')
          .insert(newLinks);

        if (insertError) throw insertError;
      }
      setError('');
    } catch (err) {
      console.error('Error updating campaign knowledge links:', err);
      setError('Failed to update knowledge assets');
    }
  }, []);

  const updateHotLeadDistribution = useCallback(async (campaignId, field, value) => {
  let updateData = {};
  
  if (field === 'mode') {
    updateData.hot_lead_distribution_mode = value;
    // Clear other fields when mode changes
    updateData.hot_lead_single_assignee_id = null;
    updateData.hot_lead_round_robin_team = null;
  } else if (field === 'single_assignee') {
    updateData.hot_lead_single_assignee_id = value;
  } else if (field === 'round_robin_toggle') {
    const campaign = campaigns.find(c => c.id === campaignId);
    const currentTeam = campaign.hot_lead_round_robin_team || [];
    let newTeam;
    
    if (value.checked) {
      newTeam = [...currentTeam, value.memberId];
    } else {
      newTeam = currentTeam.filter(id => id !== value.memberId);
    }
    
    updateData.hot_lead_round_robin_team = newTeam.length > 0 ? newTeam : null;
  }

  // Optimistic update
  setCampaigns(prev => prev.map(c => 
    c.id === campaignId ? { ...c, ...updateData } : c
  ));

  try {
    const { error } = await supabase
      .from('campaigns')
      .update(updateData)
      .eq('id', campaignId);

    if (error) throw error;
    setError('');
  } catch (err) {
    console.error('Error updating hot lead distribution:', err);
    setError('Failed to update hot lead assignment');
    fetchCampaigns(); // Revert on error
  }
}, [campaigns, fetchCampaigns]);

// AI Toggle Pre-Flight Validation Function
const validateAiToggle = useCallback(async (campaign) => {
  try {
    // Check 0: AI Strategy (talk track) required — campaigns created via the add-lead
    // flow are "shells" (leads attached, no strategy). They must be completed with Surf
    // before AI can be turned on. This runs first so shells route to Surf rather than
    // failing the AI-instructions check (which would dead-end them at AI Studio).
    // Voice is exempt. WEB IS NOT (2026-09-10): web chat builds its whole brain from the
    // talk track (generate-web-chat-reply reads strategy_block + pillar_data +
    // objectives_block). Same rule as the readiness ring and the ai-toggle route.
    const isWebChannel = campaign.id === widgetCampaignId;
    const isVoiceChannel = campaign.id === voiceCampaignId;
    // Same brain as web (generate-web-chat-reply, channel:'messenger') — everywhere
    // isWebChannel exempts a check below, Messenger needs the identical exemption.
    const isMessengerChannel = campaign.id === messengerCampaignId;
    const hasTalkTrack = !!(campaign.custom_talk_track_id || campaign.talk_track_type);
    if (!isVoiceChannel && !hasTalkTrack) {
      return {
        valid: false,
        error: {
          type: 'talk_track',
          title: 'Campaign Needs a Strategy First',
          message: isWebChannel
            ? 'Web chat answers from this campaign\'s talk track, and it does not have one yet. Set one up with Surf (it takes about a minute) before turning AI on.'
            : isMessengerChannel
              ? 'Messenger answers from this campaign\'s talk track, and it does not have one yet. Set one up with Surf (it takes about a minute) before turning AI on.'
              : 'This campaign has leads but no AI strategy yet. Set one up with Surf (it takes about a minute) before turning AI on.',
          action: 'Set Up with Surf',
          actionEvent: 'surf:open',
          actionUrl: null
        }
      };
    }

    // Check 1: AI Instructions Configured. Not for web: generate-web-chat-reply never
    // reads ai_instruction_initial (the SMS opener's snapshot), so it cannot matter there.
    if (!isWebChannel && !isMessengerChannel && (!campaign.ai_instruction_initial || campaign.ai_instruction_initial.trim() === '')) {
      return {
        valid: false,
        error: {
          type: 'ai_instructions',
          title: 'AI Instructions Missing',
          message: 'Configure AI instructions before enabling AI.',
          action: 'Configure AI Instructions',
          actionUrl: `/settings?tab=ai-studio&campaign=${campaign.id}`
        }
      };
    }

    // Check 2: Knowledge Base Required — at least 1 master doc or 1 linked doc
    const hasMasterDoc = knowledgeAssets?.some(a => a.tenant_id === campaign.tenant_id && a.is_master);
    const hasLinkedDoc = selectedKnowledgeAssets?.[campaign.id]?.length > 0;
    if (!hasMasterDoc && !hasLinkedDoc) {
      return {
        valid: false,
        error: {
          type: 'knowledge_base',
          title: 'Knowledge Base Required',
          message: 'Upload at least one Knowledge Base document (or set a master document) before enabling AI.',
          action: 'Go to Knowledge Base',
          actionUrl: `/settings?tab=knowledge-base`
        }
      };
    }

    // Web channel campaigns have no phone/hours/subscription requirements — remaining checks are SMS-only
    // Voice campaigns get their number from Voice Channel settings — same exemption applies.
    // EXCEPT the hot-lead email, which every channel needs. Until 2026-09-10 this branch
    // returned valid before the email check was ever reached, although the comment on
    // that check said the opposite; the readiness ring already required it here.
    if (isWebChannel || isVoiceChannel || isMessengerChannel) {
      const emailBlock = hotLeadEmailBlock(campaign);
      if (emailBlock) return emailBlock;
      return { valid: true };
    }

    // Check 3: Phone Number Assigned (skip for web/Messenger — neither uses a phone number)
    if (!campaign.phone_number_id && campaign.id !== widgetCampaignId && campaign.id !== voiceCampaignId && campaign.id !== messengerCampaignId) {
      return {
        valid: false,
        error: {
          type: 'phone_number',
          title: 'Phone Number Not Assigned',
          message: 'Assign a phone number to this campaign before enabling AI.',
          action: 'Assign Phone Number',
          actionUrl: `/settings?tab=phone-numbers`
        }
      };
    }

    // Check 2b: Hot Lead Notification Email Required (SMS). Web and voice run the same
    // check in their branch above. The comment that used to sit here said this check
    // ran above the web/voice exemption; it did not, it ran below it, so web and voice
    // never reached it. Kept at this position so SMS reports its errors in the same
    // order as before. Rationale lives on hotLeadEmailBlock.
    const smsEmailBlock = hotLeadEmailBlock(campaign);
    if (smsEmailBlock) return smsEmailBlock;

    // Check 3a: Campaign Timezone Required
    if (!campaign.timezone) {
      return {
        valid: false,
        error: {
          type: 'campaign_timezone',
          title: 'Campaign Timezone Not Set',
          message: 'Select a timezone for this campaign so business hours are enforced correctly for your target audience.',
          action: 'Set Timezone',
          actionUrl: null
        }
      };
    }

    // Check 3b: Business Hours Configured
    const { data: settingsData, error: settingsError } = await supabase
      .from('platform_settings')
      .select('key, value')
      .eq('tenant_id', campaign.tenant_id)
      .in('key', ['officeOpenHour', 'officeCloseHour', 'officeDays', 'timezone']);

    if (settingsError) {
      console.error('Error fetching business hours:', settingsError);
    }

    const settings = {};
    if (settingsData) {
      settingsData.forEach(s => {
        settings[s.key] = s.value;
      });
    }

    if (!settings.officeOpenHour || !settings.officeCloseHour || !settings.officeDays) {
      return {
        valid: false,
        error: {
          type: 'business_hours',
          title: 'Business Hours Not Configured',
          message: 'Configure business hours in settings before enabling AI.',
          action: 'Configure Business Hours',
          actionUrl: '/settings?tab=company-profile'
        }
      };
    }

    // Check 4: Subscription Limits
    const { data: tenantData, error: tenantError } = await supabase
      .from('tenants')
      .select('current_month_sms_count, plan_limits, plan')
      .eq('id', campaign.tenant_id)
      .single();

    if (tenantError) {
      console.error('Error fetching tenant data:', tenantError);
    }

    if (tenantData) {
      const limits = tenantData.plan_limits || {};
      const monthlyLimit = limits.monthly_sms_limit || limits.sms_limit || 2000;
      const currentCount = tenantData.current_month_sms_count || 0;

      if (currentCount >= monthlyLimit) {
        return {
          valid: false,
          error: {
            type: 'subscription_limit',
            title: 'Message Limit Reached',
            message: `You've reached your ${tenantData.plan || 'plan'} limit of ${monthlyLimit.toLocaleString()} messages this month. Upgrade your plan or wait for the cycle to reset.`,
            action: 'Upgrade Plan',
            actionUrl: '/settings?tab=billing'
          }
        };
      }
    }

    // All checks passed - now check business hours for informational warning
    // Use campaign-level timezone (falls back to tenant timezone from platform_settings)
    const now = new Date();
    const timezone = campaign.timezone || settings.timezone || 'America/New_York';
    const currentHour = parseInt(now.toLocaleString('en-US', {
      hour: '2-digit',
      hour12: false,
      timeZone: timezone
    }));
    const currentDayShort = now.toLocaleString('en-US', { weekday: 'short', timeZone: timezone });
    const currentDayLong = now.toLocaleString('en-US', { weekday: 'long', timeZone: timezone });

    const openHour = parseInt(settings.officeOpenHour);
    const closeHour = parseInt(settings.officeCloseHour);
    const officeDays = settings.officeDays; // Could be "M–F", "M–Sat", "Everyday" OR "Monday,Tuesday,Wednesday,Thursday,Friday"

    let isBusinessHours = false;

    // Check if current day is within office days
    // Handle both preset format ("M–F") and full day name format ("Monday,Tuesday,Wednesday,Thursday,Friday")
    if (officeDays.includes(',')) {
      // Database stores comma-separated full day names
      const dayList = officeDays.split(',').map(d => d.trim());
      isBusinessHours = dayList.includes(currentDayLong);
    } else {
      // Legacy preset format
      const dayMap = { 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6, 'Sun': 7 };
      const currentDayNum = dayMap[currentDayShort];

      if (officeDays === 'Everyday') {
        isBusinessHours = true;
      } else if (officeDays === 'M–F' && currentDayNum >= 1 && currentDayNum <= 5) {
        isBusinessHours = true;
      } else if (officeDays === 'M–Sat' && currentDayNum >= 1 && currentDayNum <= 6) {
        isBusinessHours = true;
      }
    }

    // Check if current hour is within business hours
    if (isBusinessHours) {
      isBusinessHours = currentHour >= openHour && currentHour < closeHour;
    }

    // Calculate next business hours window
    let nextBusinessHours = null;
    if (!isBusinessHours) {
      const openTime = `${openHour === 0 ? '12' : openHour > 12 ? openHour - 12 : openHour} ${openHour >= 12 ? 'PM' : 'AM'}`;

      // Build list of valid business day names
      let businessDayNames = [];
      if (officeDays.includes(',')) {
        businessDayNames = officeDays.split(',').map(d => d.trim());
      } else if (officeDays === 'Everyday') {
        businessDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      } else if (officeDays === 'M–Sat') {
        businessDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      } else {
        businessDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      }

      // If today is a business day and we're before open hour, next window is today
      if (businessDayNames.includes(currentDayLong) && currentHour < openHour) {
        nextBusinessHours = `Today at ${openTime}`;
      } else {
        // Walk forward up to 7 days to find the next business day
        const cursor = new Date(now);
        for (let i = 1; i <= 7; i++) {
          cursor.setDate(cursor.getDate() + 1);
          const dayName = cursor.toLocaleString('en-US', { weekday: 'long', timeZone: timezone });
          if (businessDayNames.includes(dayName)) {
            nextBusinessHours = `${dayName} at ${openTime}`;
            break;
          }
        }
      }
    }

    // Convert day list to display format
    let officeDaysDisplay = officeDays;
    if (officeDays.includes(',')) {
      const dayList = officeDays.split(',').map(d => d.trim());
      if (dayList.length === 7) {
        officeDaysDisplay = 'Everyday';
      } else if (dayList.length === 6 && !dayList.includes('Sunday')) {
        officeDaysDisplay = 'M–Sat';
      } else if (dayList.length === 5 && dayList.includes('Monday') && dayList.includes('Friday') && !dayList.includes('Saturday') && !dayList.includes('Sunday')) {
        officeDaysDisplay = 'M–F';
      }
    }

    return {
      valid: true,
      isBusinessHours,
      businessHoursInfo: {
        officeDays: officeDaysDisplay,
        openHour,
        closeHour,
        timezone,
        nextBusinessHours
      }
    };

  } catch (err) {
    console.error('Validation error:', err);
    return {
      valid: false,
      error: {
        type: 'system_error',
        title: 'Validation Failed',
        message: 'Unable to validate campaign configuration. Please try again.',
        action: null,
        actionUrl: null
      }
    };
  }
}, [widgetCampaignId, voiceCampaignId, messengerCampaignId, knowledgeAssets, selectedKnowledgeAssets]);

  // adminConfirmed: set only when re-entered from AdminActivationWarningModal's
  // Confirm. window.confirm() blocked inline and fell through on OK; a modal can't
  // block, so the confirm branch bails and the modal calls back in with this flag.
  // Everything downstream is untouched and runs in the same order it always did.
  const toggleAiOn = useCallback(async (campaignId, currentAiOn, adminConfirmed = false) => {
  // Only run validation when ENABLING AI (not when disabling)
  if (!currentAiOn) {
    // Global admin safety confirmation — prevents accidental AI activation
    if (isGlobalAdmin && !adminConfirmed) {
      const campaign = campaigns.find(c => c.id === campaignId);
      setAdminActivationConfirm({ campaignId, currentAiOn, campaignName: campaign?.name });
      return; // Cancel simply never calls back — same as declining the old dialog.
    }

    const campaign = campaigns.find(c => c.id === campaignId);
    if (!campaign) {
      setError('Campaign not found');
      return;
    }

    // Run pre-flight validation
    const validation = await validateAiToggle(campaign);

    if (!validation.valid) {
      // Show error modal - block the toggle
      setValidationError(validation.error);
      return;
    }

    // Validation passed - check if outside business hours (web channel is 24/7, skip this)
    // Voice channel is 24/7 too — caller dials in whenever, agent answers.
    if (!validation.isBusinessHours && campaignId !== widgetCampaignId && campaignId !== voiceCampaignId && campaignId !== messengerCampaignId) {
      // Show informational warning (doesn't block toggle)
      setBusinessHoursInfo({
        ...validation.businessHoursInfo,
        campaignId,
        campaignName: campaign.name
      });
      setShowBusinessHoursWarning(true);
      return;
    }
  }

  // Proceed with toggle (either disabling AI or validation passed)
  performAiToggle(campaignId, currentAiOn);
  // Deps intentionally unchanged: performAiToggle is a `const` declared below, so
  // naming it here would evaluate in its temporal dead zone at render and throw.
}, [campaigns, validateAiToggle, isGlobalAdmin]);

// Extracted toggle logic for reuse after validation/warning
const performAiToggle = useCallback(async (campaignId, currentAiOn) => {
  // Optimistic update. Pause toggles ai_on ONLY — it no longer touches is_active,
  // mirroring the ai-toggle route. A paused campaign is still an active campaign;
  // only archiving clears is_active.
  setCampaigns(prev => prev.map(c =>
    c.id === campaignId
      ? { ...c, ai_on: !currentAiOn }
      : c
  ));

  try {
    const response = await fetchWithCsrf(`${API_BASE}/campaigns/${campaignId}/ai-toggle`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ai_on: !currentAiOn
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      // The go-live gates (NO_TALK_TRACK / A2P_NOT_READY / TEST_GATE_NOT_CLEARED) return a
      // machine code in `error` and readable copy in `message`. Prefer the message —
      // surfacing the raw code told the customer "Failed to toggle AI setting: NO_TALK_TRACK".
      if (errorData.error === 'TEST_GATE_NOT_CLEARED') {
        // Server refused because the gate isn't cleared. Reachable even with a locked
        // button (stale bundle, or a race with another tab), so explain it the same warm
        // way instead of dropping a red error string on them. Undo the optimistic flip
        // and bail without setError — the modal IS the message.
        invalidateGateStatus(campaignId);
        setCampaigns(prev => prev.map(c =>
          c.id === campaignId
            ? { ...c, ai_on: currentAiOn }
            : c
        ));
        const blocked = campaigns.find(c => c.id === campaignId);
        if (blocked) explainGate(blocked);
        return;
      }
      throw new Error(errorData.message || errorData.error || 'Failed to toggle AI setting');
    }

    setError('');

    if (!currentAiOn) {
      // AI just turned ON — celebrate the go-live.
      const campaign = campaigns.find(c => c.id === campaignId);
      setCelebrateCampaign(campaign || { id: campaignId, name: 'Campaign' });
    }
  } catch (err) {
    console.error('Error toggling AI setting:', err);
    setError(`Failed to toggle AI setting: ${err.message}`);
    // Revert optimistic update. Only ai_on was flipped, so only ai_on is restored.
    setCampaigns(prev => prev.map(c =>
      c.id === campaignId
        ? { ...c, ai_on: currentAiOn }
        : c
    ));
  }
}, [campaigns, validateAiToggle, widgetCampaignId, voiceCampaignId, messengerCampaignId, invalidateGateStatus, explainGate]);

  // Archive and restore both confirm through ArchiveCampaignModal. The native
  // window.confirm it replaced read as broken to customers. These two are the public
  // entry points (Launch Console header, Setup panel): they only OPEN the modal. The
  // request itself runs in runArchiveAction when the customer confirms.
  const archiveCampaign = useCallback((campaignId) => {
    const name = campaigns.find(c => c.id === campaignId)?.name || 'this campaign';
    // Same channel rule the console uses (consoleCampaigns). Web chat is the one channel
    // where archiving removes the chat itself: the widget exists only while its campaign is
    // bound, not archived and live (Phase 1e, public.web_chat_widget_state), so the modal
    // only promises that replies continue where it is true.
    setArchiveConfirm({
      mode: 'archive', campaignId, name,
      isWebChat: campaignId === widgetCampaignId,
      busy: false, error: '',
    });
  }, [campaigns, widgetCampaignId]);

  const unarchiveCampaign = useCallback((campaignId) => {
    const name = campaigns.find(c => c.id === campaignId)?.name || 'this campaign';
    setArchiveConfirm({ mode: 'restore', campaignId, name, isWebChat: false, busy: false, error: '' });
  }, [campaigns]);

  const runArchiveAction = useCallback(async () => {
    if (!archiveConfirm || archiveConfirm.busy) return;
    const { mode, campaignId } = archiveConfirm;
    const endpoint = mode === 'archive' ? 'archive' : 'unarchive';
    setArchiveConfirm(prev => prev && { ...prev, busy: true, error: '' });

    try {
      const response = await fetchWithCsrf(`${API_BASE}/campaigns/${campaignId}/${endpoint}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Could not ${mode} this campaign.`);
      }
      // Archiving the web chat campaign clears the tenant's widget binding server-side
      // (campaigns.js archive route, 2026-09-10). Mirror it so nothing on this page still
      // treats the archived campaign as the web one.
      const okBody = await response.json().catch(() => ({}));
      if (mode === 'archive' && okBody?.web_chat_unbound) setWidgetCampaignId(null);
      // Same for the Messenger campaign (the server cleared tenants.messenger_campaign_id, 2026-10-18).
      if (mode === 'archive' && okBody?.messenger_unbound) setMessengerCampaignId(null);

      // Off the current list now, then refetch past the campaign cache. Before
      // 2026-09-10 this called fetchCampaigns() alone, which returned the CACHED list,
      // so the campaign stayed visible for up to two minutes.
      setArchiveConfirm(null);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      clearCampaignListCache();
      await fetchCampaigns();
    } catch (err) {
      console.error(`Error trying to ${mode} campaign:`, err);
      // Keep the modal open and say what went wrong in it, instead of closing it and
      // dropping an error banner somewhere else on the page.
      setArchiveConfirm(prev => prev && { ...prev, busy: false, error: err.message || `Could not ${mode} this campaign.` });
    }
  }, [archiveConfirm, fetchCampaigns]);

  // Campaign creation

useEffect(() => {
  if (showTenantColumn && user) {
    fetchTenants();
  }
}, [showTenantColumn, user, fetchTenants]);

  // Initialize data
  useEffect(() => {
    let mounted = true;
    
const loadInitialData = async () => {
  if (user?.tenant_id && mounted) {
    await Promise.all([
      fetchTenantIndustries(),
      fetchSalesTeamMembers(),
      fetchKnowledgeAssets(),
      fetchPhoneNumbers()
    ]);
        
        if (mounted) {
          await fetchCampaigns();
        }
      }
    };
    
    loadInitialData();
    
    return () => {
      mounted = false;
    };
  }, [user?.tenant_id, fetchTenantIndustries, fetchSalesTeamMembers, fetchKnowledgeAssets, fetchPhoneNumbers, fetchCampaigns]);

  // Refresh campaigns when filters change
  useEffect(() => {
    if (user?.tenant_id) {
      fetchCampaigns();
    }
  }, [showArchived, tenantFilter, user?.tenant_id, fetchCampaigns]);

  // Load knowledge asset links when campaigns change
  useEffect(() => {
    const loadKnowledgeLinks = async () => {
      if (campaigns.length === 0) return;
      
      try {
        const { data, error } = await supabase
          .from('campaign_knowledge_links')
          .select('campaign_id, knowledge_id');
        
        if (error) throw error;
        
        const links = {};
        data?.forEach(link => {
          if (!links[link.campaign_id]) {
            links[link.campaign_id] = [];
          }
          links[link.campaign_id].push(link.knowledge_id);
        });
        
        setSelectedKnowledgeAssets(links);
      } catch (err) {
        console.error('Error loading knowledge links:', err);
      }
    };
    
    if (campaigns.length > 0) {
      loadKnowledgeLinks();
    }
  }, [campaigns]);
  
  // Calculate stats
  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(c => c.is_active === true && c.ai_on === true).length;

  // Template management functions - ADD ALL OF THIS BEFORE THE RETURN STATEMENT
const fetchCampaignTemplates = useCallback(async (campaignId) => {
  if (!campaignId) return;
  
  try {
    const { data, error } = await supabase
      .from('smstemplates')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    
    setCampaignTemplates(prev => ({
      ...prev,
      [campaignId]: data || []
    }));
  } catch (err) {
    console.error('Error fetching campaign templates:', err);
    setError(`Failed to load templates: ${err.message}`);
  }
}, []);

const addCampaignTemplate = useCallback(async (campaignId) => {
  const currentTemplates = campaignTemplates[campaignId] || [];
  if (currentTemplates.length >= 10) {
    setError('Maximum of 10 templates allowed per campaign');
    return;
  }

  try {
    const { data, error } = await supabase
      .from('smstemplates')
      .insert([{
        message: '',
        campaign_id: campaignId,
        tenant_id: user.tenant_id,
        used: 0
      }])
      .select()
      .single();

    if (error) throw error;
    
    setCampaignTemplates(prev => ({
      ...prev,
      [campaignId]: [...(prev[campaignId] || []), data]
    }));
  } catch (err) {
    console.error('Error adding template:', err);
    setError(`Failed to add template: ${err.message}`);
  }
}, [campaignTemplates, user.tenant_id]);

// Local state updates only (no Supabase call)
const updateCampaignTemplateLocal = useCallback((campaignId, templateId, message) => {
  setEditingTemplates(prev => ({
    ...prev,
    [`${campaignId}-${templateId}`]: message
  }));
}, []);

// Actual save to Supabase (manual trigger)
const saveCampaignTemplate = useCallback(async (campaignId, templateId) => {
  const key = `${campaignId}-${templateId}`;
  const message = editingTemplates[key];
  
  if (message === undefined) {
    return;
  }
  
  try {
    const { error } = await supabase
      .from('smstemplates')
      .update({ message })
      .eq('id', templateId)
      .eq('campaign_id', campaignId);

    if (error) {
      console.error('Supabase error:', error);
      throw error;
    }

    // Fetch fresh data from database to ensure sync
    const { data: updatedTemplates, error: fetchError } = await supabase
      .from('smstemplates')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });

    if (fetchError) throw fetchError;

    // Update local state with fresh data
    setCampaignTemplates(prev => ({
      ...prev,
      [campaignId]: updatedTemplates || []
    }));
    
    // Clear editing state
    setEditingTemplates(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
    
  } catch (err) {
    console.error('Error saving template:', err);
    setError(`Failed to save template: ${err.message}`);
  }
}, [editingTemplates]);

// Get current value (editing or saved)
const getTemplateValue = useCallback((campaignId, templateId, savedMessage) => {
  const key = `${campaignId}-${templateId}`;
  return editingTemplates[key] !== undefined ? editingTemplates[key] : savedMessage;
}, [editingTemplates]);

const deleteCampaignTemplate = useCallback(async (campaignId, templateId) => {
  if (!window.confirm('Are you sure you want to delete this template?')) {
    return;
  }

  try {
    const { error } = await supabase
      .from('smstemplates')
      .delete()
      .eq('id', templateId)
      .eq('campaign_id', campaignId);

    if (error) throw error;

    setCampaignTemplates(prev => ({
      ...prev,
      [campaignId]: (prev[campaignId] || []).filter(t => t.id !== templateId)
    }));
  } catch (err) {
    console.error('Error deleting template:', err);
    setError(`Failed to delete template: ${err.message}`);
  }
}, []);

const insertPlaceholderInCampaignTemplate = useCallback((campaignId, templateId, placeholder) => {
  const templates = campaignTemplates[campaignId] || [];
  const template = templates.find(t => t.id === templateId);
  if (!template) return;

  const textarea = document.getElementById(`campaign-template-${templateId}`);
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const currentMessage = editingTemplates[`${campaignId}-${templateId}`] !== undefined 
    ? editingTemplates[`${campaignId}-${templateId}`] 
    : template.message || '';
  const newMessage = currentMessage.slice(0, start) + placeholder + currentMessage.slice(end);
  
  updateCampaignTemplateLocal(campaignId, templateId, newMessage);
  
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
  }, 0);
}, [campaignTemplates, editingTemplates, updateCampaignTemplateLocal]); 

// 🔧 Utility: replace placeholders with actual field values
const populateTemplate = (template, lead) => {
  if (!template) return '';
  
  return template.replace(/\{([^}]+)\}/g, (match, fieldName) => {
    const trimmedField = fieldName.trim();
    
    // Check leads table fields first
    if (trimmedField === 'first_name') return lead.first_name || '[No First Name]';
    if (trimmedField === 'last_name') return lead.last_name || '[No Last Name]';
    
    // Otherwise check custom_fields
    return lead.custom_fields?.[trimmedField] || `[No ${trimmedField}]`;
  });
};

// 🔍 Preview Modal handler
const handlePreviewTemplate = useCallback(async (campaignId, templateId) => {
  try {
    const templates = campaignTemplates[campaignId] || [];
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    // Fetch first lead from campaign
    const { data: lead, error } = await supabase
      .from('leads')
      .select('first_name, last_name, custom_fields')
      .eq('campaign_id', campaignId)
      .limit(1)
      .single();

    if (error || !lead) {
      setPreviewContent('No leads found for this campaign');
      setShowPreview(true);
      return;
    }

    // Get current value (either editing or saved)
    const currentMessage = editingTemplates[`${campaignId}-${templateId}`] !== undefined
      ? editingTemplates[`${campaignId}-${templateId}`]
      : template.message;
    
    const merged = populateTemplate(currentMessage, lead);
    setPreviewContent(merged);
    setShowPreview(true);
  } catch (err) {
    console.error('Preview error:', err.message);
    setPreviewContent('Error loading preview');
    setShowPreview(true);
  }
}, [campaignTemplates, editingTemplates]);



// Load templates when campaign is selected
useEffect(() => {
  if (selectedCampaignForTemplates && !campaignTemplates[selectedCampaignForTemplates]) {
    fetchCampaignTemplates(selectedCampaignForTemplates);
  }
}, [selectedCampaignForTemplates, campaignTemplates, fetchCampaignTemplates]);

  if (error && campaigns.length === 0) {
    return <ErrorMessage error={error} onRetry={fetchCampaigns} />;
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 lg:px-8">

      {/* New-account send ramp. Self-hides outside the 21-day window and on the
          archived view, where there is nothing actionable to explain. */}
      {!showArchived && <WarmupRampBanner />}

      {/* The ramp's successor. Once the 21 days are up there is no ceiling at all,
          and this is where the customer sets their own. Deliberately adjacent: the
          two are the same question at different stages of an account's life, and
          exactly one of them renders at a time (both read the same anchor, and this
          one returns null while the ramp is running). */}
      {!showArchived && <SendingPaceBanner />}

      {/* TCPA quiet-hours correction. Self-hides once the saved window is legal.
          Lives here rather than in settings because nobody revisits a settings
          page they've already filled in. */}
      {!showArchived && <TcpaHoursBanner />}

      {/* Header with Tabs */}
<div className="space-y-6">
  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
    <div className="min-w-0 flex-1">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
        {showArchived ? 'Archived Campaigns' : 'Campaign Management'}
      </h1>
      <p className="text-gray-600 mt-2">
        {showArchived 
          ? 'View and restore archived campaigns'
          : 'Create, manage, and track your AI-powered messaging campaigns'
        }
      </p>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <Button
        className="!bg-white !text-gray-700 border border-surfox-border hover:!bg-gray-50 !shadow-none"
        onClick={() => setShowArchived(!showArchived)}
      >
        {showArchived ? (
          <>
            <ArchiveRestore className="w-4 h-4 mr-2" />
            Show Active
          </>
        ) : (
          <>
            <Archive className="w-4 h-4 mr-2" />
            Archived
          </>
        )}
      </Button>
      
      {!showArchived && (
        <Button onClick={() => window.dispatchEvent(new CustomEvent('surf:open'))}>
          <Plus className="w-4 h-4 mr-2" />
          Create Campaign
        </Button>
      )}
    </div>
  </div>

  {/* Tab Navigation */}
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="border-b border-gray-200">
      <nav className="flex space-x-8 px-6" aria-label="Tabs">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'campaigns'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Megaphone className="w-4 h-4" />
            <span>Campaigns</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'templates'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Campaign Templates</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('transfer')}
          className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
            activeTab === 'transfer'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            <ArrowRight className="w-4 h-4" />
            <span>Lead Transfer</span>
          </div>
        </button>
      </nav>
    </div>
  </div>
</div>

{activeTab === 'campaigns' && (
  <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          icon={Megaphone}
          title="Total Campaigns"
          value={totalCampaigns.toLocaleString()}
          color="blue"
        />
        <StatCard
          icon={BarChart2}
          title="Leads Analyzed"
          value={campaignStats.processedLeads.toLocaleString()}
          subtitle={campaignStats.totalLeads > 0 
            ? `${campaignStats.remainingLeads > 0 ? Math.min(99, Math.floor((campaignStats.processedLeads / campaignStats.totalLeads) * 100)) : 100}% complete`
            : 'No leads yet'}
          color="blue"
        />
        <StatCard
          icon={Clock}
          title="Remaining"
          value={campaignStats.remainingLeads.toLocaleString()}
          subtitle={(() => {
            if (campaignStats.remainingLeads === 0) return 'All processed';
            // Only show an ETA when we're actually sending. Otherwise the estimate is misleading
            // (e.g. "~1 hr" at 6pm when nothing will go out until tomorrow morning).
            if (!messagesLastHour || messagesLastHour === 0) return 'Paused';
            const hrs = Math.ceil(campaignStats.remainingLeads / messagesLastHour);
            return `~${hrs} hr${hrs === 1 ? '' : 's'} at current rate`;
          })()}
          color="blue"
        />
        <StatCard
          icon={TrendingUp}
          title="AI Speed"
          value={messagesLastHour && messagesLastHour > 0 ? messagesLastHour.toLocaleString() : '—'}
          subtitle={messagesLastHour && messagesLastHour > 0 ? 'msgs sent last hr' : 'not sending'}
          color="blue"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2 lg:p-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={isGlobalAdmin ? "Search campaigns or tenants..." : "Search campaigns..."}
                className="w-full pl-10 pr-4 py-3 border border-surfox-border rounded-lg focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary text-base"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
            
            <select
              className="border border-surfox-border rounded-lg px-3 py-3 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary text-base bg-white min-w-[120px]"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="ai_active">AI Active</option>
            </select>

            {isGlobalAdmin && (
              <select
                className="border border-surfox-border rounded-lg px-3 py-3 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary text-base bg-white min-w-[140px]"
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
              >
                <option value="all">All Tenants</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenantNames[tenant.id] || tenant.name || `Tenant ${tenant.id}`}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Campaign List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <CampaignSkeleton key={i} />
          ))}
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 lg:p-12">
          <div className="text-center flex flex-col items-center">
            {showArchived ? (
              <>
                <Archive className="w-16 h-16 text-gray-400 mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No archived campaigns found</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto text-lg">
                  There are no archived campaigns to display.
                </p>
              </>
            ) : searchTerm || statusFilter !== 'all' ? (
              <>
                <Megaphone className="w-16 h-16 text-gray-400 mx-auto mb-6" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No campaigns found</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto text-lg">
                  Try adjusting your search or filter criteria.
                </p>
              </>
            ) : (
              <>
                {/* Speech bubble */}
                <div className="relative bg-slate-100 rounded-2xl px-6 py-4 mb-3">
                  <p className="text-base text-slate-700 font-medium">
                    No campaigns yet. Create one and I'll get to work.
                  </p>
                  {/* Bubble tail */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-slate-100" />
                </div>
                <img
                  src="/transparentfoxsitting.png"
                  alt="SurFox"
                  className="h-36 w-auto mb-6"
                />
                <Button onClick={() => window.dispatchEvent(new CustomEvent('surf:open'))} size="lg">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Campaign
                </Button>
              </>
            )}
          </div>
        </div>
) : (
        /* Launch Console (master–detail readiness view) */
        <div data-tour="campaign-row" className="h-[78vh] min-h-[620px]">
          <LaunchConsole
            key={deepLinkCampaignId || 'all'}
            campaigns={consoleCampaigns}
            initialSelectedId={deepLinkCampaignId}
            onCreate={() => window.dispatchEvent(new CustomEvent('surf:open'))}
            onToggleAi={(c) => toggleAiOn(c.id, c.ai_on)}
            onOpenStep={(c, key) => openCampaignSettings(c.id, key)}
            fetchAudience={fetchCampaignAudience}
            getSetup={consoleGetSetup}
            sandboxEnabled={sandboxEnabled}
            fetchGateStatus={fetchGateStatus}
            onBlockedActivate={explainGate}
            onTestConversation={(c) => setTestCampaign(c)}
            dncEligible={dncEligible}
            onScrubDnc={(c) => setScrubCampaign(c)}
            onArchive={(c) => archiveCampaign(c.id)}
            onRestore={(c) => unarchiveCampaign(c.id)}
            archivedView={showArchived}
            webChatSnippet={webChatSnippet}
          />
        </div>
      )}
  {archiveConfirm && (
    <ArchiveCampaignModal
      mode={archiveConfirm.mode}
      campaignName={archiveConfirm.name}
      isWebChat={archiveConfirm.isWebChat}
      busy={archiveConfirm.busy}
      error={archiveConfirm.error}
      onConfirm={runArchiveAction}
      onCancel={() => { if (!archiveConfirm.busy) setArchiveConfirm(null); }}
    />
  )}
  {adminActivationConfirm && (
    <AdminActivationWarningModal
      campaignName={adminActivationConfirm.campaignName}
      onCancel={() => setAdminActivationConfirm(null)}
      onConfirm={() => {
        const { campaignId, currentAiOn } = adminActivationConfirm;
        setAdminActivationConfirm(null);
        // Re-enter past the confirm branch; validation, business hours and the
        // toggle then run exactly as they did after the old dialog's OK.
        toggleAiOn(campaignId, currentAiOn, true);
      }}
    />
  )}
  {gateBlockedCampaign && (
    <TestGateExplainerModal
      campaign={gateBlockedCampaign}
      gate={gateStatuses[gateBlockedCampaign.id]}
      onClose={() => setGateBlockedCampaign(null)}
      onStartTesting={() => {
        const c = gateBlockedCampaign;
        setGateBlockedCampaign(null);
        setTestCampaign(c);
      }}
    />
  )}
  {testCampaign && (
    <TestConversationModal
      campaign={testCampaign}
      apiBase={API_BASE}
      fetchWithCsrf={fetchWithCsrf}
      onClose={() => { invalidateGateStatus(testCampaign.id); setTestCampaign(null); }}
      // Gate cleared and confirmed in the modal — close it and run the SAME
      // activation path the Go live button uses (validation, business hours, PATCH).
      onGateCleared={(c) => {
        invalidateGateStatus(c.id);
        setTestCampaign(null);
        toggleAiOn(c.id, c.ai_on);
      }}
    />
  )}
  {scrubCampaign && (
    <CampaignDncScrubModal
      campaign={scrubCampaign}
      apiBase={API_BASE}
      fetchWithCsrf={fetchWithCsrf}
      onClose={() => setScrubCampaign(null)}
    />
  )}
  </div>
)}

{/* Legacy table view — superseded by LaunchConsole, retained (disabled) for reference */}
{false && activeTab === 'campaigns' && (
  <div className="space-y-6">
      {filteredCampaigns.length === 0 ? null : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Hot Leads
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Start AI
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCampaigns.map((campaign) => {
                  const hotLeadAssignment = getHotLeadAssignment(campaign);

                  const toggleRowExpanded = () => {
                    const newExpanded = new Set(expandedRows);
                    if (newExpanded.has(campaign.id)) {
                      newExpanded.delete(campaign.id);
                    } else {
                      newExpanded.add(campaign.id);
                      // Dispatch event for tour
                      window.dispatchEvent(new CustomEvent('tour-campaign-expanded'));
                    }
                    setExpandedRows(newExpanded);
                  };

                  return (
                    <React.Fragment key={campaign.id}>
                      <tr
                        onClick={toggleRowExpanded}
                        className={`cursor-pointer transition-all duration-200 border-l-4 ${
                        expandedRows.has(campaign.id)
                          ? 'bg-amber-50/30 border-l-blue-500'
                          : campaign.ai_on
                            ? 'hover:bg-orange-50/30 border-l-green-500'
                            : isCampaignSetupComplete(campaign)
                              ? 'hover:bg-orange-50/30 border-l-amber-400'
                              : 'hover:bg-orange-50/30 border-l-gray-300'
                      }`}>
                        <td className="px-6 py-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="text-base font-bold text-gray-900">{campaign.name}</div>
                              <button
                                onClick={(e) => handleCopyId(e, campaign.id)}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                aria-label="Copy campaign ID"
                                title={copiedId === campaign.id ? 'Copied!' : 'Copy campaign ID'}
                              >
                                {copiedId === campaign.id ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                            {editingDescriptionId === campaign.id ? (
                              <div
                                className="flex items-center gap-1.5 mt-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="text"
                                  autoFocus
                                  value={descriptionDraft}
                                  onChange={(e) => setDescriptionDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateCampaignDescription(campaign.id, descriptionDraft);
                                      setEditingDescriptionId(null);
                                    } else if (e.key === 'Escape') {
                                      setEditingDescriptionId(null);
                                    }
                                  }}
                                  placeholder="Add a description..."
                                  className="flex-1 text-sm border border-surfox-border rounded-lg px-2 py-1 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary"
                                />
                                <button
                                  onClick={() => {
                                    updateCampaignDescription(campaign.id, descriptionDraft);
                                    setEditingDescriptionId(null);
                                  }}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Save description"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingDescriptionId(null)}
                                  className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 mt-0.5 group/desc">
                                <span className={`text-sm ${campaign.description ? 'text-gray-500' : 'text-gray-400 italic'}`}>
                                  {campaign.description || 'No description'}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDescriptionDraft(campaign.description || '');
                                    setEditingDescriptionId(campaign.id);
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors opacity-0 group-hover/desc:opacity-100 focus:opacity-100"
                                  aria-label="Edit description"
                                  title="Edit description"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(campaign)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="w-48">
                            <CampaignProgress
                              campaignId={campaign.id}
                              onClick={(e) => {
                                e?.stopPropagation?.();
                                setProgressModalCampaign(campaign);
                                setShowProgressModal(true);
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <HotLeadAssignment assignment={hotLeadAssignment} />
                        </td>
                        <td className="px-6 py-4 text-center">
                          {!campaign.ai_on && !isCampaignSetupComplete(campaign) ? (
                            <div className="relative group inline-flex items-center justify-center">
                              <button
                                disabled
                                className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-100 cursor-not-allowed opacity-60"
                                aria-label="Complete all setup steps to launch"
                              >
                                <span className="inline-block h-4 w-4 transform rounded-full bg-gray-300 translate-x-1" />
                                <Lock className="w-3 h-3 absolute right-1 text-gray-400" />
                              </button>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20 w-52 text-xs bg-gray-800 text-white rounded-lg px-3 py-2 text-center shadow-lg pointer-events-none">
                                Complete all setup steps to launch.
                                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800" />
                              </div>
                            </div>
                          ) : (
                            <div className={`inline-flex rounded-full transition-all duration-700 ${recentlyUnlocked.has(campaign.id) ? 'ring-4 ring-green-400 ring-opacity-70 shadow-lg shadow-green-200' : ''}`}>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleAiOn(campaign.id, campaign.ai_on); }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                  campaign.ai_on ? 'bg-blue-600' : recentlyUnlocked.has(campaign.id) ? 'bg-green-400' : 'bg-gray-200'
                                }`}
                                title={campaign.ai_on ? 'AI Enabled - Click to disable' : 'AI Disabled - Click to enable'}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    campaign.ai_on ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            data-tour="campaign-expand-button"
                            onClick={(e) => { e.stopPropagation(); toggleRowExpanded(); }}
                            className="inline-flex items-center p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            {expandedRows.has(campaign.id) ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedRows.has(campaign.id) && (
                        <tr>
                          <td colSpan={6} data-tour="campaign-config" className="px-6 py-6 bg-gradient-to-br from-amber-50/40 via-orange-50/20 to-white">
                            <CampaignDetailPanel
                              campaign={campaign}
                              widgetCampaignId={widgetCampaignId}
                              voiceCampaignId={voiceCampaignId}
                              messengerCampaignId={messengerCampaignId}
                              isGlobalAdmin={isGlobalAdmin}
                              salesTeamsByTenant={salesTeamsByTenant}
                              webChatCompanyName={webChatCompanyName}
                              setWebChatCompanyName={setWebChatCompanyName}
                              saveWebChatCompanyName={saveWebChatCompanyName}
                              setWebChatCampaign={setWebChatCampaign}
                              webChatHolderName={webChatHolderName}
                              setVoiceCampaign={setVoiceCampaign}
                              setMessengerCampaign={setMessengerCampaign}
                              phoneNumbers={phoneNumbers}
                              knowledgeAssets={knowledgeAssets}
                              selectedKnowledgeAssets={selectedKnowledgeAssets}
                              setSelectedKnowledgeAssets={setSelectedKnowledgeAssets}
                              updateCampaignKnowledgeLinks={updateCampaignKnowledgeLinks}
                              updateCampaignAssignment={updateCampaignAssignment}
                              updateCampaignPhoneNumber={updateCampaignPhoneNumber}
                              updateCampaignAdditionalPhones={updateCampaignAdditionalPhones}
                              autoImportCampaignNumberSilently={autoImportCampaignNumberSilently}
                              updateCampaignTimezone={updateCampaignTimezone}
                              updateCampaignForwardNumber={updateCampaignForwardNumber}
                              updateCampaignNotificationEmail={updateCampaignNotificationEmail}
                              updateCampaignVisitorSupportEmail={updateCampaignVisitorSupportEmail}
                              updateCampaignNotificationCcEmail={updateCampaignNotificationCcEmail}
                              updateCampaignAIRepresentativeName={updateCampaignAIRepresentativeName}
                              updateCampaignDynamicField={updateCampaignDynamicField}
                              updateHotLeadDistribution={updateHotLeadDistribution}
                              importCampaignNumberToRetell={importCampaignNumberToRetell}
                              updateCampaignWarmTransferMode={updateCampaignWarmTransferMode}
                              tenantIndustries={tenantIndustries}
                              getDynamicColumnHeader={getDynamicColumnHeader}
                              getDynamicDropdownOptions={getDynamicDropdownOptions}
                              customTalkTracks={customTalkTracks}
                              showArchived={showArchived}
                              archiveCampaign={archiveCampaign}
                              unarchiveCampaign={unarchiveCampaign}
                              canEditInstructions={canEditInstructions}
                              canRebuildBundle={canRebuildBundle}
                              onConfigSaved={onConfigSaved}
                            />
                          </td>

                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
  </div>
)}

{/* Launch Console: full campaign settings (the old expanded-row config) in a modal */}
{settingsCampaign && createPortal(
  <div
    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
    onClick={() => { setSettingsCampaignId(null); setSettingsTab('setup'); }}
  >
    <div
      className="relative w-full max-w-5xl my-4 bg-surfox-surface rounded-2xl shadow-[0_24px_60px_rgba(18,30,50,.22)] border border-surfox-border font-sans antialiased text-surfox-ink"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-surfox-border sticky top-0 bg-surfox-surface rounded-t-2xl z-10">
        <div className="min-w-0">
          <h3 className="text-[17px] font-semibold tracking-[-.2px] text-surfox-ink truncate">{settingsCampaign.name}</h3>
          <p className="text-[12.5px] font-medium text-surfox-muted mt-0.5">
            {{ setup: 'Setup', behavior: 'Behavior', followups: 'Follow-ups', knowledge: 'Knowledge', qualification: 'Qualification' }[settingsTab] || 'Campaign settings'}
          </p>
        </div>
        <button
          onClick={() => { setSettingsCampaignId(null); setSettingsTab('setup'); }}
          className="p-2 text-surfox-faint hover:text-surfox-muted hover:bg-surfox-line rounded-lg transition-colors flex-shrink-0"
          aria-label="Close settings"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6 max-h-[82vh] overflow-y-auto bg-surfox-bg rounded-b-2xl">
        <CampaignDetailPanel
          key={`${settingsCampaign.id}-${settingsTab}`}
          campaign={settingsCampaign}
          initialTab={settingsTab}
          singleSection={true}
          widgetCampaignId={widgetCampaignId}
          voiceCampaignId={voiceCampaignId}
          messengerCampaignId={messengerCampaignId}
          isGlobalAdmin={isGlobalAdmin}
          salesTeamsByTenant={salesTeamsByTenant}
          webChatCompanyName={webChatCompanyName}
          setWebChatCompanyName={setWebChatCompanyName}
          saveWebChatCompanyName={saveWebChatCompanyName}
          setWebChatCampaign={setWebChatCampaign}
          webChatHolderName={webChatHolderName}
          setVoiceCampaign={setVoiceCampaign}
          setMessengerCampaign={setMessengerCampaign}
          phoneNumbers={phoneNumbers}
          knowledgeAssets={knowledgeAssets}
          selectedKnowledgeAssets={selectedKnowledgeAssets}
          setSelectedKnowledgeAssets={setSelectedKnowledgeAssets}
          updateCampaignKnowledgeLinks={updateCampaignKnowledgeLinks}
          updateCampaignAssignment={updateCampaignAssignment}
          updateCampaignPhoneNumber={updateCampaignPhoneNumber}
          updateCampaignAdditionalPhones={updateCampaignAdditionalPhones}
          autoImportCampaignNumberSilently={autoImportCampaignNumberSilently}
          updateCampaignTimezone={updateCampaignTimezone}
          updateCampaignForwardNumber={updateCampaignForwardNumber}
          updateCampaignNotificationEmail={updateCampaignNotificationEmail}
          updateCampaignVisitorSupportEmail={updateCampaignVisitorSupportEmail}
          updateCampaignNotificationCcEmail={updateCampaignNotificationCcEmail}
          updateCampaignAIRepresentativeName={updateCampaignAIRepresentativeName}
          updateCampaignDynamicField={updateCampaignDynamicField}
          updateHotLeadDistribution={updateHotLeadDistribution}
          importCampaignNumberToRetell={importCampaignNumberToRetell}
          updateCampaignWarmTransferMode={updateCampaignWarmTransferMode}
          tenantIndustries={tenantIndustries}
          getDynamicColumnHeader={getDynamicColumnHeader}
          getDynamicDropdownOptions={getDynamicDropdownOptions}
          customTalkTracks={customTalkTracks}
          showArchived={showArchived}
          archiveCampaign={archiveCampaign}
          unarchiveCampaign={unarchiveCampaign}
          canEditInstructions={canEditInstructions}
          canRebuildBundle={canRebuildBundle}
          onConfigSaved={onConfigSaved}
        />
      </div>
    </div>
  </div>,
  document.body
)}

{activeTab === 'templates' && (
  <div className="space-y-6">
    {/* Campaign Selection */}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <label className="block text-sm font-medium text-gray-900 mb-3">
        Select Campaign to Manage Templates
      </label>
      <select
        value={selectedCampaignForTemplates || ''}
        onChange={(e) => setSelectedCampaignForTemplates(e.target.value)}
        className="w-full md:w-96 border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary bg-white"
      >
        <option value="">Choose a campaign...</option>
        {campaigns
          .filter(c => !c.archived)
          .map(campaign => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name}
            </option>
          ))}
      </select>
    </div>

    {selectedCampaignForTemplates && (
      <div className="space-y-6">
        {/* Template Management Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Templates for "{campaigns.find(c => c.id === selectedCampaignForTemplates)?.name}"
            </h3>
            <p className="text-gray-600 text-sm">
              Create up to 10 opening message templates for this specific campaign
            </p>
          </div>
          <Button
            onClick={() => addCampaignTemplate(selectedCampaignForTemplates)}
            disabled={(campaignTemplates[selectedCampaignForTemplates] || []).length >= 10}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Template</span>
          </Button>
        </div>

        {/* Templates Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">
              {(campaignTemplates[selectedCampaignForTemplates] || []).length === 0 ? 
                'AI Generated Messages Only' : 
                `${(campaignTemplates[selectedCampaignForTemplates] || []).length}/10 Templates Active`}
            </span>
          </div>
          <p className="text-sm text-blue-800 mt-1">
            {(campaignTemplates[selectedCampaignForTemplates] || []).length === 0 ? 
              'Add templates to use pre-approved messages for this campaign.' :
              'System will randomly select from these templates for initial messages in this campaign.'
            }
          </p>
        </div>

        {/* Templates List */}
        <div className="space-y-4">
          {(campaignTemplates[selectedCampaignForTemplates] || []).map((template, index) => (
            <div key={template.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">Template {index + 1}</span>
                  {template.used > 0 && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Used {template.used} times
                    </span>
                  )}
                </div>
                <button
                  onClick={() => deleteCampaignTemplate(selectedCampaignForTemplates, template.id)}
                  className="text-red-600 hover:text-red-800"
                  title="Delete template"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <textarea
  id={`campaign-template-${template.id}`}
  value={getTemplateValue(selectedCampaignForTemplates, template.id, template.message || '')}
  onChange={(e) => updateCampaignTemplateLocal(selectedCampaignForTemplates, template.id, e.target.value)}
  placeholder="Enter your campaign-specific message template here..."
  className="w-full h-24 border border-surfox-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-surfox-primary/25 focus:border-surfox-primary resize-none"
/>

{/* Preview and Save buttons */}
<div className="flex items-center justify-between mt-2">
  <Button
    variant="outline"
    onClick={() => handlePreviewTemplate(selectedCampaignForTemplates, template.id)}
  >
    Preview
  </Button>
  
  {editingTemplates[`${selectedCampaignForTemplates}-${template.id}`] !== undefined && (
    <Button
      onClick={() => saveCampaignTemplate(selectedCampaignForTemplates, template.id)}
      className="bg-green-600 hover:bg-green-700 text-white"
    >
      💾 Save Changes
    </Button>
  )}
</div>



                {/* Placeholder Buttons - DYNAMIC */}
{/* Quick Insert Buttons */}
<div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
  <div className="flex flex-wrap gap-1.5 mb-1.5 items-center">
    <span className="text-xs font-semibold text-blue-700 mr-1">Quick Insert:</span>

    {/* ALWAYS show first_name and last_name */}
    <button
      onClick={() => insertPlaceholderInCampaignTemplate(
        selectedCampaignForTemplates,
        template.id,
        '{first_name}'
      )}
      className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
      title='Smart name — "Dr. Bauer" or "Tom"'
    >
      {'{first_name}'}
    </button>

    <button
      onClick={() => insertPlaceholderInCampaignTemplate(
        selectedCampaignForTemplates,
        template.id,
        '{last_name}'
      )}
      className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
      title="Last name"
    >
      {'{last_name}'}
    </button>

    <button
      onClick={() => insertPlaceholderInCampaignTemplate(
        selectedCampaignForTemplates,
        template.id,
        '{name}'
      )}
      className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
      title="Full name"
    >
      {'{name}'}
    </button>

    <button
      onClick={() => insertPlaceholderInCampaignTemplate(
        selectedCampaignForTemplates,
        template.id,
        '{company}'
      )}
      className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
      title="Company name"
    >
      {'{company}'}
    </button>

    <button
      onClick={() => insertPlaceholderInCampaignTemplate(
        selectedCampaignForTemplates,
        template.id,
        '{job_title}'
      )}
      className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
      title="Job title"
    >
      {'{job_title}'}
    </button>

    {/* Dynamic fields from available_fields (CSV column names) */}
    {(() => {
      const campaign = campaigns.find(c => c.id === selectedCampaignForTemplates);
      const availableFields = campaign?.available_fields || [];

      return availableFields.map((field) => (
        <button
          key={field.csv_column}
          onClick={() => insertPlaceholderInCampaignTemplate(
            selectedCampaignForTemplates,
            template.id,
            `{${field.csv_column}}`
          )}
          className="px-2 py-0.5 bg-white border border-blue-300 rounded text-xs font-mono text-blue-700 hover:bg-blue-100 transition-colors"
          title={field.display_name}
        >
          {`{${field.csv_column}}`}
        </button>
      ));
    })()}
  </div>
  <p className="text-xs text-blue-600">
    <span className="font-semibold">{'{first_name}'}</span> is credential-aware — addresses doctors as "Dr. Smith", everyone else by first name.
  </p>
</div>

              </div>
            </div>
          ))}

          {/* Empty state */}
          {(!campaignTemplates[selectedCampaignForTemplates] || campaignTemplates[selectedCampaignForTemplates].length === 0) && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <MessageSquare className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">No templates for this campaign yet</p>
              <p className="text-sm text-gray-500">Click "Add Template" to create your first campaign-specific template</p>
            </div>
          )}
        </div>
      </div>
    )}
    {/* ✅ Preview Modal (Enhanced with Supabase + Merged Field Values) */}
{showPreview && (
  <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-lg w-full p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Template Preview</h2>
        <button
          onClick={() => setShowPreview(false)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Body */}
      <div className="border border-gray-100 rounded-lg p-4 bg-gray-50 whitespace-pre-wrap text-gray-800 max-h-96 overflow-y-auto leading-relaxed shadow-inner">
        {previewContent || 'No preview available.'}
      </div>

      {/* Footer */}
      <div className="flex justify-end pt-4">
        <button
          onClick={() => setShowPreview(false)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
  </div>
)}

{activeTab === 'transfer' && (
  <LeadTransfer 
    campaigns={campaigns}
    user={user}
    onCreateCampaign={() => window.dispatchEvent(new CustomEvent('surf:open'))}
  />
)}

      {/* Go-live celebration — fires after AI successfully turns ON */}
      {celebrateCampaign && (
        <GoLiveCelebration
          campaignName={celebrateCampaign.name || 'Your campaign'}
          onClose={() => setCelebrateCampaign(null)}
          onWatch={() => { setCelebrateCampaign(null); navigate('/control-room'); }}
        />
      )}

      {/* Validation Error Modal */}
      {validationError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setValidationError(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            {/* Icon */}
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>

            {/* Content */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {validationError.title}
              </h3>
              <p className="text-gray-600">
                {validationError.message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {validationError.action && (validationError.actionUrl || validationError.actionEvent) && (
                <Button
                  onClick={() => {
                    const { actionEvent, actionUrl } = validationError;
                    setValidationError(null);
                    if (actionEvent) {
                      window.dispatchEvent(new CustomEvent(actionEvent));
                    } else {
                      window.location.href = actionUrl;
                    }
                  }}
                  className="w-full"
                >
                  {validationError.action}
                </Button>
              )}
              <button
                onClick={() => setValidationError(null)}
                className="w-full px-4 py-2 rounded-2xl bg-gray-100 text-gray-700 font-medium shadow hover:bg-gray-200 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Progress Modal */}
      {showProgressModal && progressModalCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowProgressModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] m-2 lg:m-4 overflow-hidden">
            {/* Header */}
            <div className="border-b border-gray-200 px-4 lg:px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg lg:text-2xl font-bold text-gray-900">
                    {progressModalCampaign.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">Campaign Progress & Health</p>
                </div>
                <button
                  onClick={() => setShowProgressModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-2"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-4 lg:p-6">
              <CampaignProgressModal
                campaign={progressModalCampaign}
                isWebChannel={progressModalCampaign?.id === widgetCampaignId}
                isVoiceChannel={progressModalCampaign?.id === voiceCampaignId}
                isMessengerChannel={progressModalCampaign?.id === messengerCampaignId}
              />
            </div>
          </div>
        </div>
      )}

      {/* Business Hours Warning Modal */}
      {aiToast && (
        <div
          className="fixed bottom-6 right-6 z-50"
          style={{ animation: 'sfx-toast-in 0.3s cubic-bezier(0.16,1,0.3,1) both' }}
        >
          <style>{`
            @keyframes sfx-toast-in {
              from { opacity: 0; transform: translateY(16px) scale(0.95); }
              to   { opacity: 1; transform: translateY(0)    scale(1);    }
            }
          `}</style>
          <div className="bg-gray-900 text-white rounded-2xl shadow-2xl flex items-start gap-3 px-5 py-4 max-w-sm w-80">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white fill-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm leading-snug">AI Processing Started</p>
              <p className="text-gray-400 text-sm mt-0.5 truncate">"{aiToast}"</p>
              <p className="text-gray-500 text-xs mt-1.5 leading-snug">
                You'll get progress updates at 25%, 50%, 75%, and completion.
              </p>
            </div>
            <button
              onClick={() => setAiToast(null)}
              className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors mt-0.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showBusinessHoursWarning && businessHoursInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setShowBusinessHoursWarning(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            {/* Icon */}
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 mx-auto mb-4">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>

            {/* Content */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                AI Enabled - Outside Business Hours
              </h3>
              <p className="text-gray-600 mb-4">
                Campaign AI is now enabled, but it's currently outside your configured business hours
                ({businessHoursInfo.officeDays}, {businessHoursInfo.openHour === 0 ? '12' : businessHoursInfo.openHour > 12 ? businessHoursInfo.openHour - 12 : businessHoursInfo.openHour} {businessHoursInfo.openHour >= 12 ? 'PM' : 'AM'} - {businessHoursInfo.closeHour === 0 ? '12' : businessHoursInfo.closeHour > 12 ? businessHoursInfo.closeHour - 12 : businessHoursInfo.closeHour} {businessHoursInfo.closeHour >= 12 ? 'PM' : 'AM'} {businessHoursInfo.timezone}).
              </p>
              <p className="text-sm text-gray-500">
                Messages will start sending when business hours resume: <span className="font-medium text-gray-700">{businessHoursInfo.nextBusinessHours}</span>
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  setShowBusinessHoursWarning(false);
                  performAiToggle(businessHoursInfo.campaignId, false);
                }}
                className="w-full"
              >
                Got it
              </Button>
              <button
                onClick={() => {
                  setShowBusinessHoursWarning(false);
                  window.location.href = '/settings?tab=company-profile';
                }}
                className="w-full px-4 py-2 rounded-2xl bg-gray-100 text-gray-700 font-medium shadow hover:bg-gray-200 transition-all"
              >
                Adjust Business Hours
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}